// AUTO-GENERATED by scripts/sync-exercises.mjs from the ArmProgress app's
// lib/data/exercises.ts, lib/data/techniques.ts, lib/data/armwrestlingGroups.ts
// and locales/<lang>/training.json. Do not hand-edit — re-run
// `npm run sync:exercises` (requires ArmProgress checked out as a sibling
// directory) instead. Human-authored SEO copy lives in seo-content.<lang>.json,
// which this script never touches.

export type Lang = "en" | "ru" | "de" | "fr" | "es" | "pt-BR" | "ja" | "ko";

export interface ExerciseTranslation {
  name: string;
  description: string;
  muscles: string;
  instructions: string[];
  sets: string;
  reps: string;
  tips: string[];
}

export interface ExerciseRecord {
  id: string;
  slug: string;
  emoji: string;
  category: 'dynamic' | 'static' | 'warmup';
  equipmentKeys: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  hasVideo: boolean;
  relatedTechniqueIds: string[];
  armGroup: string | null;
  translations: Partial<Record<Lang, ExerciseTranslation>>;
}

export interface TechniqueMin {
  id: string;
  keyExerciseIds: string[];
  names: Partial<Record<Lang, string>>;
}

export const EXERCISES: ExerciseRecord[] = [
  {
    "id": "wristCurls",
    "slug": "wrist-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "barbell",
      "table"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Wrist Curls",
        "description": "Fundamental exercise for building wrist flexor strength. Essential for hook technique and cupping power.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Sit with forearm resting on thigh or bench, palm facing up",
          "Hold dumbbell or barbell with relaxed grip",
          "Curl wrist up as high as possible, squeezing at top",
          "Lower slowly with control, allowing slight finger roll"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Use full range of motion for maximum development",
          "Control the negative (lowering) portion slowly",
          "Allow the weight to roll to fingers for extra stretch"
        ]
      },
      "ru": {
        "name": "Сгибания запястий",
        "description": "Базовое упражнение для развития силы сгибателей кисти. Необходимо для техники крюка и силы чашки.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Сядьте, положив предплечье на бедро или скамью, ладонью вверх",
          "Держите гантель или штангу расслабленным хватом",
          "Согните кисть максимально вверх, сжимая в верхней точке",
          "Медленно опускайте с контролем, позволяя пальцам слегка раскрыться"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Используйте полную амплитуду для максимального развития",
          "Контролируйте негативную фазу (опускание)",
          "Позвольте весу скатиться к пальцам для дополнительной растяжки"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls",
        "description": "Grundübung zum Aufbau der Handgelenkbeuger-Kraft. Essenziell für die Hook-Technik und Cupping-Power.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Setze dich mit dem Unterarm auf dem Oberschenkel oder der Bank, Handfläche nach oben",
          "Halte Kurz- oder Langhantel mit lockerem Griff",
          "Curle das Handgelenk so hoch wie möglich, oben anspannen",
          "Senke langsam und kontrolliert ab, leichtes Abrollen zu den Fingern erlauben"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Nutze den vollen Bewegungsradius für maximale Entwicklung",
          "Kontrolliere die negative Phase (Absenken) langsam",
          "Lass das Gewicht zu den Fingern rollen für extra Dehnung"
        ]
      },
      "fr": {
        "name": "Flexions du poignet",
        "description": "Exercice fondamental pour développer la force des fléchisseurs du poignet. Essentiel pour la technique du hook et la puissance du cupping.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Asseyez-vous, l'avant-bras posé sur la cuisse ou le banc, paume vers le haut",
          "Tenez un haltère ou une barre avec une prise détendue",
          "Enroulez le poignet le plus haut possible en contractant en haut",
          "Descendez lentement en contrôlant, en laissant les doigts se dérouler légèrement"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Utilisez une amplitude complète pour un développement maximal",
          "Contrôlez lentement la phase négative (descente)",
          "Laissez le poids rouler jusqu'aux doigts pour un étirement supplémentaire"
        ]
      },
      "es": {
        "name": "Curl de Muñeca",
        "description": "Ejercicio fundamental para desarrollar la fuerza de los flexores de muñeca. Esencial para la técnica de gancho y la potencia de ahuecado.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Siéntate con el antebrazo apoyado sobre el muslo o un banco, palma hacia arriba",
          "Sujeta una mancuerna o barra con agarre relajado",
          "Curva la muñeca hacia arriba lo más alto posible, apretando arriba",
          "Baja lentamente con control, permitiendo un ligero rodar de los dedos"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Usa un rango de movimiento completo para máximo desarrollo",
          "Controla la fase negativa (bajada) lentamente",
          "Deja que el peso ruede hacia los dedos para un estiramiento extra"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho",
        "description": "Exercício fundamental para desenvolver a força dos flexores do punho. Essencial para a técnica de hook e a potência do cupping.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Senta-te com o antebraço apoiado na coxa ou banco, palma virada para cima",
          "Segura o halter ou a barra com pega relaxada",
          "Enrola o punho o mais alto possível, contraindo no topo",
          "Desce lentamente com controlo, permitindo um ligeiro rolar dos dedos"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Usa a amplitude completa para o máximo desenvolvimento",
          "Controla a fase negativa (descida) lentamente",
          "Deixa o peso rolar até aos dedos para mais alongamento"
        ]
      },
      "ja": {
        "name": "リストカール",
        "description": "手首屈筋群の筋力を鍛える基本的なエクササイズです。フックテクニックとカッピングの力に不可欠です。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "前腕を太ももかベンチに乗せ、手のひらを上に向けて座る",
          "リラックスしたグリップでダンベルかバーベルを持つ",
          "手首をできるだけ高く巻き上げ、頂点で絞る",
          "指を少し伸ばしながらゆっくりと制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "最大限の発達のために可動域をフルに使う",
          "ネガティブ（下ろす）動作をゆっくり制御する",
          "追加のストレッチのために重量を指まで転がす"
        ]
      },
      "ko": {
        "name": "리스트 컬",
        "description": "손목 굴근 힘을 기르는 기본 운동입니다. 훅 기술과 커핑 파워에 필수적입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "손바닥을 위로 향하게 하고 전완을 허벅지나 벤치에 올린 채 앉으세요",
          "덤벨이나 바벨을 편안한 그립으로 잡으세요",
          "손목을 최대한 높이 감아 올리고 정점에서 조이세요",
          "손가락이 살짝 풀리도록 하며 천천히 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "최대 발달을 위해 전체 가동 범위를 사용하세요",
          "네거티브(내리는) 구간을 천천히 통제하세요",
          "추가 스트레치를 위해 무게가 손가락까지 굴러가게 하세요"
        ]
      }
    }
  },
  {
    "id": "cupping",
    "slug": "cupping",
    "emoji": "🤲",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "table",
      "none"
    ],
    "difficulty": "beginner",
    "hasVideo": false,
    "relatedTechniqueIds": [
      "hook"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Cupping",
        "description": "Wrist flexion to create a 'cup' shape with your hand. Foundation of the hook technique. Essential for hand control and defending against top rolls.",
        "muscles": "Wrist flexors, finger flexors",
        "instructions": [
          "Sit with forearm resting on thigh or bench, palm facing up",
          "Curl your wrist and fingers together into a tight cup shape",
          "Hold briefly at full contraction, then release with control"
        ],
        "sets": "3 sets",
        "reps": "10-12 reps",
        "tips": [
          "Curl your wrist as deep as possible",
          "Strong fingers make a strong cup",
          "This is the foundation of hook technique"
        ]
      }
    }
  },
  {
    "id": "reverseWristCurls",
    "slug": "reverse-wrist-curls",
    "emoji": "🔃",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "barbell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Reverse Wrist Curls",
        "description": "Targets wrist extensors to balance forearm development and prevent injury.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Sit with forearm on bench, palm facing down",
          "Hold lighter weight than regular wrist curls",
          "Raise wrist up, keeping forearm still"
        ],
        "sets": "3 sets",
        "reps": "12-15 reps",
        "tips": [
          "Use lighter weight than regular wrist curls",
          "Essential for elbow and wrist health"
        ]
      },
      "ru": {
        "name": "Обратные сгибания запястий",
        "description": "Прорабатывает разгибатели кисти для сбалансированного развития предплечья и предотвращения травм.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Сядьте, положив предплечье на скамью, ладонью вниз",
          "Используйте меньший вес, чем для обычных сгибаний",
          "Поднимите кисть вверх, удерживая предплечье неподвижным"
        ],
        "sets": "3 подхода",
        "reps": "12-15 повторений",
        "tips": [
          "Используйте меньший вес, чем для обычных сгибаний",
          "Необходимо для здоровья локтя и запястья"
        ]
      },
      "de": {
        "name": "Umgekehrte Handgelenk-Curls",
        "description": "Trainiert die Handgelenkstrecker, um die Unterarmentwicklung auszugleichen und Verletzungen vorzubeugen.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Setze dich mit dem Unterarm auf der Bank, Handfläche nach unten",
          "Halte leichteres Gewicht als bei normalen Handgelenk-Curls",
          "Hebe das Handgelenk an, Unterarm bleibt still"
        ],
        "sets": "3 Sätze",
        "reps": "12–15 Wdh.",
        "tips": [
          "Nutze leichteres Gewicht als bei normalen Handgelenk-Curls",
          "Essenziell für die Gesundheit von Ellbogen und Handgelenk"
        ]
      },
      "fr": {
        "name": "Flexions inversées du poignet",
        "description": "Cible les extenseurs du poignet pour équilibrer le développement de l'avant-bras et prévenir les blessures.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Asseyez-vous, l'avant-bras sur le banc, paume vers le bas",
          "Tenez un poids plus léger que pour les flexions classiques",
          "Levez le poignet en gardant l'avant-bras immobile"
        ],
        "sets": "3 séries",
        "reps": "12-15 répétitions",
        "tips": [
          "Utilisez un poids plus léger que pour les flexions classiques",
          "Essentiel pour la santé du coude et du poignet"
        ]
      },
      "es": {
        "name": "Curl Inverso de Muñeca",
        "description": "Trabaja los extensores de muñeca para equilibrar el desarrollo del antebrazo y prevenir lesiones.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Siéntate con el antebrazo sobre un banco, palma hacia abajo",
          "Sujeta un peso más ligero que en los curls de muñeca normales",
          "Eleva la muñeca hacia arriba, manteniendo el antebrazo quieto"
        ],
        "sets": "3 series",
        "reps": "12-15 repeticiones",
        "tips": [
          "Usa un peso más ligero que en los curls de muñeca normales",
          "Esencial para la salud del codo y la muñeca"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho Invertidas",
        "description": "Trabalha os extensores do punho para equilibrar o desenvolvimento do antebraço e prevenir lesões.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Senta-te com o antebraço no banco, palma virada para baixo",
          "Segura um peso mais leve do que nas flexões de punho normais",
          "Eleva o punho, mantendo o antebraço imóvel"
        ],
        "sets": "3 séries",
        "reps": "12-15 reps",
        "tips": [
          "Usa um peso mais leve do que nas flexões de punho normais",
          "Essencial para a saúde do cotovelo e do punho"
        ]
      },
      "ja": {
        "name": "リバースリストカール",
        "description": "手首伸筋群を鍛えて前腕の発達をバランスよくし、怪我を防ぎます。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "前腕をベンチに乗せ、手のひらを下に向けて座る",
          "通常のリストカールより軽い重量を持つ",
          "前腕を固定したまま手首を持ち上げる"
        ],
        "sets": "3セット",
        "reps": "12〜15レップ",
        "tips": [
          "通常のリストカールより軽い重量を使う",
          "肘と手首の健康に不可欠"
        ]
      },
      "ko": {
        "name": "리버스 리스트 컬",
        "description": "손목 신근을 타깃으로 하여 전완 발달의 균형을 맞추고 부상을 예방합니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손바닥을 아래로 향하게 하고 전완을 벤치에 올린 채 앉으세요",
          "일반 리스트 컬보다 가벼운 무게를 잡으세요",
          "전완을 고정한 채 손목을 위로 들어 올리세요"
        ],
        "sets": "3세트",
        "reps": "12-15회",
        "tips": [
          "일반 리스트 컬보다 가벼운 무게를 사용하세요",
          "팔꿈치와 손목 건강에 필수적입니다"
        ]
      }
    }
  },
  {
    "id": "hammerCurls",
    "slug": "hammer-curls",
    "emoji": "🔨",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "toproll"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Hammer Curls",
        "description": "Targets brachioradialis and biceps with neutral grip. Great for overall arm wrestling strength.",
        "muscles": "Brachioradialis, biceps, forearm",
        "instructions": [
          "Stand with dumbbells at sides, palms facing in (neutral)",
          "Curl weight up keeping palm neutral throughout",
          "Lower with control, no swinging"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Keep palms facing each other throughout",
          "No swinging - strict form only",
          "Try preacher bench variation for isolation"
        ]
      },
      "ru": {
        "name": "Молотковые сгибания",
        "description": "Прорабатывает плечелучевую мышцу и бицепс нейтральным хватом. Отлично для общей силы в армрестлинге.",
        "muscles": "Плечелучевая мышца, бицепс, предплечье",
        "instructions": [
          "Встаньте с гантелями по бокам, ладони смотрят внутрь",
          "Поднимите вес, сохраняя нейтральное положение ладони",
          "Опускайте с контролем, без раскачивания"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Держите ладони обращёнными друг к другу",
          "Без раскачивания - только строгая техника",
          "Попробуйте вариант на скамье Скотта для изоляции"
        ]
      },
      "de": {
        "name": "Hammer-Curls",
        "description": "Trainiert Brachioradialis und Bizeps mit neutralem Griff. Ideal für die allgemeine Armdrück-Kraft.",
        "muscles": "Brachioradialis, Bizeps, Unterarm",
        "instructions": [
          "Stehe mit Kurzhanteln an den Seiten, Handflächen zeigen nach innen (neutral)",
          "Curle das Gewicht hoch, Handfläche bleibt durchgehend neutral",
          "Senke kontrolliert ab, kein Schwung"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Halte die Handflächen durchgehend einander zugewandt",
          "Kein Schwung – nur strenge Ausführung",
          "Probiere die Scott-Bank-Variante zur Isolation"
        ]
      },
      "fr": {
        "name": "Curls marteau",
        "description": "Cible le brachioradial et les biceps avec une prise neutre. Excellent pour la force globale au bras de fer.",
        "muscles": "Brachioradial, biceps, avant-bras",
        "instructions": [
          "Debout, haltères sur les côtés, paumes tournées vers l'intérieur (neutre)",
          "Enroulez le poids vers le haut en gardant la paume neutre tout du long",
          "Descendez en contrôlant, sans balancement"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Gardez les paumes face à face tout du long",
          "Pas de balancement - forme stricte uniquement",
          "Essayez la variante au banc Larry Scott pour l'isolation"
        ]
      },
      "es": {
        "name": "Curl Martillo",
        "description": "Trabaja el braquiorradial y los bíceps con agarre neutro. Excelente para la fuerza general en el pulso.",
        "muscles": "Braquiorradial, bíceps, antebrazo",
        "instructions": [
          "Ponte de pie con mancuernas a los lados, palmas hacia dentro (neutras)",
          "Curva el peso hacia arriba manteniendo la palma neutra en todo momento",
          "Baja con control, sin balanceo"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "Mantén las palmas enfrentadas en todo momento",
          "Sin balanceo, solo forma estricta",
          "Prueba la variación en banco predicador para aislamiento"
        ]
      },
      "pt-BR": {
        "name": "Rosca Martelo",
        "description": "Trabalha o braquiorradial e o bíceps com pega neutra. Ótimo para a força geral na queda de braço.",
        "muscles": "Braquiorradial, bíceps, antebraço",
        "instructions": [
          "Fica de pé com os halteres ao lado, palmas viradas para dentro (neutra)",
          "Enrola o peso mantendo a palma neutra durante todo o movimento",
          "Desce com controlo, sem balançar"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "Mantém as palmas viradas uma para a outra durante todo o movimento",
          "Sem balanços - apenas forma rigorosa",
          "Experimenta a variação no banco Scott para isolamento"
        ]
      },
      "ja": {
        "name": "ハンマーカール",
        "description": "ニュートラルグリップで腕橈骨筋と上腕二頭筋を鍛えます。アームレスリング全体の筋力に最適です。",
        "muscles": "腕橈骨筋、上腕二頭筋、前腕",
        "instructions": [
          "ダンベルを体の横に持ち、手のひらを内側に向けて（ニュートラル）立つ",
          "手のひらをニュートラルに保ったまま重量を巻き上げる",
          "反動を使わず制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "手のひらを常に向かい合わせに保つ",
          "反動を使わず、厳格なフォームのみ",
          "アイソレーションのためにプリーチャーベンチのバリエーションを試す"
        ]
      },
      "ko": {
        "name": "해머 컬",
        "description": "중립 그립으로 상완요골근과 이두근을 타깃으로 합니다. 전반적인 팔씨름 근력에 좋습니다.",
        "muscles": "상완요골근, 이두근, 전완",
        "instructions": [
          "손바닥을 안쪽(중립)으로 향하게 하고 덤벨을 옆에 든 채 서세요",
          "손바닥을 내내 중립으로 유지하며 무게를 감아 올리세요",
          "반동 없이 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "손바닥이 서로 마주보도록 내내 유지하세요",
          "반동 금지 - 엄격한 자세만 사용하세요",
          "고립을 위해 프리처 벤치 변형을 시도해 보세요"
        ]
      }
    }
  },
  {
    "id": "pronationCurls",
    "slug": "pronation-curls",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "cable",
      "sledgehammer"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Pronation Curls",
        "description": "Rotational exercise to strengthen pronator muscles. Essential for top roll technique.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Hold dumbbell or use cable with arm at side",
          "Rotate forearm to turn palm down (pronate)",
          "Return to neutral with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Focus on controlled rotation, not speed",
          "Use full range of motion"
        ]
      },
      "ru": {
        "name": "Пронация",
        "description": "Вращательное упражнение для укрепления мышц-пронаторов. Необходимо для техники верхнего.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Держите гантель или используйте блок с рукой сбоку",
          "Вращайте предплечье, поворачивая ладонь вниз (пронация)",
          "Вернитесь в нейтральное положение с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Фокус на контролируемом вращении, не скорости",
          "Используйте полную амплитуду движения"
        ]
      },
      "de": {
        "name": "Pronations-Curls",
        "description": "Rotationsübung zur Stärkung der Pronatormuskeln. Essenziell für die Top-Roll-Technik.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Halte eine Kurzhantel oder nutze den Kabelzug mit dem Arm an der Seite",
          "Drehe den Unterarm, um die Handfläche nach unten zu drehen (pronieren)",
          "Kehre kontrolliert in die neutrale Position zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Konzentriere dich auf kontrollierte Drehung, nicht auf Tempo",
          "Nutze den vollen Bewegungsradius"
        ]
      },
      "fr": {
        "name": "Curls en pronation",
        "description": "Exercice de rotation pour renforcer les muscles pronateurs. Essentiel pour la technique du top roll.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Tenez un haltère ou utilisez une poulie, bras le long du corps",
          "Tournez l'avant-bras pour amener la paume vers le bas (pronation)",
          "Revenez en position neutre en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Concentrez-vous sur une rotation contrôlée, pas sur la vitesse",
          "Utilisez une amplitude complète"
        ]
      },
      "es": {
        "name": "Curl de Pronación",
        "description": "Ejercicio rotacional para fortalecer los músculos pronadores. Esencial para la técnica de top roll.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Sujeta una mancuerna o usa una polea con el brazo al costado",
          "Rota el antebrazo para girar la palma hacia abajo (pronar)",
          "Vuelve a la posición neutra con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Concéntrate en la rotación controlada, no en la velocidad",
          "Usa un rango de movimiento completo"
        ]
      },
      "pt-BR": {
        "name": "Rosca de Pronação",
        "description": "Exercício rotacional para fortalecer os músculos pronadores. Essencial para a técnica de top roll.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Segura um halter ou usa um cabo com o braço ao lado",
          "Roda o antebraço para virar a palma para baixo (pronação)",
          "Regressa à posição neutra com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Foca-te em rotação controlada, não em velocidade",
          "Usa a amplitude completa"
        ]
      },
      "ja": {
        "name": "プロネーションカール",
        "description": "回内筋を強化する回転エクササイズです。トップロールのテクニックに不可欠です。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "腕を体の横にしてダンベルを持つかケーブルを使う",
          "前腕を回して手のひらを下に向ける（回内）",
          "制御してニュートラルに戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "スピードではなく制御された回転に集中する",
          "可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "회내 컬",
        "description": "회내근을 강화하는 회전 운동입니다. 탑롤 기술에 필수적입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "팔을 옆에 두고 덤벨을 잡거나 케이블을 사용하세요",
          "전완을 회전시켜 손바닥을 아래로 돌리세요(회내)",
          "통제하여 중립으로 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "속도가 아닌 통제된 회전에 집중하세요",
          "전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "supinationCurls",
    "slug": "supination-curls",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "cable",
      "sledgehammer"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Supination Curls",
        "description": "Rotational exercise to strengthen supinator. Essential for hook technique.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Hold dumbbell with palm facing down",
          "Rotate forearm to turn palm up (supinate)",
          "Return to starting position with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Focus on full rotation until palm faces up",
          "Directly builds hook technique strength"
        ]
      },
      "ru": {
        "name": "Супинация",
        "description": "Вращательное упражнение для укрепления супинатора. Необходимо для техники крюка.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Держите гантель ладонью вниз",
          "Вращайте предплечье, поворачивая ладонь вверх (супинация)",
          "Вернитесь в исходное положение с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Фокус на полном вращении до положения ладонью вверх",
          "Напрямую развивает силу для техники крюка"
        ]
      },
      "de": {
        "name": "Supinations-Curls",
        "description": "Rotationsübung zur Stärkung des Supinators. Essenziell für die Hook-Technik.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Halte eine Kurzhantel mit der Handfläche nach unten",
          "Drehe den Unterarm, um die Handfläche nach oben zu drehen (supinieren)",
          "Kehre kontrolliert in die Ausgangsposition zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Konzentriere dich auf die volle Drehung, bis die Handfläche nach oben zeigt",
          "Baut direkt die Kraft für die Hook-Technik auf"
        ]
      },
      "fr": {
        "name": "Curls en supination",
        "description": "Exercice de rotation pour renforcer le supinateur. Essentiel pour la technique du hook.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Tenez un haltère paume vers le bas",
          "Tournez l'avant-bras pour amener la paume vers le haut (supination)",
          "Revenez à la position de départ en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Concentrez-vous sur une rotation complète jusqu'à ce que la paume soit vers le haut",
          "Développe directement la force pour la technique du hook"
        ]
      },
      "es": {
        "name": "Curl de Supinación",
        "description": "Ejercicio rotacional para fortalecer el supinador. Esencial para la técnica de gancho.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Sujeta una mancuerna con la palma hacia abajo",
          "Rota el antebrazo para girar la palma hacia arriba (supinar)",
          "Vuelve a la posición inicial con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Concéntrate en la rotación completa hasta que la palma mire hacia arriba",
          "Desarrolla directamente la fuerza de la técnica de gancho"
        ]
      },
      "pt-BR": {
        "name": "Rosca de Supinação",
        "description": "Exercício rotacional para fortalecer o supinador. Essencial para a técnica de hook.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Segura um halter com a palma virada para baixo",
          "Roda o antebraço para virar a palma para cima (supinação)",
          "Regressa à posição inicial com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Foca-te na rotação completa até a palma ficar virada para cima",
          "Desenvolve diretamente a força da técnica de hook"
        ]
      },
      "ja": {
        "name": "スピネーションカール",
        "description": "回外筋を強化する回転エクササイズです。フックテクニックに不可欠です。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "手のひらを下に向けてダンベルを持つ",
          "前腕を回して手のひらを上に向ける（回外）",
          "制御して開始位置に戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "手のひらが上を向くまで完全に回転することに集中する",
          "フックテクニックの力を直接鍛える"
        ]
      },
      "ko": {
        "name": "회외 컬",
        "description": "회외근을 강화하는 회전 운동입니다. 훅 기술에 필수적입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "손바닥을 아래로 향하게 하고 덤벨을 잡으세요",
          "전완을 회전시켜 손바닥을 위로 돌리세요(회외)",
          "통제하여 시작 자세로 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "손바닥이 위를 향할 때까지 완전한 회전에 집중하세요",
          "훅 기술 근력을 직접적으로 키웁니다"
        ]
      }
    }
  },
  {
    "id": "rising",
    "slug": "rising",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "sledgehammer",
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Rising (Radial Deviation)",
        "description": "Raises the thumb side of hand upward. Critical for top roll technique.",
        "muscles": "Brachioradialis, wrist extensors",
        "instructions": [
          "Hold sledgehammer or cable with arm extended",
          "Raise thumb side of hand upward (radial deviation)",
          "Lower with control"
        ],
        "sets": "3 sets",
        "reps": "10-12 reps",
        "tips": [
          "Movement is toward the thumb (radial) side",
          "Key strength component for top roll"
        ]
      },
      "ru": {
        "name": "Подъём (Радиальное отклонение)",
        "description": "Подъём большого пальца стороны руки вверх. Критически важно для техники верхнего.",
        "muscles": "Плечелучевая мышца, разгибатели кисти",
        "instructions": [
          "Держите кувалду или блок с вытянутой рукой",
          "Поднимите сторону большого пальца вверх (радиальное отклонение)",
          "Опустите с контролем"
        ],
        "sets": "3 подхода",
        "reps": "10-12 повторений",
        "tips": [
          "Движение в сторону большого пальца",
          "Ключевой компонент силы для верхнего"
        ]
      },
      "de": {
        "name": "Rising (Radialabduktion)",
        "description": "Hebt die Daumenseite der Hand nach oben. Entscheidend für die Top-Roll-Technik.",
        "muscles": "Brachioradialis, Handgelenkstrecker",
        "instructions": [
          "Halte einen Vorschlaghammer oder Kabelzug mit gestrecktem Arm",
          "Hebe die Daumenseite der Hand nach oben (Radialabduktion)",
          "Senke kontrolliert ab"
        ],
        "sets": "3 Sätze",
        "reps": "10–12 Wdh.",
        "tips": [
          "Die Bewegung geht zur Daumenseite (radial)",
          "Wichtige Kraftkomponente für den Top Roll"
        ]
      },
      "fr": {
        "name": "Rising (déviation radiale)",
        "description": "Lève le côté pouce de la main vers le haut. Crucial pour la technique du top roll.",
        "muscles": "Brachioradial, extenseurs du poignet",
        "instructions": [
          "Tenez une masse ou utilisez une poulie, bras tendu",
          "Levez le côté pouce de la main vers le haut (déviation radiale)",
          "Descendez en contrôlant"
        ],
        "sets": "3 séries",
        "reps": "10-12 répétitions",
        "tips": [
          "Le mouvement se fait vers le côté du pouce (radial)",
          "Composante de force clé pour le top roll"
        ]
      },
      "es": {
        "name": "Elevación (Desviación Radial)",
        "description": "Eleva el lado del pulgar de la mano hacia arriba. Fundamental para la técnica de top roll.",
        "muscles": "Braquiorradial, extensores de muñeca",
        "instructions": [
          "Sujeta un mazo o polea con el brazo extendido",
          "Eleva el lado del pulgar de la mano hacia arriba (desviación radial)",
          "Baja con control"
        ],
        "sets": "3 series",
        "reps": "10-12 repeticiones",
        "tips": [
          "El movimiento es hacia el lado del pulgar (radial)",
          "Componente clave de fuerza para el top roll"
        ]
      },
      "pt-BR": {
        "name": "Elevação (Desvio Radial)",
        "description": "Eleva o lado do polegar da mão para cima. Fundamental para a técnica de top roll.",
        "muscles": "Braquiorradial, extensores do punho",
        "instructions": [
          "Segura uma marreta ou um cabo com o braço estendido",
          "Eleva o lado do polegar da mão para cima (desvio radial)",
          "Desce com controlo"
        ],
        "sets": "3 séries",
        "reps": "10-12 reps",
        "tips": [
          "O movimento é para o lado do polegar (radial)",
          "Componente-chave de força para o top roll"
        ]
      },
      "ja": {
        "name": "ライジング（橈屈）",
        "description": "手の親指側を上に持ち上げます。トップロールのテクニックに極めて重要です。",
        "muscles": "腕橈骨筋、手首伸筋群",
        "instructions": [
          "腕を伸ばしてスレッジハンマーかケーブルを持つ",
          "手の親指側を上に持ち上げる（橈屈）",
          "制御して下ろす"
        ],
        "sets": "3セット",
        "reps": "10〜12レップ",
        "tips": [
          "動作は親指（橈側）方向へ",
          "トップロールの重要な筋力要素"
        ]
      },
      "ko": {
        "name": "라이징 (요측 편위)",
        "description": "손의 엄지 쪽을 위로 들어 올립니다. 탑롤 기술에 매우 중요합니다.",
        "muscles": "상완요골근, 손목 신근",
        "instructions": [
          "팔을 뻗은 채 슬레지해머나 케이블을 잡으세요",
          "손의 엄지 쪽을 위로 들어 올리세요(요측 편위)",
          "통제하여 내리세요"
        ],
        "sets": "3세트",
        "reps": "10-12회",
        "tips": [
          "동작은 엄지(요측) 방향입니다",
          "탑롤의 핵심 근력 요소입니다"
        ]
      }
    }
  },
  {
    "id": "sidePressureDrills",
    "slug": "side-pressure-drills",
    "emoji": "➡️",
    "category": "dynamic",
    "equipmentKeys": [
      "cable",
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Side Pressure Drills",
        "description": "Lateral pressing movement at armwrestling table height.",
        "muscles": "Brachialis, lateral ligaments, shoulder",
        "instructions": [
          "Set cable or band at table height",
          "Press sideways mimicking pin movement",
          "Maintain wrist angle throughout"
        ],
        "sets": "4 sets",
        "reps": "8-10 reps",
        "tips": [
          "Set resistance at arm wrestling table height",
          "Use body weight to assist the press"
        ]
      },
      "ru": {
        "name": "Боковое давление",
        "description": "Боковое давящее движение на высоте стола для армрестлинга.",
        "muscles": "Плечевая мышца, боковые связки, плечо",
        "instructions": [
          "Установите блок или резинку на высоте стола",
          "Давите вбок, имитируя движение укладки",
          "Сохраняйте угол запястья на протяжении всего движения"
        ],
        "sets": "4 подхода",
        "reps": "8-10 повторений",
        "tips": [
          "Установите сопротивление на высоте стола для армрестлинга",
          "Используйте вес тела для усиления давления"
        ]
      },
      "de": {
        "name": "Seitendruck-Drills",
        "description": "Seitliche Drückbewegung auf Armdrück-Tischhöhe.",
        "muscles": "Brachialis, seitliche Bänder, Schulter",
        "instructions": [
          "Stelle Kabelzug oder Band auf Tischhöhe ein",
          "Drücke seitlich und ahme die Pin-Bewegung nach",
          "Halte den Handgelenkwinkel durchgehend"
        ],
        "sets": "4 Sätze",
        "reps": "8–10 Wdh.",
        "tips": [
          "Stelle den Widerstand auf Armdrück-Tischhöhe ein",
          "Nutze dein Körpergewicht, um den Druck zu unterstützen"
        ]
      },
      "fr": {
        "name": "Exercices de pression latérale",
        "description": "Mouvement de poussée latérale à hauteur de la table de bras de fer.",
        "muscles": "Brachial, ligaments latéraux, épaule",
        "instructions": [
          "Réglez la poulie ou l'élastique à hauteur de table",
          "Poussez latéralement en imitant le mouvement de pin",
          "Maintenez l'angle du poignet tout du long"
        ],
        "sets": "4 séries",
        "reps": "8-10 répétitions",
        "tips": [
          "Réglez la résistance à hauteur de la table de bras de fer",
          "Utilisez le poids du corps pour aider la poussée"
        ]
      },
      "es": {
        "name": "Ejercicios de Presión Lateral",
        "description": "Movimiento de presión lateral a la altura de la mesa de pulso.",
        "muscles": "Braquial, ligamentos laterales, hombro",
        "instructions": [
          "Coloca la polea o banda a la altura de la mesa",
          "Presiona lateralmente imitando el movimiento de derribo",
          "Mantén el ángulo de la muñeca en todo momento"
        ],
        "sets": "4 series",
        "reps": "8-10 repeticiones",
        "tips": [
          "Coloca la resistencia a la altura de la mesa de pulso",
          "Usa el peso corporal para asistir la presión"
        ]
      },
      "pt-BR": {
        "name": "Exercícios de Pressão Lateral",
        "description": "Movimento de pressão lateral à altura da mesa de queda de braço.",
        "muscles": "Braquial, ligamentos laterais, ombro",
        "instructions": [
          "Coloca o cabo ou a banda à altura da mesa",
          "Pressiona para o lado imitando o movimento de pin",
          "Mantém o ângulo do punho durante todo o movimento"
        ],
        "sets": "4 séries",
        "reps": "8-10 reps",
        "tips": [
          "Coloca a resistência à altura da mesa de queda de braço",
          "Usa o peso do corpo para ajudar na pressão"
        ]
      },
      "ja": {
        "name": "サイドプレッシャードリル",
        "description": "アームレスリングテーブルの高さで行う横方向のプレス動作です。",
        "muscles": "上腕筋、外側靭帯、肩",
        "instructions": [
          "ケーブルかバンドをテーブルの高さにセットする",
          "ピン動作を模して横方向にプレスする",
          "手首の角度を常に維持する"
        ],
        "sets": "4セット",
        "reps": "8〜10レップ",
        "tips": [
          "抵抗をアームレスリングテーブルの高さにセットする",
          "体重を使ってプレスを補助する"
        ]
      },
      "ko": {
        "name": "사이드 프레셔 드릴",
        "description": "팔씨름 테이블 높이에서의 측면 프레스 동작입니다.",
        "muscles": "상완근, 측면 인대, 어깨",
        "instructions": [
          "케이블이나 밴드를 테이블 높이에 설정하세요",
          "핀 동작을 모방하여 옆으로 프레스하세요",
          "손목 각도를 내내 유지하세요"
        ],
        "sets": "4세트",
        "reps": "8-10회",
        "tips": [
          "저항을 팔씨름 테이블 높이에 설정하세요",
          "체중을 이용해 프레스를 도우세요"
        ]
      }
    }
  },
  {
    "id": "fingerHold",
    "slug": "finger-hold",
    "emoji": "🤏",
    "category": "static",
    "equipmentKeys": [
      "gripper"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "Finger Hold (Gripper)",
        "description": "Isometric finger strength using hand gripper.",
        "muscles": "Finger flexors, forearm",
        "instructions": [
          "Close gripper fully",
          "Hold closed for prescribed time",
          "Release slowly"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Use different gripper resistances",
          "Builds crush grip strength"
        ]
      },
      "ru": {
        "name": "Удержание эспандера",
        "description": "Изометрическая сила пальцев с кистевым эспандером.",
        "muscles": "Сгибатели пальцев, предплечье",
        "instructions": [
          "Полностью сожмите эспандер",
          "Удерживайте сжатым заданное время",
          "Медленно отпустите"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Используйте эспандеры разного сопротивления",
          "Развивает силу сдавливающего хвата"
        ]
      },
      "de": {
        "name": "Finger-Halten (Handtrainer)",
        "description": "Isometrische Fingerkraft mit dem Handtrainer.",
        "muscles": "Fingerbeuger, Unterarm",
        "instructions": [
          "Schließe den Handtrainer vollständig",
          "Halte geschlossen für die vorgegebene Zeit",
          "Löse langsam"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Nutze verschiedene Handtrainer-Widerstände",
          "Baut Crush-Griffkraft auf"
        ]
      },
      "fr": {
        "name": "Maintien des doigts (pince)",
        "description": "Force isométrique des doigts à l'aide d'une pince à main.",
        "muscles": "Fléchisseurs des doigts, avant-bras",
        "instructions": [
          "Fermez complètement la pince",
          "Maintenez fermée pendant le temps prescrit",
          "Relâchez lentement"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Utilisez différentes résistances de pince",
          "Développe la force de serrage"
        ]
      },
      "es": {
        "name": "Sostén de Dedos (Pinza)",
        "description": "Fuerza isométrica de dedos usando una pinza de mano.",
        "muscles": "Flexores de dedos, antebrazo",
        "instructions": [
          "Cierra la pinza por completo",
          "Mantenla cerrada durante el tiempo indicado",
          "Suelta lentamente"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "Usa pinzas de diferentes resistencias",
          "Desarrolla la fuerza de agarre por aplastamiento"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Dedos (Pinça)",
        "description": "Força isométrica dos dedos usando pinça de mão.",
        "muscles": "Flexores dos dedos, antebraço",
        "instructions": [
          "Fecha a pinça por completo",
          "Mantém fechada durante o tempo prescrito",
          "Solta lentamente"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Usa diferentes resistências de pinça",
          "Desenvolve a força de esmagamento da pega"
        ]
      },
      "ja": {
        "name": "フィンガーホールド（グリッパー）",
        "description": "ハンドグリッパーを使ったアイソメトリックな指の筋力です。",
        "muscles": "指屈筋群、前腕",
        "instructions": [
          "グリッパーを完全に閉じる",
          "決められた時間閉じたまま保持する",
          "ゆっくり離す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "さまざまなグリッパーの抵抗を使う",
          "クラッシュグリップの力を鍛える"
        ]
      },
      "ko": {
        "name": "핑거 홀드 (그립퍼)",
        "description": "핸드 그립퍼를 이용한 등척성 손가락 근력 운동입니다.",
        "muscles": "손가락 굴근, 전완",
        "instructions": [
          "그립퍼를 완전히 닫으세요",
          "정해진 시간 동안 닫은 채 유지하세요",
          "천천히 풀어 주세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "다양한 그립퍼 저항을 사용하세요",
          "쥐는 그립 힘을 키웁니다"
        ]
      }
    }
  },
  {
    "id": "pronationHold",
    "slug": "pronation-hold",
    "emoji": "🔄",
    "category": "static",
    "equipmentKeys": [
      "cable",
      "sledgehammer"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "pronation",
      "toproll"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Pronation Hold",
        "description": "Isometric hold with forearm pronated against resistance.",
        "muscles": "Pronator teres, forearm",
        "instructions": [
          "Use cable or sledgehammer",
          "Rotate to full pronation",
          "Hold against resistance"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "Maintain full rotation throughout",
          "Keep fingers pressing against surface"
        ]
      },
      "ru": {
        "name": "Удержание пронации",
        "description": "Изометрическое удержание с пронированным предплечьем против сопротивления.",
        "muscles": "Круглый пронатор, предплечье",
        "instructions": [
          "Используйте блок или кувалду",
          "Вращайте до полной пронации",
          "Удерживайте против сопротивления"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "Поддерживайте полное вращение",
          "Держите пальцы прижатыми к поверхности"
        ]
      },
      "de": {
        "name": "Pronations-Halten",
        "description": "Isometrisches Halten mit proniertem Unterarm gegen Widerstand.",
        "muscles": "Pronator teres, Unterarm",
        "instructions": [
          "Nutze Kabelzug oder Vorschlaghammer",
          "Drehe in volle Pronation",
          "Halte gegen den Widerstand"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "Halte die volle Drehung durchgehend",
          "Halte die Finger gegen die Oberfläche gedrückt"
        ]
      },
      "fr": {
        "name": "Maintien en pronation",
        "description": "Maintien isométrique avec l'avant-bras en pronation contre résistance.",
        "muscles": "Rond pronateur, avant-bras",
        "instructions": [
          "Utilisez une poulie ou une masse",
          "Tournez jusqu'à la pronation complète",
          "Maintenez contre la résistance"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "Maintenez une rotation complète tout du long",
          "Gardez les doigts appuyés contre la surface"
        ]
      },
      "es": {
        "name": "Sostén de Pronación",
        "description": "Sostén isométrico con el antebrazo pronado contra resistencia.",
        "muscles": "Pronador redondo, antebrazo",
        "instructions": [
          "Usa una polea o un mazo",
          "Rota hasta la pronación completa",
          "Sostén contra la resistencia"
        ],
        "sets": "3-4 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "Mantén la rotación completa en todo momento",
          "Mantén los dedos presionando contra la superficie"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Pronação",
        "description": "Isometria com o antebraço pronado contra resistência.",
        "muscles": "Pronador redondo, antebraço",
        "instructions": [
          "Usa um cabo ou uma marreta",
          "Roda até à pronação completa",
          "Mantém contra a resistência"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "Mantém a rotação completa do início ao fim",
          "Mantém os dedos a pressionar contra a superfície"
        ]
      },
      "ja": {
        "name": "プロネーションホールド",
        "description": "抵抗に対して前腕を回内させたアイソメトリックホールドです。",
        "muscles": "円回内筋、前腕",
        "instructions": [
          "ケーブルかスレッジハンマーを使う",
          "完全な回内まで回す",
          "抵抗に対して保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15秒保持",
        "tips": [
          "常に完全な回転を維持する",
          "指を表面に押し付け続ける"
        ]
      },
      "ko": {
        "name": "회내 홀드",
        "description": "저항에 맞서 전완을 회내한 채 하는 등척성 홀드입니다.",
        "muscles": "원회내근, 전완",
        "instructions": [
          "케이블이나 슬레지해머를 사용하세요",
          "완전한 회내로 회전하세요",
          "저항에 맞서 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15초 유지",
        "tips": [
          "내내 완전한 회전을 유지하세요",
          "손가락을 표면에 계속 눌러 대세요"
        ]
      }
    }
  },
  {
    "id": "riceBucket",
    "slug": "rice-bucket",
    "emoji": "🍚",
    "category": "warmup",
    "equipmentKeys": [
      "riceBucket"
    ],
    "difficulty": "beginner",
    "hasVideo": false,
    "relatedTechniqueIds": [],
    "armGroup": "warmup",
    "translations": {
      "en": {
        "name": "Rice Bucket",
        "description": "Warm-up exercise using bucket of rice for wrist/finger mobility.",
        "muscles": "Wrist, fingers, forearm (all directions)",
        "instructions": [
          "Submerge hand in bucket of rice",
          "Perform various movements: circles, opens, closes",
          "Continue for 1-2 minutes"
        ],
        "sets": "1-2 sets",
        "reps": "1-2 minutes",
        "tips": [
          "Move in all directions for full warm-up",
          "Perfect for pre-workout warm-up"
        ]
      },
      "ru": {
        "name": "Ведро с рисом",
        "description": "Разминочное упражнение с ведром риса для подвижности кисти и пальцев.",
        "muscles": "Кисть, пальцы, предплечье (все направления)",
        "instructions": [
          "Погрузите руку в ведро с рисом",
          "Выполняйте различные движения: круги, раскрытия, сжатия",
          "Продолжайте 1-2 минуты"
        ],
        "sets": "1-2 подхода",
        "reps": "1-2 минуты",
        "tips": [
          "Двигайтесь во всех направлениях для полной разминки",
          "Идеально для разминки перед тренировкой"
        ]
      },
      "de": {
        "name": "Reiseimer",
        "description": "Aufwärmübung mit einem Eimer Reis für Handgelenk-/Fingerbeweglichkeit.",
        "muscles": "Handgelenk, Finger, Unterarm (alle Richtungen)",
        "instructions": [
          "Tauche die Hand in den Eimer mit Reis",
          "Führe verschiedene Bewegungen aus: Kreise, Öffnen, Schließen",
          "Setze 1–2 Minuten fort"
        ],
        "sets": "1–2 Sätze",
        "reps": "1–2 Minuten",
        "tips": [
          "Bewege dich in alle Richtungen für ein vollständiges Aufwärmen",
          "Perfekt zum Aufwärmen vor dem Workout"
        ]
      },
      "fr": {
        "name": "Seau de riz",
        "description": "Exercice d'échauffement utilisant un seau de riz pour la mobilité du poignet et des doigts.",
        "muscles": "Poignet, doigts, avant-bras (toutes directions)",
        "instructions": [
          "Plongez la main dans un seau de riz",
          "Effectuez divers mouvements : cercles, ouvertures, fermetures",
          "Continuez pendant 1 à 2 minutes"
        ],
        "sets": "1-2 séries",
        "reps": "1-2 minutes",
        "tips": [
          "Bougez dans toutes les directions pour un échauffement complet",
          "Parfait pour l'échauffement avant la séance"
        ]
      },
      "es": {
        "name": "Cubo de Arroz",
        "description": "Ejercicio de calentamiento usando un cubo de arroz para la movilidad de muñeca y dedos.",
        "muscles": "Muñeca, dedos, antebrazo (todas las direcciones)",
        "instructions": [
          "Sumerge la mano en el cubo de arroz",
          "Realiza diversos movimientos: círculos, aperturas, cierres",
          "Continúa durante 1-2 minutos"
        ],
        "sets": "1-2 series",
        "reps": "1-2 minutos",
        "tips": [
          "Muévete en todas las direcciones para un calentamiento completo",
          "Perfecto para el calentamiento previo al entrenamiento"
        ]
      },
      "pt-BR": {
        "name": "Balde de Arroz",
        "description": "Exercício de aquecimento usando um balde de arroz para mobilidade do punho/dedos.",
        "muscles": "Punho, dedos, antebraço (todas as direções)",
        "instructions": [
          "Mergulha a mão no balde de arroz",
          "Faz vários movimentos: círculos, aberturas, fechos",
          "Continua durante 1-2 minutos"
        ],
        "sets": "1-2 séries",
        "reps": "1-2 minutos",
        "tips": [
          "Move em todas as direções para um aquecimento completo",
          "Perfeito para o aquecimento antes do treino"
        ]
      },
      "ja": {
        "name": "ライスバケツ",
        "description": "手首/指の可動性のために米の入ったバケツを使うウォームアップエクササイズです。",
        "muscles": "手首、指、前腕（全方向）",
        "instructions": [
          "手を米の入ったバケツに沈める",
          "さまざまな動作を行う：円、開く、閉じる",
          "1〜2分続ける"
        ],
        "sets": "1〜2セット",
        "reps": "1〜2分",
        "tips": [
          "全方向に動かして完全なウォームアップを行う",
          "ワークアウト前のウォームアップに最適"
        ]
      },
      "ko": {
        "name": "쌀통",
        "description": "손목/손가락 가동성을 위해 쌀통을 이용하는 워밍업 운동입니다.",
        "muscles": "손목, 손가락, 전완 (모든 방향)",
        "instructions": [
          "손을 쌀통에 담그세요",
          "원 그리기, 펴기, 쥐기 등 다양한 동작을 하세요",
          "1-2분간 계속하세요"
        ],
        "sets": "1-2세트",
        "reps": "1-2분",
        "tips": [
          "완전한 워밍업을 위해 모든 방향으로 움직이세요",
          "운동 전 워밍업에 완벽합니다"
        ]
      }
    }
  },
  {
    "id": "bandFingerExtensions",
    "slug": "band-finger-extensions",
    "emoji": "🖐️",
    "category": "warmup",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": false,
    "relatedTechniqueIds": [],
    "armGroup": "warmup",
    "translations": {
      "en": {
        "name": "Band Finger Extensions",
        "description": "Opens fingers against band resistance to balance gripping muscles.",
        "muscles": "Finger extensors, forearm extensors",
        "instructions": [
          "Place rubber band around all fingers",
          "Spread fingers apart against resistance",
          "Close and repeat"
        ],
        "sets": "2-3 sets",
        "reps": "15-25 reps",
        "tips": [
          "Balances grip muscles to prevent injury",
          "Use high reps for best effect"
        ]
      },
      "ru": {
        "name": "Разгибания с резинкой",
        "description": "Раскрытие пальцев против сопротивления резинки для баланса мышц хвата.",
        "muscles": "Разгибатели пальцев, разгибатели предплечья",
        "instructions": [
          "Наденьте резинку на все пальцы",
          "Раздвигайте пальцы против сопротивления",
          "Сожмите и повторите"
        ],
        "sets": "2-3 подхода",
        "reps": "15-25 повторений",
        "tips": [
          "Балансирует мышцы хвата для предотвращения травм",
          "Используйте много повторений для лучшего эффекта"
        ]
      },
      "de": {
        "name": "Band-Fingerstreckungen",
        "description": "Öffnet die Finger gegen Bandwiderstand, um die Greifmuskeln auszugleichen.",
        "muscles": "Fingerstrecker, Unterarmstrecker",
        "instructions": [
          "Lege ein Gummiband um alle Finger",
          "Spreize die Finger gegen den Widerstand",
          "Schließe und wiederhole"
        ],
        "sets": "2–3 Sätze",
        "reps": "15–25 Wdh.",
        "tips": [
          "Gleicht die Greifmuskeln aus, um Verletzungen vorzubeugen",
          "Nutze hohe Wiederholungszahlen für beste Wirkung"
        ]
      },
      "fr": {
        "name": "Extensions des doigts à l'élastique",
        "description": "Ouvre les doigts contre la résistance de l'élastique pour équilibrer les muscles de préhension.",
        "muscles": "Extenseurs des doigts, extenseurs de l'avant-bras",
        "instructions": [
          "Placez un élastique autour de tous les doigts",
          "Écartez les doigts contre la résistance",
          "Refermez et répétez"
        ],
        "sets": "2-3 séries",
        "reps": "15-25 répétitions",
        "tips": [
          "Équilibre les muscles de préhension pour prévenir les blessures",
          "Utilisez un nombre élevé de répétitions pour un meilleur effet"
        ]
      },
      "es": {
        "name": "Extensiones de Dedos con Banda",
        "description": "Abre los dedos contra la resistencia de la banda para equilibrar los músculos de agarre.",
        "muscles": "Extensores de dedos, extensores del antebrazo",
        "instructions": [
          "Coloca una banda de goma alrededor de todos los dedos",
          "Separa los dedos contra la resistencia",
          "Cierra y repite"
        ],
        "sets": "2-3 series",
        "reps": "15-25 repeticiones",
        "tips": [
          "Equilibra los músculos de agarre para prevenir lesiones",
          "Usa muchas repeticiones para mejor efecto"
        ]
      },
      "pt-BR": {
        "name": "Extensões de Dedos com Banda",
        "description": "Abre os dedos contra a resistência da banda para equilibrar os músculos de pega.",
        "muscles": "Extensores dos dedos, extensores do antebraço",
        "instructions": [
          "Coloca uma banda de borracha à volta de todos os dedos",
          "Afasta os dedos contra a resistência",
          "Fecha e repete"
        ],
        "sets": "2-3 séries",
        "reps": "15-25 reps",
        "tips": [
          "Equilibra os músculos de pega para prevenir lesões",
          "Usa reps altas para o melhor efeito"
        ]
      },
      "ja": {
        "name": "バンドフィンガーエクステンション",
        "description": "バンドの抵抗に対して指を開き、握る筋肉をバランスよくします。",
        "muscles": "指伸筋群、前腕伸筋群",
        "instructions": [
          "すべての指にゴムバンドをかける",
          "抵抗に対して指を広げる",
          "閉じて繰り返す"
        ],
        "sets": "2〜3セット",
        "reps": "15〜25レップ",
        "tips": [
          "怪我を防ぐためにグリップの筋肉をバランスよくする",
          "最良の効果のために高レップを使う"
        ]
      },
      "ko": {
        "name": "밴드 핑거 익스텐션",
        "description": "밴드 저항에 맞서 손가락을 펴서 쥐는 근육의 균형을 맞춥니다.",
        "muscles": "손가락 신근, 전완 신근",
        "instructions": [
          "모든 손가락에 고무 밴드를 끼우세요",
          "저항에 맞서 손가락을 벌리세요",
          "다시 오므리고 반복하세요"
        ],
        "sets": "2-3세트",
        "reps": "15-25회",
        "tips": [
          "그립 근육의 균형을 맞춰 부상을 예방합니다",
          "최고의 효과를 위해 고반복을 사용하세요"
        ]
      }
    }
  },
  {
    "id": "wristCircles",
    "slug": "wrist-circles",
    "emoji": "🔁",
    "category": "warmup",
    "equipmentKeys": [
      "none"
    ],
    "difficulty": "beginner",
    "hasVideo": false,
    "relatedTechniqueIds": [],
    "armGroup": "warmup",
    "translations": {
      "en": {
        "name": "Wrist Circles",
        "description": "Simple mobility exercise for wrist joints.",
        "muscles": "Wrist joint (all muscles)",
        "instructions": [
          "Make a fist or keep hand open",
          "Rotate wrist in circles",
          "Perform both clockwise and counter-clockwise"
        ],
        "sets": "2 sets",
        "reps": "15-20 each direction",
        "tips": [
          "Always do both directions",
          "Do before every training session"
        ]
      },
      "ru": {
        "name": "Круговые движения кистью",
        "description": "Простое упражнение на подвижность суставов запястья.",
        "muscles": "Сустав запястья (все мышцы)",
        "instructions": [
          "Сожмите кулак или держите руку открытой",
          "Вращайте кисть по кругу",
          "Выполняйте по и против часовой стрелки"
        ],
        "sets": "2 подхода",
        "reps": "15-20 в каждую сторону",
        "tips": [
          "Всегда делайте в обоих направлениях",
          "Делайте перед каждой тренировкой"
        ]
      },
      "de": {
        "name": "Handgelenkkreise",
        "description": "Einfache Mobilitätsübung für die Handgelenke.",
        "muscles": "Handgelenk (alle Muskeln)",
        "instructions": [
          "Balle eine Faust oder lass die Hand offen",
          "Drehe das Handgelenk in Kreisen",
          "Führe es sowohl im als auch gegen den Uhrzeigersinn aus"
        ],
        "sets": "2 Sätze",
        "reps": "15–20 je Richtung",
        "tips": [
          "Mach immer beide Richtungen",
          "Vor jeder Trainingseinheit machen"
        ]
      },
      "fr": {
        "name": "Cercles du poignet",
        "description": "Exercice de mobilité simple pour les articulations du poignet.",
        "muscles": "Articulation du poignet (tous les muscles)",
        "instructions": [
          "Serrez le poing ou gardez la main ouverte",
          "Faites tourner le poignet en cercles",
          "Effectuez le mouvement dans le sens horaire et antihoraire"
        ],
        "sets": "2 séries",
        "reps": "15-20 dans chaque direction",
        "tips": [
          "Faites toujours les deux directions",
          "À faire avant chaque séance d'entraînement"
        ]
      },
      "es": {
        "name": "Círculos de Muñeca",
        "description": "Ejercicio simple de movilidad para las articulaciones de la muñeca.",
        "muscles": "Articulación de la muñeca (todos los músculos)",
        "instructions": [
          "Haz un puño o mantén la mano abierta",
          "Rota la muñeca en círculos",
          "Realízalo en sentido horario y antihorario"
        ],
        "sets": "2 series",
        "reps": "15-20 en cada dirección",
        "tips": [
          "Hazlo siempre en ambas direcciones",
          "Hazlo antes de cada sesión de entrenamiento"
        ]
      },
      "pt-BR": {
        "name": "Círculos de Punho",
        "description": "Exercício simples de mobilidade para as articulações do punho.",
        "muscles": "Articulação do punho (todos os músculos)",
        "instructions": [
          "Faz um punho ou mantém a mão aberta",
          "Roda o punho em círculos",
          "Faz no sentido dos ponteiros e no sentido contrário"
        ],
        "sets": "2 séries",
        "reps": "15-20 em cada direção",
        "tips": [
          "Faz sempre as duas direções",
          "Faz antes de cada sessão de treino"
        ]
      },
      "ja": {
        "name": "リストサークル",
        "description": "手首関節のシンプルな可動性エクササイズです。",
        "muscles": "手首関節（全筋肉）",
        "instructions": [
          "拳を握るか手を開いたままにする",
          "手首を円を描くように回す",
          "時計回りと反時計回りの両方を行う"
        ],
        "sets": "2セット",
        "reps": "各方向15〜20回",
        "tips": [
          "常に両方向を行う",
          "毎回のトレーニングセッションの前に行う"
        ]
      },
      "ko": {
        "name": "손목 돌리기",
        "description": "손목 관절을 위한 간단한 가동성 운동입니다.",
        "muscles": "손목 관절 (모든 근육)",
        "instructions": [
          "주먹을 쥐거나 손을 편 채로 두세요",
          "손목을 원을 그리며 돌리세요",
          "시계 방향과 반시계 방향으로 모두 하세요"
        ],
        "sets": "2세트",
        "reps": "방향당 15-20회",
        "tips": [
          "항상 양방향으로 하세요",
          "모든 훈련 세션 전에 하세요"
        ]
      }
    }
  },
  {
    "id": "forearmStretches",
    "slug": "forearm-stretches",
    "emoji": "🧘",
    "category": "warmup",
    "equipmentKeys": [
      "none"
    ],
    "difficulty": "beginner",
    "hasVideo": false,
    "relatedTechniqueIds": [],
    "armGroup": "warmup",
    "translations": {
      "en": {
        "name": "Forearm Stretches",
        "description": "Static stretches for forearm flexors and extensors.",
        "muscles": "Forearm flexors and extensors",
        "instructions": [
          "Extend arm with palm up, pull fingers back",
          "Hold stretch for 20-30 seconds",
          "Repeat with palm down for extensors"
        ],
        "sets": "2 sets each",
        "reps": "20-30 seconds hold",
        "tips": [
          "Stretch both flexors and extensors",
          "Essential for recovery between sessions"
        ]
      },
      "ru": {
        "name": "Растяжка предплечья",
        "description": "Статическая растяжка для сгибателей и разгибателей предплечья.",
        "muscles": "Сгибатели и разгибатели предплечья",
        "instructions": [
          "Вытяните руку ладонью вверх, потяните пальцы назад",
          "Удерживайте растяжку 20-30 секунд",
          "Повторите ладонью вниз для разгибателей"
        ],
        "sets": "2 подхода каждая",
        "reps": "20-30 секунд удержания",
        "tips": [
          "Растягивайте и сгибатели, и разгибатели",
          "Необходимо для восстановления между сессиями"
        ]
      },
      "de": {
        "name": "Unterarmdehnungen",
        "description": "Statische Dehnungen für die Unterarmbeuger und -strecker.",
        "muscles": "Unterarmbeuger und -strecker",
        "instructions": [
          "Strecke den Arm mit der Handfläche nach oben, ziehe die Finger zurück",
          "Halte die Dehnung 20–30 Sekunden",
          "Wiederhole mit Handfläche nach unten für die Strecker"
        ],
        "sets": "2 Sätze je",
        "reps": "20–30 Sekunden halten",
        "tips": [
          "Dehne sowohl Beuger als auch Strecker",
          "Essenziell für die Erholung zwischen den Einheiten"
        ]
      },
      "fr": {
        "name": "Étirements de l'avant-bras",
        "description": "Étirements statiques pour les fléchisseurs et extenseurs de l'avant-bras.",
        "muscles": "Fléchisseurs et extenseurs de l'avant-bras",
        "instructions": [
          "Tendez le bras paume vers le haut, tirez les doigts vers l'arrière",
          "Maintenez l'étirement pendant 20-30 secondes",
          "Répétez paume vers le bas pour les extenseurs"
        ],
        "sets": "2 séries de chaque",
        "reps": "Maintien de 20-30 secondes",
        "tips": [
          "Étirez à la fois les fléchisseurs et les extenseurs",
          "Essentiel pour la récupération entre les séances"
        ]
      },
      "es": {
        "name": "Estiramientos de Antebrazo",
        "description": "Estiramientos estáticos para los flexores y extensores del antebrazo.",
        "muscles": "Flexores y extensores del antebrazo",
        "instructions": [
          "Extiende el brazo con la palma hacia arriba, tira de los dedos hacia atrás",
          "Mantén el estiramiento durante 20-30 segundos",
          "Repite con la palma hacia abajo para los extensores"
        ],
        "sets": "2 series cada uno",
        "reps": "20-30 segundos de sostén",
        "tips": [
          "Estira tanto los flexores como los extensores",
          "Esencial para la recuperación entre sesiones"
        ]
      },
      "pt-BR": {
        "name": "Alongamentos de Antebraço",
        "description": "Alongamentos estáticos para os flexores e extensores do antebraço.",
        "muscles": "Flexores e extensores do antebraço",
        "instructions": [
          "Estende o braço com a palma para cima, puxa os dedos para trás",
          "Mantém o alongamento durante 20-30 segundos",
          "Repete com a palma para baixo para os extensores"
        ],
        "sets": "2 séries cada",
        "reps": "20-30 segundos de isometria",
        "tips": [
          "Alonga tanto os flexores como os extensores",
          "Essencial para a recuperação entre sessões"
        ]
      },
      "ja": {
        "name": "前腕ストレッチ",
        "description": "前腕屈筋群と伸筋群のためのスタティックストレッチです。",
        "muscles": "前腕屈筋群と伸筋群",
        "instructions": [
          "手のひらを上にして腕を伸ばし、指を後ろに引く",
          "20〜30秒ストレッチを保持する",
          "手のひらを下にして伸筋群も繰り返す"
        ],
        "sets": "各2セット",
        "reps": "20〜30秒保持",
        "tips": [
          "屈筋群と伸筋群の両方をストレッチする",
          "セッション間のリカバリーに不可欠"
        ]
      },
      "ko": {
        "name": "전완 스트레칭",
        "description": "전완 굴근과 신근을 위한 정적 스트레칭입니다.",
        "muscles": "전완 굴근 및 신근",
        "instructions": [
          "손바닥을 위로 하고 팔을 뻗어 손가락을 뒤로 당기세요",
          "20-30초간 스트레칭을 유지하세요",
          "신근을 위해 손바닥을 아래로 하고 반복하세요"
        ],
        "sets": "각 2세트",
        "reps": "20-30초 유지",
        "tips": [
          "굴근과 신근을 모두 스트레칭하세요",
          "세션 간 회복에 필수적입니다"
        ]
      }
    }
  },
  {
    "id": "larrattPronationPulls",
    "slug": "larratt-pronation-pulls",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Larratt Pronation Pulls",
        "description": "Cable pronation drill popularized by top pros, focusing on slow, controlled rotation through the full range.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Attach a handle to a low cable or band anchor",
          "Rotate your forearm from supinated to pronated position slowly",
          "Return to start with control, resisting the band the whole way"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Slow down the rotation — speed reduces time under tension",
          "Let the fingers lead the rotation, not just the wrist"
        ]
      },
      "ru": {
        "name": "Пронация Ларратта",
        "description": "Тяга на пронацию на блоке, популяризированная топ-профи; акцент на медленном контролируемом вращении по полной амплитуде.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Закрепите рукоять на низком блоке или резинке",
          "Медленно вращайте предплечье из супинации в пронацию",
          "Вернитесь в исходное положение с контролем, сопротивляясь резинке"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Замедлите вращение — скорость снижает время под нагрузкой",
          "Пусть пальцы ведут вращение, а не только кисть"
        ]
      },
      "de": {
        "name": "Larratt-Pronations-Züge",
        "description": "Von Top-Profis populärer Pronations-Zug am Kabelzug mit Fokus auf langsame, kontrollierte Drehung über den vollen Radius.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Befestige einen Griff an einem niedrigen Kabelzug oder Band",
          "Drehe den Unterarm langsam von supiniert zu proniert",
          "Kehre kontrolliert zurück, gegen den Widerstand des Bands"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Verlangsame die Drehung – Tempo reduziert die Spannungsdauer",
          "Lass die Finger die Drehung führen, nicht nur das Handgelenk"
        ]
      },
      "fr": {
        "name": "Tirages de pronation Larratt",
        "description": "Tirage de pronation à la poulie popularisé par les meilleurs pros, avec l'accent sur une rotation lente et contrôlée sur toute l'amplitude.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Fixez une poignée à une poulie basse ou un élastique",
          "Faites tourner l'avant-bras lentement de supination vers pronation",
          "Revenez au départ en contrôlant, en résistant à l'élastique tout du long"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Ralentissez la rotation — la vitesse réduit le temps sous tension",
          "Laissez les doigts guider la rotation, pas seulement le poignet"
        ]
      },
      "es": {
        "name": "Tirones de Pronación Larratt",
        "description": "Tirón de pronación en polea popularizado por los mejores profesionales, con énfasis en la rotación lenta y controlada en todo el rango.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Fija un asa a una polea baja o banda",
          "Rota el antebrazo lentamente de supinado a pronado",
          "Vuelve al inicio con control, resistiendo la banda todo el camino"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Ralentiza la rotación — la velocidad reduce el tiempo bajo tensión",
          "Deja que los dedos guíen la rotación, no solo la muñeca"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Pronação Larratt",
        "description": "Puxada de pronação no cabo popularizada pelos melhores profissionais, com foco na rotação lenta e controlada em toda a amplitude.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Fixa uma pega num cabo baixo ou banda",
          "Roda o antebraço lentamente de supinado para pronado",
          "Regressa ao início com controlo, resistindo à banda todo o percurso"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Abranda a rotação — a velocidade reduz o tempo sob tensão",
          "Deixa os dedos liderarem a rotação, não apenas o punho"
        ]
      },
      "ja": {
        "name": "ラレット式プロネーションプル",
        "description": "トッププロが広めたケーブルでのプロネーションプルで、フルレンジでのゆっくりとした制御された回転に重点を置きます。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "低いケーブルかバンドにハンドルを取り付ける",
          "前腕をゆっくりと回外から回内へ回す",
          "バンドに抵抗しながら制御して開始位置に戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "回転を遅くする－スピードは緊張時間を減らす",
          "手首だけでなく指に回転をリードさせる"
        ]
      },
      "ko": {
        "name": "라렛 회내 풀",
        "description": "최상급 선수들이 대중화한 케이블 회내 운동으로, 전체 범위에서 느리고 통제된 회전에 중점을 둡니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "낮은 케이블이나 밴드에 손잡이를 연결하세요",
          "전완을 천천히 회외에서 회내로 회전시키세요",
          "밴드에 저항하며 통제하여 시작 자세로 돌아가세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "회전을 늦추세요 - 속도는 긴장 시간을 줄입니다",
          "손목이 아닌 손가락이 회전을 이끌게 하세요"
        ]
      }
    }
  },
  {
    "id": "bandPronationPulls",
    "slug": "band-pronation-pulls",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Resistance Band Pronation Pulls",
        "description": "Home-friendly pronation pull using a resistance band instead of a cable stack.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Anchor a resistance band at forearm height",
          "Grip the handle and rotate your forearm into pronation",
          "Control the return, don't let the band snap you back"
        ],
        "sets": "3-4 sets",
        "reps": "12-15 reps",
        "tips": [
          "No cable machine needed — great for home training",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Пронация с резинкой",
        "description": "Домашний вариант тяги на пронацию с использованием резиновой ленты вместо блока.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Закрепите резинку на уровне предплечья",
          "Возьмитесь за рукоять и вращайте предплечье в пронацию",
          "Контролируйте возврат, не позволяйте резинке дёрнуть руку назад"
        ],
        "sets": "3-4 подхода",
        "reps": "12-15 повторений",
        "tips": [
          "Не требуется блочный тренажёр — отлично для домашних тренировок",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Pronations-Züge mit Widerstandsband",
        "description": "Heimtaugliche Variante des Pronations-Zugs mit Widerstandsband statt Kabelzug.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Befestige ein Widerstandsband auf Unterarmhöhe",
          "Greife den Griff und drehe den Unterarm in Pronation",
          "Kontrolliere die Rückkehr, lass dich nicht zurückreißen"
        ],
        "sets": "3–4 Sätze",
        "reps": "12–15 Wdh.",
        "tips": [
          "Kein Kabelzug nötig – ideal fürs Heimtraining",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Tirages de pronation à l'élastique",
        "description": "Variante pour la maison du tirage de pronation avec un élastique au lieu d'une poulie.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Fixez un élastique à hauteur d'avant-bras",
          "Saisissez la poignée et faites tourner l'avant-bras en pronation",
          "Contrôlez le retour, ne vous laissez pas tirer en arrière par l'élastique"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 répétitions",
        "tips": [
          "Aucune poulie nécessaire — parfait pour s'entraîner à la maison",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Tirones de Pronación con Banda",
        "description": "Variante casera del tirón de pronación usando una banda de resistencia en lugar de polea.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Fija una banda de resistencia a la altura del antebrazo",
          "Agarra el asa y rota el antebrazo hacia la pronación",
          "Controla el retorno, no dejes que la banda te tire hacia atrás"
        ],
        "sets": "3-4 series",
        "reps": "12-15 repeticiones",
        "tips": [
          "No necesitas polea — ideal para entrenar en casa",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Pronação com Banda",
        "description": "Variante caseira da puxada de pronação usando uma banda elástica em vez de cabo.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Fixa uma banda elástica à altura do antebraço",
          "Agarra a pega e roda o antebraço para pronação",
          "Controla o regresso, não deixes a banda puxar-te para trás"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 reps",
        "tips": [
          "Sem necessidade de máquina de cabos — ótimo para treino em casa",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "バンドプロネーションプル",
        "description": "ケーブルの代わりにレジスタンスバンドを使う自宅向けのプロネーションプルです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "前腕の高さにレジスタンスバンドを固定する",
          "ハンドルを握り前腕を回内方向へ回す",
          "バンドに引き戻されないよう戻りを制御する"
        ],
        "sets": "3〜4セット",
        "reps": "12〜15レップ",
        "tips": [
          "ケーブルマシン不要－自宅トレーニングに最適",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "밴드 회내 풀",
        "description": "케이블 대신 저항 밴드를 사용하는 가정용 회내 풀 변형입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "전완 높이에 저항 밴드를 고정하세요",
          "손잡이를 잡고 전완을 회내 방향으로 회전시키세요",
          "밴드에 뒤로 당겨지지 않도록 복귀를 통제하세요"
        ],
        "sets": "3-4세트",
        "reps": "12-15회",
        "tips": [
          "케이블 머신이 필요 없습니다 - 홈트레이닝에 좋습니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "bandHandlePronationCurls",
    "slug": "band-handle-pronation-curls",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Pronation Curls with Resistance Band Handle",
        "description": "Pronation curl using a dedicated handle attachment on a resistance band.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Grip the handle at one end of the band",
          "Curl and rotate into pronation together",
          "Reset slowly under tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Grip near the end of the handle for a longer lever",
          "Keep a slow, controlled tempo throughout"
        ]
      },
      "ru": {
        "name": "Пронация с рукоятью на резинке",
        "description": "Сгибание на пронацию с использованием специальной рукояти на резиновой ленте.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Возьмитесь за рукоять на одном конце резинки",
          "Сгибайте и вращайте в пронацию одновременно",
          "Медленно возвращайтесь под напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Держитесь ближе к концу рукояти для более длинного рычага",
          "Сохраняйте медленный контролируемый темп на протяжении"
        ]
      },
      "de": {
        "name": "Pronations-Curls mit Bandgriff",
        "description": "Pronations-Curl mit einem speziellen Griffaufsatz an einem Widerstandsband.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Greife den Griff an einem Ende des Bands",
          "Curle und drehe gleichzeitig in Pronation",
          "Kehre langsam unter Spannung zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Greife nah am Ende des Griffs für einen längeren Hebel",
          "Halte durchgehend ein langsames, kontrolliertes Tempo"
        ]
      },
      "fr": {
        "name": "Curls de pronation avec poignée élastique",
        "description": "Curl de pronation avec une poignée spéciale sur un élastique.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Saisissez la poignée à une extrémité de l'élastique",
          "Enroulez et tournez en pronation en même temps",
          "Revenez lentement sous tension"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Tenez près de l'extrémité de la poignée pour un levier plus long",
          "Gardez un tempo lent et contrôlé tout du long"
        ]
      },
      "es": {
        "name": "Curl de Pronación con Asa en Banda",
        "description": "Curl de pronación usando un asa especial en una banda de resistencia.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Agarra el asa en un extremo de la banda",
          "Curva y rota hacia la pronación al mismo tiempo",
          "Vuelve lentamente bajo tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Agarra cerca del extremo del asa para una palanca más larga",
          "Mantén un tempo lento y controlado en todo momento"
        ]
      },
      "pt-BR": {
        "name": "Rosca de Pronação com Pega em Banda",
        "description": "Rosca de pronação usando uma pega especial numa banda elástica.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Agarra a pega numa extremidade da banda",
          "Enrola e roda para pronação ao mesmo tempo",
          "Regressa lentamente sob tensão"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Agarra perto da extremidade da pega para uma alavanca mais longa",
          "Mantém um ritmo lento e controlado do início ao fim"
        ]
      },
      "ja": {
        "name": "バンドハンドルプロネーションカール",
        "description": "レジスタンスバンドの専用ハンドルを使ったプロネーションカールです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "バンドの片端のハンドルを握る",
          "巻き込みと回内を同時に行う",
          "緊張下でゆっくり戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "レバーを長くするためハンドルの端寄りを握る",
          "常にゆっくりと制御されたテンポを保つ"
        ]
      },
      "ko": {
        "name": "밴드 핸들 회내 컬",
        "description": "저항 밴드의 전용 핸들을 사용하는 회내 컬입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "밴드 한쪽 끝의 핸들을 잡으세요",
          "감기와 회내를 동시에 하세요",
          "긴장 상태로 천천히 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "더 긴 지렛대를 위해 핸들 끝 쪽을 잡으세요",
          "내내 느리고 통제된 템포를 유지하세요"
        ]
      }
    }
  },
  {
    "id": "explosivePronationLifts",
    "slug": "explosive-pronation-lifts",
    "emoji": "⚡",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "advanced",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Explosive Pronation Lifts",
        "description": "Power-focused pronation lift performed with speed to build fast-twitch rotational strength.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Set light-to-moderate resistance on a cable",
          "Rotate into pronation as explosively as possible",
          "Return under control and reset before the next rep"
        ],
        "sets": "3-4 sets",
        "reps": "5-8 explosive reps",
        "tips": [
          "Prioritize speed over load — this is a power drill",
          "Rest fully between sets to keep every rep explosive"
        ]
      },
      "ru": {
        "name": "Взрывная пронация",
        "description": "Силовая тяга на пронацию, выполняемая быстро для развития взрывной вращательной силы.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Установите лёгкое-среднее сопротивление на блоке",
          "Вращайте в пронацию максимально взрывно",
          "Возвращайтесь с контролем и восстанавливайтесь перед следующим повторением"
        ],
        "sets": "3-4 подхода",
        "reps": "5-8 взрывных повторений",
        "tips": [
          "Приоритет скорости над весом — это силовое упражнение",
          "Полностью отдыхайте между подходами, чтобы каждое повторение было взрывным"
        ]
      },
      "de": {
        "name": "Explosive Pronations-Hebungen",
        "description": "Kraftorientierter Pronations-Zug, schnell ausgeführt zum Aufbau explosiver Rotationskraft.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Stelle leichten bis mittleren Widerstand am Kabelzug ein",
          "Drehe so explosiv wie möglich in Pronation",
          "Kehre kontrolliert zurück und erhole dich vor der nächsten Wdh."
        ],
        "sets": "3–4 Sätze",
        "reps": "5–8 explosive Wdh.",
        "tips": [
          "Priorisiere Geschwindigkeit vor Gewicht – dies ist eine Kraftübung",
          "Erhole dich vollständig zwischen den Sätzen, damit jede Wdh. explosiv bleibt"
        ]
      },
      "fr": {
        "name": "Levées explosives de pronation",
        "description": "Tirage de pronation axé sur la puissance, exécuté rapidement pour développer la force rotationnelle explosive.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Réglez une résistance légère à modérée à la poulie",
          "Tournez en pronation aussi explosivement que possible",
          "Revenez en contrôlant et récupérez avant la répétition suivante"
        ],
        "sets": "3-4 séries",
        "reps": "5-8 répétitions explosives",
        "tips": [
          "Priorisez la vitesse sur la charge — c'est un exercice de puissance",
          "Récupérez complètement entre les séries pour que chaque répétition reste explosive"
        ]
      },
      "es": {
        "name": "Elevaciones Explosivas de Pronación",
        "description": "Tirón de pronación centrado en la potencia, ejecutado con velocidad para desarrollar fuerza rotacional explosiva.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Ajusta resistencia ligera a moderada en la polea",
          "Rota hacia la pronación tan explosivamente como sea posible",
          "Vuelve con control y recupérate antes de la siguiente repetición"
        ],
        "sets": "3-4 series",
        "reps": "5-8 repeticiones explosivas",
        "tips": [
          "Prioriza la velocidad sobre la carga — es un ejercicio de potencia",
          "Descansa completamente entre series para que cada repetición sea explosiva"
        ]
      },
      "pt-BR": {
        "name": "Elevações Explosivas de Pronação",
        "description": "Puxada de pronação focada em potência, executada com velocidade para desenvolver força rotacional explosiva.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Ajusta resistência leve a moderada no cabo",
          "Roda para pronação o mais explosivamente possível",
          "Regressa com controlo e recupera antes da próxima rep"
        ],
        "sets": "3-4 séries",
        "reps": "5-8 reps explosivas",
        "tips": [
          "Prioriza a velocidade em vez da carga — este é um exercício de potência",
          "Descansa completamente entre séries para manter cada rep explosiva"
        ]
      },
      "ja": {
        "name": "エクスプローシブプロネーションリフト",
        "description": "爆発的な回転力を鍛えるため、素早く行うパワー重視のプロネーションプルです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "ケーブルに軽め〜中程度の抵抗をセットする",
          "できるだけ爆発的に回内方向へ回す",
          "制御して戻し、次のレップ前に回復する"
        ],
        "sets": "3〜4セット",
        "reps": "5〜8爆発的レップ",
        "tips": [
          "重量よりスピードを優先する－これはパワー種目",
          "各レップを爆発的に保つためセット間で完全に休息する"
        ]
      },
      "ko": {
        "name": "폭발적 회내 리프트",
        "description": "폭발적인 회전력을 기르기 위해 빠르게 수행하는 파워 중심 회내 풀입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "케이블에 가벼운-중간 저항을 설정하세요",
          "최대한 폭발적으로 회내 방향으로 회전하세요",
          "통제하여 돌아가고 다음 반복 전에 회복하세요"
        ],
        "sets": "3-4세트",
        "reps": "5-8회 폭발적 반복",
        "tips": [
          "무게보다 속도를 우선하세요 - 이것은 파워 운동입니다",
          "각 반복이 폭발적으로 유지되도록 세트 사이에 충분히 쉬세요"
        ]
      }
    }
  },
  {
    "id": "chainHandlePronationLifts",
    "slug": "chain-handle-pronation-lifts",
    "emoji": "⛓️",
    "category": "dynamic",
    "equipmentKeys": [
      "chain"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Chain Handle Pronation Lifts",
        "description": "Pronation lift using a chain-handle attachment for a longer lever and extra shoulder stabilization.",
        "muscles": "Pronator teres, pronator quadratus, shoulder stabilizers",
        "instructions": [
          "Attach the chain handle to a cable or fixed point",
          "Rotate into pronation while bracing the shoulder",
          "Control the return through the full range"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "The chain's length increases the lever — start lighter than you expect",
          "Keep the shoulder braced so the rotation stays isolated to the forearm"
        ]
      },
      "ru": {
        "name": "Пронация с цепной рукоятью",
        "description": "Тяга на пронацию с использованием рукояти на цепи для более длинного рычага и дополнительной стабилизации плеча.",
        "muscles": "Круглый пронатор, квадратный пронатор, стабилизаторы плеча",
        "instructions": [
          "Закрепите цепную рукоять на блоке или неподвижной точке",
          "Вращайте в пронацию, стабилизируя плечо",
          "Контролируйте возврат по полной амплитуде"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Длина цепи увеличивает рычаг — начните легче, чем ожидаете",
          "Держите плечо стабилизированным, чтобы вращение оставалось изолированным в предплечье"
        ]
      },
      "de": {
        "name": "Pronations-Hebungen mit Kettengriff",
        "description": "Pronations-Hebung mit Kettengriff-Aufsatz für einen längeren Hebel und zusätzliche Schulterstabilisierung.",
        "muscles": "Pronator teres, Pronator quadratus, Schulterstabilisatoren",
        "instructions": [
          "Befestige den Kettengriff an Kabelzug oder fixem Punkt",
          "Drehe in Pronation, während du die Schulter stabilisierst",
          "Kontrolliere die Rückkehr über den vollen Radius"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Die Kettenlänge vergrößert den Hebel – starte leichter als erwartet",
          "Halte die Schulter stabil, damit die Drehung im Unterarm isoliert bleibt"
        ]
      },
      "fr": {
        "name": "Levées de pronation avec poignée à chaîne",
        "description": "Tirage de pronation avec une poignée à chaîne pour un levier plus long et une stabilisation supplémentaire de l'épaule.",
        "muscles": "Rond pronateur, carré pronateur, stabilisateurs de l'épaule",
        "instructions": [
          "Fixez la poignée à chaîne à une poulie ou un point fixe",
          "Tournez en pronation tout en stabilisant l'épaule",
          "Contrôlez le retour sur toute l'amplitude"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "La longueur de la chaîne augmente le levier — commencez plus léger que prévu",
          "Gardez l'épaule stabilisée pour que la rotation reste isolée à l'avant-bras"
        ]
      },
      "es": {
        "name": "Elevaciones de Pronación con Asa de Cadena",
        "description": "Tirón de pronación usando un asa de cadena para una palanca más larga y estabilización extra del hombro.",
        "muscles": "Pronador redondo, pronador cuadrado, estabilizadores del hombro",
        "instructions": [
          "Fija el asa de cadena a una polea o punto fijo",
          "Rota hacia la pronación mientras estabilizas el hombro",
          "Controla el retorno en todo el rango"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "La longitud de la cadena aumenta la palanca — empieza más ligero de lo esperado",
          "Mantén el hombro estabilizado para que la rotación quede aislada en el antebrazo"
        ]
      },
      "pt-BR": {
        "name": "Elevações de Pronação com Pega de Corrente",
        "description": "Puxada de pronação usando um acessório de pega de corrente para uma alavanca mais longa e estabilização extra do ombro.",
        "muscles": "Pronador redondo, pronador quadrado, estabilizadores do ombro",
        "instructions": [
          "Fixa a pega de corrente a um cabo ou ponto fixo",
          "Roda para pronação enquanto estabilizas o ombro",
          "Controla o regresso em toda a amplitude"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "O comprimento da corrente aumenta a alavanca — começa mais leve do que esperas",
          "Mantém o ombro estabilizado para que a rotação fique isolada no antebraço"
        ]
      },
      "ja": {
        "name": "チェーンハンドルプロネーションリフト",
        "description": "チェーンハンドルアタッチメントを使い、より長いレバーと肩の追加安定化を加えたプロネーションリフトです。",
        "muscles": "円回内筋、方形回内筋、肩の安定筋群",
        "instructions": [
          "チェーンハンドルをケーブルか固定点に取り付ける",
          "肩を安定させながら回内方向へ回す",
          "フルレンジで戻りを制御する"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "チェーンの長さがレバーを増やす－予想より軽くから始める",
          "回転が前腕だけに集中するよう肩を安定させておく"
        ]
      },
      "ko": {
        "name": "체인 핸들 회내 리프트",
        "description": "더 긴 지렛대와 추가적인 어깨 안정화를 위해 체인 핸들 어태치먼트를 사용하는 회내 리프트입니다.",
        "muscles": "원회내근, 방형회내근, 어깨 안정근",
        "instructions": [
          "체인 핸들을 케이블이나 고정 지점에 연결하세요",
          "어깨를 안정시키며 회내 방향으로 회전하세요",
          "전체 범위에서 복귀를 통제하세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "체인 길이가 지렛대를 늘립니다 - 예상보다 가볍게 시작하세요",
          "회전이 전완에만 집중되도록 어깨를 안정되게 유지하세요"
        ]
      }
    }
  },
  {
    "id": "pronationPulls45",
    "slug": "pronation-pulls-45",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Pronation Pulls — 45°",
        "description": "Pronation pull performed at a 45° table-style angle to mimic mid-match hand position.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Set the cable or band at a 45° angle",
          "Rotate into pronation while holding the angle",
          "Return with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Пронация — 45°",
        "description": "Тяга на пронацию под углом 45°, имитирующим позицию руки в середине поединка.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Установите блок или резинку под углом 45°",
          "Вращайте в пронацию, удерживая угол",
          "Возвращайтесь с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Pronations-Züge — 45°",
        "description": "Pronations-Zug im 45°-Winkel, der die Handposition mitten im Match nachahmt.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Stelle Kabelzug oder Band auf einen 45°-Winkel ein",
          "Drehe in Pronation, halte den Winkel",
          "Kehre kontrolliert zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Tirages de pronation — 45°",
        "description": "Tirage de pronation à un angle de 45°, imitant la position de main en milieu de match.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Réglez la poulie ou l'élastique à un angle de 45°",
          "Tournez en pronation en maintenant l'angle",
          "Revenez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Tirones de Pronación — 45°",
        "description": "Tirón de pronación en ángulo de 45°, que imita la posición de la mano a mitad de combate.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Ajusta la polea o banda a un ángulo de 45°",
          "Rota hacia la pronación manteniendo el ángulo",
          "Vuelve con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Pronação — 45°",
        "description": "Puxada de pronação num ângulo de 45°, que imita a posição da mão a meio do combate.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Ajusta o cabo ou banda para um ângulo de 45°",
          "Roda para pronação mantendo o ângulo",
          "Regressa com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "プロネーションプル－45°",
        "description": "試合中盤の手の位置を模した45°の角度でのプロネーションプルです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "ケーブルかバンドを45°の角度にセットする",
          "角度を維持しながら回内方向へ回す",
          "制御して戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "회내 풀 — 45°",
        "description": "시합 중반의 손 위치를 모방한 45° 각도의 회내 풀입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "케이블이나 밴드를 45° 각도로 설정하세요",
          "각도를 유지하며 회내 방향으로 회전하세요",
          "통제하여 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "pronationPulls110",
    "slug": "pronation-pulls-110",
    "emoji": "🔄",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Pronation Pulls — 110°+",
        "description": "Pronation pull performed at a wide 110°+ angle, closer to a defensive/finishing hand position.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Set the cable or band at a wide 110°+ angle",
          "Rotate into pronation while holding the angle",
          "Return with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "The wider angle challenges pronation strength closer to a finishing position",
          "Focus on controlled rotation, not speed"
        ]
      },
      "ru": {
        "name": "Пронация — 110°+",
        "description": "Тяга на пронацию под широким углом 110°+, ближе к защитной/завершающей позиции руки.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Установите блок или резинку под широким углом 110°+",
          "Вращайте в пронацию, удерживая угол",
          "Возвращайтесь с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Более широкий угол нагружает пронацию ближе к завершающей позиции",
          "Фокус на контролируемом вращении, не скорости"
        ]
      },
      "de": {
        "name": "Pronations-Züge — 110°+",
        "description": "Pronations-Zug im weiten Winkel von 110°+, näher an einer Abwehr-/Abschlussposition.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Stelle Kabelzug oder Band auf einen weiten Winkel von 110°+ ein",
          "Drehe in Pronation, halte den Winkel",
          "Kehre kontrolliert zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Der weitere Winkel fordert Pronationskraft näher an der Abschlussposition",
          "Konzentriere dich auf kontrollierte Drehung, nicht auf Tempo"
        ]
      },
      "fr": {
        "name": "Tirages de pronation — 110°+",
        "description": "Tirage de pronation à un angle large de 110°+, plus proche d'une position défensive/de finition.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Réglez la poulie ou l'élastique à un angle large de 110°+",
          "Tournez en pronation en maintenant l'angle",
          "Revenez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "L'angle plus large sollicite la pronation plus près de la position de finition",
          "Concentrez-vous sur une rotation contrôlée, pas sur la vitesse"
        ]
      },
      "es": {
        "name": "Tirones de Pronación — 110°+",
        "description": "Tirón de pronación en ángulo amplio de 110°+, más cerca de una posición defensiva/de remate.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Ajusta la polea o banda a un ángulo amplio de 110°+",
          "Rota hacia la pronación manteniendo el ángulo",
          "Vuelve con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "El ángulo más amplio exige fuerza de pronación cerca de la posición de remate",
          "Concéntrate en la rotación controlada, no en la velocidad"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Pronação — 110°+",
        "description": "Puxada de pronação num ângulo amplo de 110°+, mais próximo de uma posição defensiva/de finalização.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Ajusta o cabo ou banda para um ângulo amplo de 110°+",
          "Roda para pronação mantendo o ângulo",
          "Regressa com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "O ângulo mais amplo exige força de pronação mais perto da posição de finalização",
          "Foca-te em rotação controlada, não em velocidade"
        ]
      },
      "ja": {
        "name": "プロネーションプル－110°+",
        "description": "防御・フィニッシュ姿勢に近い110°+の広い角度でのプロネーションプルです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "ケーブルかバンドを110°+の広い角度にセットする",
          "角度を維持しながら回内方向へ回す",
          "制御して戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "広い角度はフィニッシュ姿勢に近いプロネーション力を要求する",
          "スピードではなく制御された回転に集中する"
        ]
      },
      "ko": {
        "name": "회내 풀 — 110°+",
        "description": "방어/마무리 자세에 가까운 110°+의 넓은 각도의 회내 풀입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "케이블이나 밴드를 110°+의 넓은 각도로 설정하세요",
          "각도를 유지하며 회내 방향으로 회전하세요",
          "통제하여 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "더 넓은 각도는 마무리 자세에 가까운 회내 힘을 요구합니다",
          "속도가 아닌 통제된 회전에 집중하세요"
        ]
      }
    }
  },
  {
    "id": "staticPronationHold110",
    "slug": "static-pronation-hold-110",
    "emoji": "🔄",
    "category": "static",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Static Pronation Hold — 110°+",
        "description": "Isometric pronation hold at a wide 110°+ angle for finishing-position strength.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Set the cable or band at a wide 110°+ angle",
          "Rotate into full pronation and hold",
          "Keep tension steady for the full hold"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "The wider angle trains pronation closer to a finishing position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание пронации — 110°+",
        "description": "Изометрическое удержание пронации под широким углом 110°+ для силы в завершающей позиции.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Установите блок или резинку под широким углом 110°+",
          "Вращайте в полную пронацию и удерживайте",
          "Сохраняйте постоянное напряжение на протяжении удержания"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Более широкий угол тренирует пронацию ближе к завершающей позиции",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Pronations-Halten — 110°+",
        "description": "Isometrisches Pronations-Halten im weiten Winkel von 110°+ für Kraft in der Abschlussposition.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Stelle Kabelzug oder Band auf einen weiten Winkel von 110°+ ein",
          "Drehe in volle Pronation und halte",
          "Halte die Spannung während des gesamten Haltens konstant"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Der weitere Winkel trainiert Pronation näher an der Abschlussposition",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien en pronation — 110°+",
        "description": "Maintien isométrique en pronation à un angle large de 110°+ pour la force en position de finition.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Réglez la poulie ou l'élastique à un angle large de 110°+",
          "Tournez en pronation complète et maintenez",
          "Gardez une tension constante pendant tout le maintien"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "L'angle plus large entraîne la pronation plus près de la position de finition",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Pronación — 110°+",
        "description": "Sostén isométrico de pronación en ángulo amplio de 110°+ para fuerza en posición de remate.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Ajusta la polea o banda a un ángulo amplio de 110°+",
          "Rota hasta pronación completa y sostén",
          "Mantén tensión constante durante todo el sostén"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "El ángulo más amplio entrena la pronación cerca de la posición de remate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Pronação — 110°+",
        "description": "Isometria de pronação num ângulo amplo de 110°+ para força na posição de finalização.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Ajusta o cabo ou banda para um ângulo amplo de 110°+",
          "Roda até pronação completa e mantém",
          "Mantém a tensão constante durante toda a isometria"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "O ângulo mais amplo treina a pronação mais perto da posição de finalização",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "プロネーションホールド－110°+",
        "description": "フィニッシュ姿勢の力のための110°+の広い角度でのアイソメトリックプロネーションホールドです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "ケーブルかバンドを110°+の広い角度にセットする",
          "完全な回内まで回して保持する",
          "保持中は緊張を一定に保つ"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "広い角度はフィニッシュ姿勢に近いプロネーションを鍛える",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "회내 홀드 — 110°+",
        "description": "마무리 자세 근력을 위한 110°+의 넓은 각도의 등척성 회내 홀드입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "케이블이나 밴드를 110°+의 넓은 각도로 설정하세요",
          "완전한 회내까지 회전하고 유지하세요",
          "홀드 내내 긴장을 일정하게 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "더 넓은 각도는 마무리 자세에 가까운 회내를 훈련합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticPronationHold45",
    "slug": "static-pronation-hold-45",
    "emoji": "🔄",
    "category": "static",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Static Pronation Hold — 45°",
        "description": "Isometric pronation hold at a 45° table-style angle.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Set the cable or band at a 45° angle",
          "Rotate into full pronation and hold",
          "Keep tension steady for the full hold"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание пронации — 45°",
        "description": "Изометрическое удержание пронации под углом 45°, имитирующим позицию стола.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Установите блок или резинку под углом 45°",
          "Вращайте в полную пронацию и удерживайте",
          "Сохраняйте постоянное напряжение на протяжении удержания"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Pronations-Halten — 45°",
        "description": "Isometrisches Pronations-Halten im 45°-Winkel, der die Tischposition nachahmt.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Stelle Kabelzug oder Band auf einen 45°-Winkel ein",
          "Drehe in volle Pronation und halte",
          "Halte die Spannung während des gesamten Haltens konstant"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien en pronation — 45°",
        "description": "Maintien isométrique en pronation à un angle de 45°, imitant la position de table.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Réglez la poulie ou l'élastique à un angle de 45°",
          "Tournez en pronation complète et maintenez",
          "Gardez une tension constante pendant tout le maintien"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Pronación — 45°",
        "description": "Sostén isométrico de pronación en ángulo de 45°, que imita la posición de mesa.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Ajusta la polea o banda a un ángulo de 45°",
          "Rota hasta pronación completa y sostén",
          "Mantén tensión constante durante todo el sostén"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Pronação — 45°",
        "description": "Isometria de pronação num ângulo de 45°, que imita a posição de mesa.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Ajusta o cabo ou banda para um ângulo de 45°",
          "Roda até pronação completa e mantém",
          "Mantém a tensão constante durante toda a isometria"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "プロネーションホールド－45°",
        "description": "テーブルの姿勢を模した45°の角度でのアイソメトリックプロネーションホールドです。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "ケーブルかバンドを45°の角度にセットする",
          "完全な回内まで回して保持する",
          "保持中は緊張を一定に保つ"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "회내 홀드 — 45°",
        "description": "테이블 자세를 모방한 45° 각도의 등척성 회내 홀드입니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "케이블이나 밴드를 45° 각도로 설정하세요",
          "완전한 회내까지 회전하고 유지하세요",
          "홀드 내내 긴장을 일정하게 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticPronationBandHold",
    "slug": "static-pronation-band-hold",
    "emoji": "🔄",
    "category": "static",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll",
      "pronation"
    ],
    "armGroup": "pronators",
    "translations": {
      "en": {
        "name": "Static Pronation Hold with Resistance Band",
        "description": "Isometric pronation hold using a resistance band, ideal for home training.",
        "muscles": "Pronator teres, pronator quadratus",
        "instructions": [
          "Anchor a resistance band at forearm height",
          "Rotate into full pronation and hold the position",
          "Keep tension steady for the full hold"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "No cable machine needed — great for home training",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание пронации с резинкой",
        "description": "Изометрическое удержание пронации с использованием резиновой ленты, идеально для домашних тренировок.",
        "muscles": "Круглый пронатор, квадратный пронатор",
        "instructions": [
          "Закрепите резинку на уровне предплечья",
          "Вращайте в полную пронацию и удерживайте позицию",
          "Сохраняйте постоянное напряжение на протяжении удержания"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Не требуется блочный тренажёр — отлично для домашних тренировок",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Pronations-Halten mit Widerstandsband",
        "description": "Isometrisches Pronations-Halten mit Widerstandsband, ideal fürs Heimtraining.",
        "muscles": "Pronator teres, Pronator quadratus",
        "instructions": [
          "Befestige ein Widerstandsband auf Unterarmhöhe",
          "Drehe in volle Pronation und halte die Position",
          "Halte die Spannung während des gesamten Haltens konstant"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Kein Kabelzug nötig – ideal fürs Heimtraining",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien en pronation à l'élastique",
        "description": "Maintien isométrique en pronation avec un élastique, idéal pour s'entraîner à la maison.",
        "muscles": "Rond pronateur, carré pronateur",
        "instructions": [
          "Fixez un élastique à hauteur d'avant-bras",
          "Tournez en pronation complète et maintenez la position",
          "Gardez une tension constante pendant tout le maintien"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Aucune poulie nécessaire — parfait pour s'entraîner à la maison",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Pronación con Banda",
        "description": "Sostén isométrico de pronación usando una banda de resistencia, ideal para entrenar en casa.",
        "muscles": "Pronador redondo, pronador cuadrado",
        "instructions": [
          "Fija una banda de resistencia a la altura del antebrazo",
          "Rota hasta pronación completa y sostén la posición",
          "Mantén tensión constante durante todo el sostén"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "No necesitas polea — ideal para entrenar en casa",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Pronação com Banda",
        "description": "Isometria de pronação usando uma banda elástica, ideal para treino em casa.",
        "muscles": "Pronador redondo, pronador quadrado",
        "instructions": [
          "Fixa uma banda elástica à altura do antebraço",
          "Roda até pronação completa e mantém a posição",
          "Mantém a tensão constante durante toda a isometria"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Sem necessidade de máquina de cabos — ótimo para treino em casa",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "バンドプロネーションホールド",
        "description": "レジスタンスバンドを使ったアイソメトリックプロネーションホールドで、自宅トレーニングに最適です。",
        "muscles": "円回内筋、方形回内筋",
        "instructions": [
          "前腕の高さにレジスタンスバンドを固定する",
          "完全な回内まで回して姿勢を保持する",
          "保持中は緊張を一定に保つ"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "ケーブルマシン不要－自宅トレーニングに最適",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "밴드 회내 홀드",
        "description": "저항 밴드를 사용하는 등척성 회내 홀드로, 홈트레이닝에 적합합니다.",
        "muscles": "원회내근, 방형회내근",
        "instructions": [
          "전완 높이에 저항 밴드를 고정하세요",
          "완전한 회내까지 회전하고 자세를 유지하세요",
          "홀드 내내 긴장을 일정하게 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "케이블 머신이 필요 없습니다 - 홈트레이닝에 좋습니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "supinationPull",
    "slug": "supination-pull",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Supination Pull",
        "description": "Cable supination pull to build hook and cupping strength.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Attach a handle to a low cable or band anchor",
          "Rotate your forearm from pronated to supinated (palm up)",
          "Return to start with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Focus on full rotation until your palm faces up",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Супинация на блоке",
        "description": "Тяга на супинацию на блоке для развития силы крюка и чашки.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Закрепите рукоять на низком блоке или резинке",
          "Вращайте предплечье из пронации в супинацию (ладонью вверх)",
          "Вернитесь в исходное положение с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Фокус на полном вращении до положения ладонью вверх",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Supinations-Zug",
        "description": "Supinations-Zug am Kabelzug zum Aufbau von Hook- und Cupping-Kraft.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Befestige einen Griff an einem niedrigen Kabelzug oder Band",
          "Drehe den Unterarm von proniert zu supiniert (Handfläche nach oben)",
          "Kehre kontrolliert zur Ausgangsposition zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Konzentriere dich auf die volle Drehung, bis die Handfläche nach oben zeigt",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Tirage de supination",
        "description": "Tirage de supination à la poulie pour développer la force du hook et du cupping.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Fixez une poignée à une poulie basse ou un élastique",
          "Tournez l'avant-bras de pronation vers supination (paume vers le haut)",
          "Revenez à la position de départ en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Concentrez-vous sur une rotation complète jusqu'à ce que la paume soit vers le haut",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Tirón de Supinación",
        "description": "Tirón de supinación en polea para desarrollar fuerza de gancho y ahuecado.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fija un asa a una polea baja o banda",
          "Rota el antebrazo de pronado a supinado (palma hacia arriba)",
          "Vuelve al inicio con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Concéntrate en la rotación completa hasta que la palma mire hacia arriba",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Puxada de Supinação",
        "description": "Puxada de supinação no cabo para desenvolver a força de hook e cupping.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fixa uma pega num cabo baixo ou banda",
          "Roda o antebraço de pronado para supinado (palma para cima)",
          "Regressa ao início com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Foca-te na rotação completa até a palma ficar virada para cima",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "スピネーションプル",
        "description": "フックとカッピングの力を鍛えるケーブルでのスピネーションプルです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "低いケーブルかバンドにハンドルを取り付ける",
          "前腕を回内から回外へ回す（手のひらを上に）",
          "制御して開始位置に戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "手のひらが上を向くまで完全に回転することに集中する",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "회외 풀",
        "description": "훅과 커핑 힘을 기르기 위한 케이블 회외 풀입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "낮은 케이블이나 밴드에 손잡이를 연결하세요",
          "전완을 회내에서 회외로 회전시키세요(손바닥 위로)",
          "통제하여 시작 자세로 돌아가세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "손바닥이 위를 향할 때까지 완전한 회전에 집중하세요",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "supinationPulls45",
    "slug": "supination-pulls-45",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Supination Pulls — 45°",
        "description": "Supination pull performed at a 45° table-style angle.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Set the cable at a 45° angle",
          "Rotate into supination while holding the angle",
          "Return with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Супинация — 45°",
        "description": "Тяга на супинацию под углом 45°, имитирующим позицию стола.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Установите блок под углом 45°",
          "Вращайте в супинацию, удерживая угол",
          "Возвращайтесь с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Supinations-Züge — 45°",
        "description": "Supinations-Zug im 45°-Winkel, der die Tischposition nachahmt.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Stelle den Kabelzug auf einen 45°-Winkel ein",
          "Drehe in Supination, halte den Winkel",
          "Kehre kontrolliert zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Tirages de supination — 45°",
        "description": "Tirage de supination à un angle de 45°, imitant la position de table.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Réglez la poulie à un angle de 45°",
          "Tournez en supination en maintenant l'angle",
          "Revenez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Tirones de Supinación — 45°",
        "description": "Tirón de supinación en ángulo de 45°, que imita la posición de mesa.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta la polea a un ángulo de 45°",
          "Rota hacia la supinación manteniendo el ángulo",
          "Vuelve con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Supinação — 45°",
        "description": "Puxada de supinação num ângulo de 45°, que imita a posição de mesa.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta o cabo para um ângulo de 45°",
          "Roda para supinação mantendo o ângulo",
          "Regressa com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "スピネーションプル－45°",
        "description": "テーブルの姿勢を模した45°の角度でのスピネーションプルです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "ケーブルを45°の角度にセットする",
          "角度を維持しながら回外方向へ回す",
          "制御して戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "회외 풀 — 45°",
        "description": "테이블 자세를 모방한 45° 각도의 회외 풀입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "케이블을 45° 각도로 설정하세요",
          "각도를 유지하며 회외 방향으로 회전하세요",
          "통제하여 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "bandSupinationPulls",
    "slug": "band-supination-pulls",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Resistance Band Supination Pulls",
        "description": "Home-friendly supination pull using a resistance band instead of a cable stack.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Anchor a resistance band at forearm height",
          "Rotate your forearm into supination against the band",
          "Control the return"
        ],
        "sets": "3-4 sets",
        "reps": "12-15 reps",
        "tips": [
          "No cable machine needed — great for home training",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Супинация с резинкой",
        "description": "Домашний вариант тяги на супинацию с использованием резиновой ленты вместо блока.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Закрепите резинку на уровне предплечья",
          "Вращайте предплечье в супинацию против резинки",
          "Контролируйте возврат"
        ],
        "sets": "3-4 подхода",
        "reps": "12-15 повторений",
        "tips": [
          "Не требуется блочный тренажёр — отлично для домашних тренировок",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Supinations-Züge mit Widerstandsband",
        "description": "Heimtaugliche Variante des Supinations-Zugs mit Widerstandsband statt Kabelzug.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Befestige ein Widerstandsband auf Unterarmhöhe",
          "Drehe den Unterarm gegen das Band in Supination",
          "Kontrolliere die Rückkehr"
        ],
        "sets": "3–4 Sätze",
        "reps": "12–15 Wdh.",
        "tips": [
          "Kein Kabelzug nötig – ideal fürs Heimtraining",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Tirages de supination à l'élastique",
        "description": "Variante pour la maison du tirage de supination avec un élastique au lieu d'une poulie.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Fixez un élastique à hauteur d'avant-bras",
          "Tournez l'avant-bras en supination contre l'élastique",
          "Contrôlez le retour"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 répétitions",
        "tips": [
          "Aucune poulie nécessaire — parfait pour s'entraîner à la maison",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Tirones de Supinación con Banda",
        "description": "Variante casera del tirón de supinación usando una banda de resistencia en lugar de polea.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fija una banda de resistencia a la altura del antebrazo",
          "Rota el antebrazo hacia la supinación contra la banda",
          "Controla el retorno"
        ],
        "sets": "3-4 series",
        "reps": "12-15 repeticiones",
        "tips": [
          "No necesitas polea — ideal para entrenar en casa",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Supinação com Banda",
        "description": "Variante caseira da puxada de supinação usando uma banda elástica em vez de cabo.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fixa uma banda elástica à altura do antebraço",
          "Roda o antebraço para supinação contra a banda",
          "Controla o regresso"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 reps",
        "tips": [
          "Sem necessidade de máquina de cabos — ótimo para treino em casa",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "バンドスピネーションプル",
        "description": "ケーブルの代わりにレジスタンスバンドを使う自宅向けのスピネーションプルです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "前腕の高さにレジスタンスバンドを固定する",
          "バンドに抵抗しながら前腕を回外方向へ回す",
          "戻りを制御する"
        ],
        "sets": "3〜4セット",
        "reps": "12〜15レップ",
        "tips": [
          "ケーブルマシン不要－自宅トレーニングに最適",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "밴드 회외 풀",
        "description": "케이블 대신 저항 밴드를 사용하는 가정용 회외 풀 변형입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "전완 높이에 저항 밴드를 고정하세요",
          "밴드에 저항하며 전완을 회외 방향으로 회전시키세요",
          "복귀를 통제하세요"
        ],
        "sets": "3-4세트",
        "reps": "12-15회",
        "tips": [
          "케이블 머신이 필요 없습니다 - 홈트레이닝에 좋습니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "bandHandleSupinationCurls",
    "slug": "band-handle-supination-curls",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Supination Curls with Resistance Band Handle",
        "description": "Supination curl using a dedicated handle attachment on a resistance band.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Grip the handle at one end of the band",
          "Curl and rotate into supination together",
          "Reset slowly under tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Grip near the end of the handle for a longer lever",
          "Keep a slow, controlled tempo throughout"
        ]
      },
      "ru": {
        "name": "Супинация с рукоятью на резинке",
        "description": "Сгибание на супинацию с использованием специальной рукояти на резиновой ленте.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Возьмитесь за рукоять на одном конце резинки",
          "Сгибайте и вращайте в супинацию одновременно",
          "Медленно возвращайтесь под напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Держитесь ближе к концу рукояти для более длинного рычага",
          "Сохраняйте медленный контролируемый темп на протяжении"
        ]
      },
      "de": {
        "name": "Supinations-Curls mit Bandgriff",
        "description": "Supinations-Curl mit einem speziellen Griffaufsatz an einem Widerstandsband.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Greife den Griff an einem Ende des Bands",
          "Curle und drehe gleichzeitig in Supination",
          "Kehre langsam unter Spannung zurück"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Greife nah am Ende des Griffs für einen längeren Hebel",
          "Halte durchgehend ein langsames, kontrolliertes Tempo"
        ]
      },
      "fr": {
        "name": "Curls de supination avec poignée élastique",
        "description": "Curl de supination avec une poignée spéciale sur un élastique.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Saisissez la poignée à une extrémité de l'élastique",
          "Enroulez et tournez en supination en même temps",
          "Revenez lentement sous tension"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Tenez près de l'extrémité de la poignée pour un levier plus long",
          "Gardez un tempo lent et contrôlé tout du long"
        ]
      },
      "es": {
        "name": "Curl de Supinación con Asa en Banda",
        "description": "Curl de supinación usando un asa especial en una banda de resistencia.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Agarra el asa en un extremo de la banda",
          "Curva y rota hacia la supinación al mismo tiempo",
          "Vuelve lentamente bajo tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Agarra cerca del extremo del asa para una palanca más larga",
          "Mantén un tempo lento y controlado en todo momento"
        ]
      },
      "pt-BR": {
        "name": "Rosca de Supinação com Pega em Banda",
        "description": "Rosca de supinação usando uma pega especial numa banda elástica.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Agarra a pega numa extremidade da banda",
          "Enrola e roda para supinação ao mesmo tempo",
          "Regressa lentamente sob tensão"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Agarra perto da extremidade da pega para uma alavanca mais longa",
          "Mantém um ritmo lento e controlado do início ao fim"
        ]
      },
      "ja": {
        "name": "バンドハンドルスピネーションカール",
        "description": "レジスタンスバンドの専用ハンドルを使ったスピネーションカールです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "バンドの片端のハンドルを握る",
          "巻き込みと回外を同時に行う",
          "緊張下でゆっくり戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "レバーを長くするためハンドルの端寄りを握る",
          "常にゆっくりと制御されたテンポを保つ"
        ]
      },
      "ko": {
        "name": "밴드 핸들 회외 컬",
        "description": "저항 밴드의 전용 핸들을 사용하는 회외 컬입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "밴드 한쪽 끝의 핸들을 잡으세요",
          "감기와 회외를 동시에 하세요",
          "긴장 상태로 천천히 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "더 긴 지렛대를 위해 핸들 끝 쪽을 잡으세요",
          "내내 느리고 통제된 템포를 유지하세요"
        ]
      }
    }
  },
  {
    "id": "staticSupinationHold110",
    "slug": "static-supination-hold-110",
    "emoji": "↩️",
    "category": "static",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Static Supination Hold — 110°+",
        "description": "Isometric supination hold at a wide 110°+ angle.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Set the cable at a wide 110°+ angle",
          "Rotate into full supination and hold",
          "Keep tension steady for the full hold"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "The wider angle trains supination closer to a finishing position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание супинации — 110°+",
        "description": "Изометрическое удержание супинации под широким углом 110°+.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Установите блок под широким углом 110°+",
          "Вращайте в полную супинацию и удерживайте",
          "Сохраняйте постоянное напряжение на протяжении удержания"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Более широкий угол тренирует супинацию ближе к завершающей позиции",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Supinations-Halten — 110°+",
        "description": "Isometrisches Supinations-Halten im weiten Winkel von 110°+.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Stelle den Kabelzug auf einen weiten Winkel von 110°+ ein",
          "Drehe in volle Supination und halte",
          "Halte die Spannung während des gesamten Haltens konstant"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Der weitere Winkel trainiert Supination näher an der Abschlussposition",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien en supination — 110°+",
        "description": "Maintien isométrique en supination à un angle large de 110°+.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Réglez la poulie à un angle large de 110°+",
          "Tournez en supination complète et maintenez",
          "Gardez une tension constante pendant tout le maintien"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "L'angle plus large entraîne la supination plus près de la position de finition",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Supinación — 110°+",
        "description": "Sostén isométrico de supinación en ángulo amplio de 110°+.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta la polea a un ángulo amplio de 110°+",
          "Rota hasta supinación completa y sostén",
          "Mantén tensión constante durante todo el sostén"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "El ángulo más amplio entrena la supinación cerca de la posición de remate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Supinação — 110°+",
        "description": "Isometria de supinação num ângulo amplo de 110°+.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta o cabo para um ângulo amplo de 110°+",
          "Roda até supinação completa e mantém",
          "Mantém a tensão constante durante toda a isometria"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "O ângulo mais amplo treina a supinação mais perto da posição de finalização",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "スピネーションホールド－110°+",
        "description": "110°+の広い角度でのアイソメトリックスピネーションホールドです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "ケーブルを110°+の広い角度にセットする",
          "完全な回外まで回して保持する",
          "保持中は緊張を一定に保つ"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "広い角度はフィニッシュ姿勢に近いスピネーションを鍛える",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "회외 홀드 — 110°+",
        "description": "110°+의 넓은 각도의 등척성 회외 홀드입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "케이블을 110°+의 넓은 각도로 설정하세요",
          "완전한 회외까지 회전하고 유지하세요",
          "홀드 내내 긴장을 일정하게 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "더 넓은 각도는 마무리 자세에 가까운 회외를 훈련합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticSupinationHold45",
    "slug": "static-supination-hold-45",
    "emoji": "↩️",
    "category": "static",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Static Supination Hold — 45°",
        "description": "Isometric supination hold at a 45° table-style angle.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Set the cable at a 45° angle",
          "Rotate into full supination and hold",
          "Keep tension steady for the full hold"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание супинации — 45°",
        "description": "Изометрическое удержание супинации под углом 45°, имитирующим позицию стола.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Установите блок под углом 45°",
          "Вращайте в полную супинацию и удерживайте",
          "Сохраняйте постоянное напряжение на протяжении удержания"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Supinations-Halten — 45°",
        "description": "Isometrisches Supinations-Halten im 45°-Winkel, der die Tischposition nachahmt.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Stelle den Kabelzug auf einen 45°-Winkel ein",
          "Drehe in volle Supination und halte",
          "Halte die Spannung während des gesamten Haltens konstant"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien en supination — 45°",
        "description": "Maintien isométrique en supination à un angle de 45°, imitant la position de table.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Réglez la poulie à un angle de 45°",
          "Tournez en supination complète et maintenez",
          "Gardez une tension constante pendant tout le maintien"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Supinación — 45°",
        "description": "Sostén isométrico de supinación en ángulo de 45°, que imita la posición de mesa.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta la polea a un ángulo de 45°",
          "Rota hasta supinación completa y sostén",
          "Mantén tensión constante durante todo el sostén"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Supinação — 45°",
        "description": "Isometria de supinação num ângulo de 45°, que imita a posição de mesa.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Ajusta o cabo para um ângulo de 45°",
          "Roda até supinação completa e mantém",
          "Mantém a tensão constante durante toda a isometria"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "スピネーションホールド－45°",
        "description": "テーブルの姿勢を模した45°の角度でのアイソメトリックスピネーションホールドです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "ケーブルを45°の角度にセットする",
          "完全な回外まで回して保持する",
          "保持中は緊張を一定に保つ"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "회외 홀드 — 45°",
        "description": "테이블 자세를 모방한 45° 각도의 등척성 회외 홀드입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "케이블을 45° 각도로 설정하세요",
          "완전한 회외까지 회전하고 유지하세요",
          "홀드 내내 긴장을 일정하게 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticSupinationHold",
    "slug": "static-supination-hold",
    "emoji": "↩️",
    "category": "static",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Static Supination Hold",
        "description": "Isometric supination hold for raw hook and cupping strength.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Attach a handle to a cable or band",
          "Rotate into full supination",
          "Hold with maximum tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Breathe steadily — don't hold your breath through the hold",
          "Combine with a slight wrist curl for maximum cup"
        ]
      },
      "ru": {
        "name": "Удержание супинации",
        "description": "Изометрическое удержание супинации для развития силы крюка и чашки.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Закрепите рукоять на блоке или резинке",
          "Вращайте в полную супинацию",
          "Удерживайте с максимальным напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Дышите ровно — не задерживайте дыхание во время удержания",
          "Добавьте лёгкое сгибание кисти для максимальной чашки"
        ]
      },
      "de": {
        "name": "Supinations-Halten",
        "description": "Isometrisches Supinations-Halten zum Aufbau von Hook- und Cupping-Kraft.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Befestige einen Griff an Kabelzug oder Band",
          "Drehe in volle Supination",
          "Halte mit maximaler Spannung"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an",
          "Kombiniere mit leichter Handgelenkbeugung für maximalen Cup"
        ]
      },
      "fr": {
        "name": "Maintien en supination",
        "description": "Maintien isométrique en supination pour développer la force du hook et du cupping.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Fixez une poignée à une poulie ou un élastique",
          "Tournez en supination complète",
          "Maintenez avec une tension maximale"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien",
          "Ajoutez une légère flexion du poignet pour un cupping maximal"
        ]
      },
      "es": {
        "name": "Sostén de Supinación",
        "description": "Sostén isométrico de supinación para desarrollar fuerza de gancho y ahuecado.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fija un asa a una polea o banda",
          "Rota hasta supinación completa",
          "Sostén con máxima tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "Respira de forma constante — no aguantes la respiración durante el sostén",
          "Añade una ligera flexión de muñeca para máximo ahuecado"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Supinação",
        "description": "Isometria de supinação para força bruta de hook e cupping.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Fixa uma pega num cabo ou banda",
          "Roda até supinação completa",
          "Mantém com tensão máxima"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Respira de forma constante — não prendas a respiração durante a isometria",
          "Adiciona uma ligeira flexão de punho para cup máximo"
        ]
      },
      "ja": {
        "name": "スピネーションホールド",
        "description": "フックとカッピングの力を鍛えるアイソメトリックスピネーションホールドです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "ケーブルかバンドにハンドルを取り付ける",
          "完全な回外まで回す",
          "最大限の緊張で保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "呼吸を一定に保つ－保持中に息を止めない",
          "最大限のカップのため軽く手首を巻き込む"
        ]
      },
      "ko": {
        "name": "회외 홀드",
        "description": "훅과 커핑 힘을 기르기 위한 등척성 회외 홀드입니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "케이블이나 밴드에 손잡이를 연결하세요",
          "완전한 회외까지 회전하세요",
          "최대 긴장으로 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요",
          "최대 컵을 위해 손목을 살짝 더 감으세요"
        ]
      }
    }
  },
  {
    "id": "tableSupinationLifts",
    "slug": "table-supination-lifts",
    "emoji": "↩️",
    "category": "dynamic",
    "equipmentKeys": [
      "table",
      "dumbbell"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Table Supination Lifts with Weight",
        "description": "Supination lift performed with a dumbbell resting on the table, close to a live match motion.",
        "muscles": "Supinator, biceps",
        "instructions": [
          "Rest your elbow on the table, holding a dumbbell",
          "Rotate your forearm from pronated to supinated",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Keep the elbow anchored on the table throughout",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Супинация на столе с весом",
        "description": "Подъём на супинацию с гантелью, лежащей на столе, максимально близко к реальному движению в поединке.",
        "muscles": "Супинатор, бицепс",
        "instructions": [
          "Положите локоть на стол, держа гантель",
          "Вращайте предплечье из пронации в супинацию",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Держите локоть зафиксированным на столе на протяжении",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Supinations-Hebungen am Tisch mit Gewicht",
        "description": "Supinations-Hebung mit einer auf dem Tisch abgestützten Kurzhantel, nah an einer echten Match-Bewegung.",
        "muscles": "Supinator, Bizeps",
        "instructions": [
          "Stütze den Ellbogen auf dem Tisch ab und halte eine Kurzhantel",
          "Drehe den Unterarm von proniert zu supiniert",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Halte den Ellbogen durchgehend auf dem Tisch fixiert",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Levées de supination à table avec poids",
        "description": "Levée de supination avec un haltère posé sur la table, très proche d'un mouvement réel en match.",
        "muscles": "Supinateur, biceps",
        "instructions": [
          "Posez le coude sur la table en tenant un haltère",
          "Tournez l'avant-bras de pronation vers supination",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Gardez le coude ancré sur la table tout du long",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Elevaciones de Supinación en Mesa con Peso",
        "description": "Elevación de supinación con una mancuerna apoyada en la mesa, muy cercana a un movimiento real de combate.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Apoya el codo en la mesa sujetando una mancuerna",
          "Rota el antebrazo de pronado a supinado",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Mantén el codo anclado en la mesa en todo momento",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Elevações de Supinação na Mesa com Peso",
        "description": "Elevação de supinação com um halter apoiado na mesa, muito próxima de um movimento real de combate.",
        "muscles": "Supinador, bíceps",
        "instructions": [
          "Apoia o cotovelo na mesa segurando um halter",
          "Roda o antebraço de pronado para supinado",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Mantém o cotovelo ancorado na mesa do início ao fim",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "テーブルスピネーションリフト（ウェイト付き）",
        "description": "テーブルに乗せたダンベルを使うスピネーションリフトで、実際の試合動作に非常に近いエクササイズです。",
        "muscles": "回外筋、上腕二頭筋",
        "instructions": [
          "肘をテーブルに乗せダンベルを持つ",
          "前腕を回内から回外へ回す",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "常に肘をテーブルに固定しておく",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "테이블 회외 리프트 (웨이트 포함)",
        "description": "테이블에 놓인 덤벨을 사용하는 회외 리프트로, 실제 시합 동작에 매우 가깝습니다.",
        "muscles": "회외근, 이두근",
        "instructions": [
          "팔꿈치를 테이블에 대고 덤벨을 잡으세요",
          "전완을 회내에서 회외로 회전시키세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "내내 팔꿈치를 테이블에 고정하세요",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "staticBarWristCurlsSidePressure",
    "slug": "static-bar-wrist-curls-side-pressure",
    "emoji": "➡️",
    "category": "static",
    "equipmentKeys": [
      "barbell",
      "table"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Fixed-Bar Wrist Curls with Side Pressure",
        "description": "Isometric wrist curl against a fixed bar while applying side pressure, training the wrist to hold its angle under lateral load.",
        "muscles": "Wrist flexors, biceps, brachioradialis",
        "instructions": [
          "Grip a fixed (non-moving) bar at table height",
          "Curl the wrist while pressing sideways into the bar",
          "Hold the position without letting the wrist collapse"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "The bar shouldn't move — this is about holding your angle under load",
          "Never let your wrist angle collapse"
        ]
      },
      "ru": {
        "name": "Сгибания запястий у неподвижного грифа с боковым давлением",
        "description": "Изометрическое сгибание кисти у неподвижного грифа с боковым давлением, тренирующее удержание угла кисти под боковой нагрузкой.",
        "muscles": "Сгибатели кисти, бицепс, плечелучевая мышца",
        "instructions": [
          "Возьмитесь за неподвижный (не двигающийся) гриф на высоте стола",
          "Сгибайте кисть, одновременно давя вбок на гриф",
          "Удерживайте позицию, не позволяя кисти провалиться"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "Гриф не должен двигаться — суть в удержании угла под нагрузкой",
          "Никогда не позволяйте углу запястья проваливаться"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls an fixierter Stange mit Seitendruck",
        "description": "Isometrischer Handgelenk-Curl an einer fixierten Stange mit Seitendruck, trainiert das Halten des Handgelenkwinkels unter seitlicher Belastung.",
        "muscles": "Handgelenkbeuger, Bizeps, Brachioradialis",
        "instructions": [
          "Greife eine fixierte (nicht bewegliche) Stange auf Tischhöhe",
          "Curle das Handgelenk, während du seitlich gegen die Stange drückst",
          "Halte die Position, ohne dass das Handgelenk nachgibt"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "Die Stange darf sich nicht bewegen – es geht ums Halten des Winkels unter Last",
          "Lass deinen Handgelenkwinkel niemals zusammenbrechen"
        ]
      },
      "fr": {
        "name": "Flexions du poignet sur barre fixe avec pression latérale",
        "description": "Flexion isométrique du poignet contre une barre fixe avec pression latérale, entraîne le maintien de l'angle du poignet sous charge latérale.",
        "muscles": "Fléchisseurs du poignet, biceps, brachioradial",
        "instructions": [
          "Saisissez une barre fixe (qui ne bouge pas) à hauteur de table",
          "Enroulez le poignet tout en poussant latéralement contre la barre",
          "Maintenez la position sans laisser le poignet s'effondrer"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "La barre ne doit pas bouger — il s'agit de maintenir l'angle sous charge",
          "Ne laissez jamais l'angle de votre poignet s'effondrer"
        ]
      },
      "es": {
        "name": "Curl de Muñeca en Barra Fija con Presión Lateral",
        "description": "Curl de muñeca isométrico contra una barra fija con presión lateral, entrena mantener el ángulo de muñeca bajo carga lateral.",
        "muscles": "Flexores de muñeca, bíceps, braquiorradial",
        "instructions": [
          "Agarra una barra fija (que no se mueve) a la altura de la mesa",
          "Curva la muñeca mientras presionas lateralmente contra la barra",
          "Sostén la posición sin dejar que la muñeca colapse"
        ],
        "sets": "3-4 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "La barra no debe moverse — se trata de mantener el ángulo bajo carga",
          "Nunca dejes que el ángulo de tu muñeca colapse"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho em Barra Fixa com Pressão Lateral",
        "description": "Flexão de punho isométrica contra uma barra fixa com pressão lateral, treina manter o ângulo do punho sob carga lateral.",
        "muscles": "Flexores do punho, bíceps, braquiorradial",
        "instructions": [
          "Agarra uma barra fixa (que não se move) à altura da mesa",
          "Enrola o punho enquanto pressionas lateralmente contra a barra",
          "Mantém a posição sem deixar o punho colapsar"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "A barra não se deve mover — trata-se de manter o ângulo sob carga",
          "Nunca deixes o ângulo do teu punho colapsar"
        ]
      },
      "ja": {
        "name": "固定バーでのサイドプレッシャーリストカール",
        "description": "固定バーに対してサイドプレッシャーをかけながら行うアイソメトリックリストカールで、横方向の負荷下で手首の角度を維持する力を鍛えます。",
        "muscles": "手首屈筋群、上腕二頭筋、腕橈骨筋",
        "instructions": [
          "テーブルの高さで固定された（動かない）バーを握る",
          "バーに横方向に押しながら手首を巻き込む",
          "手首を崩さずに姿勢を保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15秒保持",
        "tips": [
          "バーは動いてはいけない－負荷下で角度を保持することが重要",
          "手首の角度を決して崩さない"
        ]
      },
      "ko": {
        "name": "고정 바 사이드 프레셔 리스트 컬",
        "description": "고정 바에 사이드 프레셔를 가하며 하는 등척성 리스트 컬로, 측면 부하 아래 손목 각도를 유지하는 능력을 훈련합니다.",
        "muscles": "손목 굴근, 이두근, 상완요골근",
        "instructions": [
          "테이블 높이에서 고정된(움직이지 않는) 바를 잡으세요",
          "바에 옆으로 힘을 가하며 손목을 감으세요",
          "손목이 무너지지 않게 자세를 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15초 유지",
        "tips": [
          "바가 움직여서는 안 됩니다 - 부하 아래 각도를 유지하는 것이 핵심입니다",
          "손목 각도가 절대 무너지지 않게 하세요"
        ]
      }
    }
  },
  {
    "id": "pronatedSidePressure",
    "slug": "pronated-side-pressure",
    "emoji": "➡️",
    "category": "dynamic",
    "equipmentKeys": [
      "cable",
      "band"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Side Pressure — Pronated Grip",
        "description": "Side pressure drill performed with a pronated (palm-down) grip.",
        "muscles": "Biceps, shoulder, brachioradialis",
        "instructions": [
          "Set cable or band at table height",
          "Press sideways with a pronated grip, mimicking pin movement",
          "Maintain wrist angle throughout"
        ],
        "sets": "4 sets",
        "reps": "8-10 reps",
        "tips": [
          "Set resistance at arm wrestling table height",
          "Use body weight to assist the press"
        ]
      },
      "ru": {
        "name": "Боковое давление — пронированный хват",
        "description": "Упражнение на боковое давление, выполняемое пронированным (ладонью вниз) хватом.",
        "muscles": "Бицепс, плечо, плечелучевая мышца",
        "instructions": [
          "Установите блок или резинку на высоте стола",
          "Давите вбок пронированным хватом, имитируя движение укладки",
          "Сохраняйте угол запястья на протяжении всего движения"
        ],
        "sets": "4 подхода",
        "reps": "8-10 повторений",
        "tips": [
          "Установите сопротивление на высоте стола для армрестлинга",
          "Используйте вес тела для усиления давления"
        ]
      },
      "de": {
        "name": "Seitendruck — pronierter Griff",
        "description": "Seitendruck-Übung mit proniertem (Handfläche nach unten) Griff.",
        "muscles": "Bizeps, Schulter, Brachioradialis",
        "instructions": [
          "Stelle Kabelzug oder Band auf Tischhöhe ein",
          "Drücke seitlich mit proniertem Griff, ahme die Pin-Bewegung nach",
          "Halte den Handgelenkwinkel durchgehend"
        ],
        "sets": "4 Sätze",
        "reps": "8–10 Wdh.",
        "tips": [
          "Stelle den Widerstand auf Armdrück-Tischhöhe ein",
          "Nutze dein Körpergewicht, um den Druck zu unterstützen"
        ]
      },
      "fr": {
        "name": "Pression latérale — prise pronée",
        "description": "Exercice de pression latérale effectué avec une prise pronée (paume vers le bas).",
        "muscles": "Biceps, épaule, brachioradial",
        "instructions": [
          "Réglez la poulie ou l'élastique à hauteur de table",
          "Poussez latéralement avec une prise pronée, en imitant le mouvement de pin",
          "Maintenez l'angle du poignet tout du long"
        ],
        "sets": "4 séries",
        "reps": "8-10 répétitions",
        "tips": [
          "Réglez la résistance à hauteur de la table de bras de fer",
          "Utilisez le poids du corps pour aider la poussée"
        ]
      },
      "es": {
        "name": "Presión Lateral — Agarre Pronado",
        "description": "Ejercicio de presión lateral realizado con agarre pronado (palma hacia abajo).",
        "muscles": "Bíceps, hombro, braquiorradial",
        "instructions": [
          "Coloca la polea o banda a la altura de la mesa",
          "Presiona lateralmente con agarre pronado, imitando el movimiento de derribo",
          "Mantén el ángulo de la muñeca en todo momento"
        ],
        "sets": "4 series",
        "reps": "8-10 repeticiones",
        "tips": [
          "Coloca la resistencia a la altura de la mesa de pulso",
          "Usa el peso corporal para asistir la presión"
        ]
      },
      "pt-BR": {
        "name": "Pressão Lateral — Pega Pronada",
        "description": "Exercício de pressão lateral realizado com pega pronada (palma para baixo).",
        "muscles": "Bíceps, ombro, braquiorradial",
        "instructions": [
          "Coloca o cabo ou a banda à altura da mesa",
          "Pressiona para o lado com pega pronada, imitando o movimento de pin",
          "Mantém o ângulo do punho durante todo o movimento"
        ],
        "sets": "4 séries",
        "reps": "8-10 reps",
        "tips": [
          "Coloca a resistência à altura da mesa de queda de braço",
          "Usa o peso do corpo para ajudar na pressão"
        ]
      },
      "ja": {
        "name": "サイドプレッシャー－プロネートグリップ",
        "description": "プロネート（手のひら下）グリップで行うサイドプレッシャーエクササイズです。",
        "muscles": "上腕二頭筋、肩、腕橈骨筋",
        "instructions": [
          "ケーブルかバンドをテーブルの高さにセットする",
          "プロネートグリップでピン動作を模して横方向にプレスする",
          "手首の角度を常に維持する"
        ],
        "sets": "4セット",
        "reps": "8〜10レップ",
        "tips": [
          "抵抗をアームレスリングテーブルの高さにセットする",
          "体重を使ってプレスを補助する"
        ]
      },
      "ko": {
        "name": "사이드 프레셔 — 회내 그립",
        "description": "회내(손바닥 아래) 그립으로 하는 사이드 프레셔 운동입니다.",
        "muscles": "이두근, 어깨, 상완요골근",
        "instructions": [
          "케이블이나 밴드를 테이블 높이에 설정하세요",
          "회내 그립으로 핀 동작을 모방하여 옆으로 프레스하세요",
          "손목 각도를 내내 유지하세요"
        ],
        "sets": "4세트",
        "reps": "8-10회",
        "tips": [
          "저항을 팔씨름 테이블 높이에 설정하세요",
          "체중을 이용해 프레스를 도우세요"
        ]
      }
    }
  },
  {
    "id": "supinatedSidePressure",
    "slug": "supinated-side-pressure",
    "emoji": "➡️",
    "category": "dynamic",
    "equipmentKeys": [
      "cable",
      "band"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Side Pressure — Supinated Grip",
        "description": "Side pressure drill performed with a supinated (palm-up) grip.",
        "muscles": "Biceps, shoulder, brachioradialis",
        "instructions": [
          "Set cable or band at table height",
          "Press sideways with a supinated grip, mimicking pin movement",
          "Maintain wrist angle throughout"
        ],
        "sets": "4 sets",
        "reps": "8-10 reps",
        "tips": [
          "Set resistance at arm wrestling table height",
          "Use body weight to assist the press"
        ]
      },
      "ru": {
        "name": "Боковое давление — супинированный хват",
        "description": "Упражнение на боковое давление, выполняемое супинированным (ладонью вверх) хватом.",
        "muscles": "Бицепс, плечо, плечелучевая мышца",
        "instructions": [
          "Установите блок или резинку на высоте стола",
          "Давите вбок супинированным хватом, имитируя движение укладки",
          "Сохраняйте угол запястья на протяжении всего движения"
        ],
        "sets": "4 подхода",
        "reps": "8-10 повторений",
        "tips": [
          "Установите сопротивление на высоте стола для армрестлинга",
          "Используйте вес тела для усиления давления"
        ]
      },
      "de": {
        "name": "Seitendruck — supinierter Griff",
        "description": "Seitendruck-Übung mit supiniertem (Handfläche nach oben) Griff.",
        "muscles": "Bizeps, Schulter, Brachioradialis",
        "instructions": [
          "Stelle Kabelzug oder Band auf Tischhöhe ein",
          "Drücke seitlich mit supiniertem Griff, ahme die Pin-Bewegung nach",
          "Halte den Handgelenkwinkel durchgehend"
        ],
        "sets": "4 Sätze",
        "reps": "8–10 Wdh.",
        "tips": [
          "Stelle den Widerstand auf Armdrück-Tischhöhe ein",
          "Nutze dein Körpergewicht, um den Druck zu unterstützen"
        ]
      },
      "fr": {
        "name": "Pression latérale — prise supinée",
        "description": "Exercice de pression latérale effectué avec une prise supinée (paume vers le haut).",
        "muscles": "Biceps, épaule, brachioradial",
        "instructions": [
          "Réglez la poulie ou l'élastique à hauteur de table",
          "Poussez latéralement avec une prise supinée, en imitant le mouvement de pin",
          "Maintenez l'angle du poignet tout du long"
        ],
        "sets": "4 séries",
        "reps": "8-10 répétitions",
        "tips": [
          "Réglez la résistance à hauteur de la table de bras de fer",
          "Utilisez le poids du corps pour aider la poussée"
        ]
      },
      "es": {
        "name": "Presión Lateral — Agarre Supinado",
        "description": "Ejercicio de presión lateral realizado con agarre supinado (palma hacia arriba).",
        "muscles": "Bíceps, hombro, braquiorradial",
        "instructions": [
          "Coloca la polea o banda a la altura de la mesa",
          "Presiona lateralmente con agarre supinado, imitando el movimiento de derribo",
          "Mantén el ángulo de la muñeca en todo momento"
        ],
        "sets": "4 series",
        "reps": "8-10 repeticiones",
        "tips": [
          "Coloca la resistencia a la altura de la mesa de pulso",
          "Usa el peso corporal para asistir la presión"
        ]
      },
      "pt-BR": {
        "name": "Pressão Lateral — Pega Supinada",
        "description": "Exercício de pressão lateral realizado com pega supinada (palma para cima).",
        "muscles": "Bíceps, ombro, braquiorradial",
        "instructions": [
          "Coloca o cabo ou a banda à altura da mesa",
          "Pressiona para o lado com pega supinada, imitando o movimento de pin",
          "Mantém o ângulo do punho durante todo o movimento"
        ],
        "sets": "4 séries",
        "reps": "8-10 reps",
        "tips": [
          "Coloca a resistência à altura da mesa de queda de braço",
          "Usa o peso do corpo para ajudar na pressão"
        ]
      },
      "ja": {
        "name": "サイドプレッシャー－スピネートグリップ",
        "description": "スピネート（手のひら上）グリップで行うサイドプレッシャーエクササイズです。",
        "muscles": "上腕二頭筋、肩、腕橈骨筋",
        "instructions": [
          "ケーブルかバンドをテーブルの高さにセットする",
          "スピネートグリップでピン動作を模して横方向にプレスする",
          "手首の角度を常に維持する"
        ],
        "sets": "4セット",
        "reps": "8〜10レップ",
        "tips": [
          "抵抗をアームレスリングテーブルの高さにセットする",
          "体重を使ってプレスを補助する"
        ]
      },
      "ko": {
        "name": "사이드 프레셔 — 회외 그립",
        "description": "회외(손바닥 위) 그립으로 하는 사이드 프레셔 운동입니다.",
        "muscles": "이두근, 어깨, 상완요골근",
        "instructions": [
          "케이블이나 밴드를 테이블 높이에 설정하세요",
          "회외 그립으로 핀 동작을 모방하여 옆으로 프레스하세요",
          "손목 각도를 내내 유지하세요"
        ],
        "sets": "4세트",
        "reps": "8-10회",
        "tips": [
          "저항을 팔씨름 테이블 높이에 설정하세요",
          "체중을 이용해 프레스를 도우세요"
        ]
      }
    }
  },
  {
    "id": "wristCurlsWithSidePressure",
    "slug": "wrist-curls-with-side-pressure",
    "emoji": "➡️",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "table"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure",
      "hook"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Wrist Curls with Side Pressure",
        "description": "Wrist curl blended with a lateral pressing motion to combine cupping and side-pressure strength in one drill.",
        "muscles": "Wrist flexors, biceps",
        "instructions": [
          "Hold a dumbbell with forearm on the table",
          "Curl the wrist while pressing slightly sideways",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-12 reps",
        "tips": [
          "This blends cupping and side pressure into one rep",
          "Control the negative (lowering) portion slowly"
        ]
      },
      "ru": {
        "name": "Сгибания запястий с боковым давлением",
        "description": "Сгибание кисти в сочетании с боковым давящим движением, объединяющее силу чашки и бокового давления в одном упражнении.",
        "muscles": "Сгибатели кисти, бицепс",
        "instructions": [
          "Держите гантель, положив предплечье на стол",
          "Сгибайте кисть, одновременно слегка давя вбок",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-12 повторений",
        "tips": [
          "Это упражнение объединяет чашку и боковое давление в одном повторении",
          "Контролируйте негативную фазу (опускание)"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls mit Seitendruck",
        "description": "Handgelenk-Curl kombiniert mit seitlicher Druckbewegung, vereint Cupping- und Seitendruckkraft in einer Übung.",
        "muscles": "Handgelenkbeuger, Bizeps",
        "instructions": [
          "Halte eine Kurzhantel mit dem Unterarm auf dem Tisch",
          "Curle das Handgelenk, während du leicht seitlich drückst",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–12 Wdh.",
        "tips": [
          "Diese Übung vereint Cupping und Seitendruck in einer Wiederholung",
          "Kontrolliere die negative Phase (Absenken) langsam"
        ]
      },
      "fr": {
        "name": "Flexions du poignet avec pression latérale",
        "description": "Flexion du poignet combinée à un mouvement de poussée latérale, unissant la force de cupping et de pression latérale en un seul exercice.",
        "muscles": "Fléchisseurs du poignet, biceps",
        "instructions": [
          "Tenez un haltère avec l'avant-bras sur la table",
          "Enroulez le poignet tout en poussant légèrement sur le côté",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-12 répétitions",
        "tips": [
          "Cet exercice unit le cupping et la pression latérale en une seule répétition",
          "Contrôlez lentement la phase négative (descente)"
        ]
      },
      "es": {
        "name": "Curl de Muñeca con Presión Lateral",
        "description": "Curl de muñeca combinado con un movimiento de presión lateral, uniendo fuerza de ahuecado y presión lateral en un solo ejercicio.",
        "muscles": "Flexores de muñeca, bíceps",
        "instructions": [
          "Sujeta una mancuerna con el antebrazo apoyado en la mesa",
          "Curva la muñeca mientras presionas ligeramente hacia el lado",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "10-12 repeticiones",
        "tips": [
          "Este ejercicio une ahuecado y presión lateral en una sola repetición",
          "Controla la fase negativa (bajada) lentamente"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho com Pressão Lateral",
        "description": "Flexão de punho combinada com um movimento de pressão lateral, unindo força de cupping e pressão lateral num só exercício.",
        "muscles": "Flexores do punho, bíceps",
        "instructions": [
          "Segura um halter com o antebraço na mesa",
          "Enrola o punho enquanto pressionas ligeiramente para o lado",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-12 reps",
        "tips": [
          "Este exercício une cupping e pressão lateral numa só rep",
          "Controla a fase negativa (descida) lentamente"
        ]
      },
      "ja": {
        "name": "サイドプレッシャー付きリストカール",
        "description": "横方向のプレス動作を組み合わせたリストカールで、カッピングとサイドプレッシャーの力を1つのエクササイズで鍛えます。",
        "muscles": "手首屈筋群、上腕二頭筋",
        "instructions": [
          "前腕をテーブルに乗せダンベルを持つ",
          "軽く横方向に押しながら手首を巻き込む",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜12レップ",
        "tips": [
          "このエクササイズはカッピングとサイドプレッシャーを1レップに組み合わせる",
          "ネガティブ（下ろす）動作をゆっくり制御する"
        ]
      },
      "ko": {
        "name": "사이드 프레셔 리스트 컬",
        "description": "측면 프레스 동작을 결합한 리스트 컬로, 커핑과 사이드 프레셔 힘을 하나의 운동으로 훈련합니다.",
        "muscles": "손목 굴근, 이두근",
        "instructions": [
          "전완을 테이블에 대고 덤벨을 잡으세요",
          "약간 옆으로 밀며 손목을 감으세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-12회",
        "tips": [
          "이 운동은 커핑과 사이드 프레셔를 한 번의 반복에 결합합니다",
          "네거티브(내리는) 구간을 천천히 통제하세요"
        ]
      }
    }
  },
  {
    "id": "hookPulls45",
    "slug": "hook-pulls-45",
    "emoji": "🥊",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Hook Pulls — 45°",
        "description": "Hook-specific cable pull at a 45° angle, curling and pulling toward the body as in a live hook.",
        "muscles": "Biceps, wrist flexors",
        "instructions": [
          "Set the cable at a 45° angle at table height",
          "Curl the wrist and pull toward your body",
          "Return with control, maintaining the cup"
        ],
        "sets": "3-4 sets",
        "reps": "8-10 reps",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Pull toward yourself, not just sideways"
        ]
      },
      "ru": {
        "name": "Тяга крюка — 45°",
        "description": "Специфичная для крюка тяга на блоке под углом 45°, со сгибанием кисти и тягой к себе, как в реальном крюке.",
        "muscles": "Бицепс, сгибатели кисти",
        "instructions": [
          "Установите блок под углом 45° на высоте стола",
          "Сгибайте кисть и тяните к себе",
          "Возвращайтесь с контролем, сохраняя чашку"
        ],
        "sets": "3-4 подхода",
        "reps": "8-10 повторений",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Тяните на себя, не только вбок"
        ]
      },
      "de": {
        "name": "Hook-Züge — 45°",
        "description": "Hook-spezifischer Kabelzug im 45°-Winkel, mit Handgelenk-Curl und Zug zum Körper wie bei einem echten Hook.",
        "muscles": "Bizeps, Handgelenkbeuger",
        "instructions": [
          "Stelle den Kabelzug im 45°-Winkel auf Tischhöhe ein",
          "Curle das Handgelenk und ziehe zu dir",
          "Kehre kontrolliert zurück, behalte den Cup bei"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–10 Wdh.",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Ziehe zu dir, nicht nur seitlich"
        ]
      },
      "fr": {
        "name": "Tirages de hook — 45°",
        "description": "Tirage à la poulie spécifique au hook à un angle de 45°, avec flexion du poignet et traction vers le corps comme dans un vrai hook.",
        "muscles": "Biceps, fléchisseurs du poignet",
        "instructions": [
          "Réglez la poulie à un angle de 45° à hauteur de table",
          "Enroulez le poignet et tirez vers vous",
          "Revenez en contrôlant, en maintenant la coupe"
        ],
        "sets": "3-4 séries",
        "reps": "8-10 répétitions",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Tirez vers vous, pas seulement latéralement"
        ]
      },
      "es": {
        "name": "Tirones de Gancho — 45°",
        "description": "Tirón en polea específico para el gancho en ángulo de 45°, con curl de muñeca y tirón hacia el cuerpo como en un gancho real.",
        "muscles": "Bíceps, flexores de muñeca",
        "instructions": [
          "Ajusta la polea en ángulo de 45° a la altura de la mesa",
          "Curva la muñeca y tira hacia ti",
          "Vuelve con control, manteniendo el ahuecado"
        ],
        "sets": "3-4 series",
        "reps": "8-10 repeticiones",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Tira hacia ti, no solo hacia el lado"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Hook — 45°",
        "description": "Puxada no cabo específica para o hook num ângulo de 45°, com flexão de punho e puxada em direção ao corpo como num hook real.",
        "muscles": "Bíceps, flexores do punho",
        "instructions": [
          "Ajusta o cabo num ângulo de 45° à altura da mesa",
          "Enrola o punho e puxa na tua direção",
          "Regressa com controlo, mantendo o cup"
        ],
        "sets": "3-4 séries",
        "reps": "8-10 reps",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Puxa na tua direção, não apenas para o lado"
        ]
      },
      "ja": {
        "name": "フックプル－45°",
        "description": "実際のフックのように手首を巻き込みながら体に向かって引く、45°の角度でのフック専用ケーブルプルです。",
        "muscles": "上腕二頭筋、手首屈筋群",
        "instructions": [
          "テーブルの高さで45°の角度にケーブルをセットする",
          "手首を巻き込んで自分の方へ引く",
          "カップを維持しながら制御して戻す"
        ],
        "sets": "3〜4セット",
        "reps": "8〜10レップ",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "横方向ではなく自分の方へ引く"
        ]
      },
      "ko": {
        "name": "훅 풀 — 45°",
        "description": "실제 훅처럼 손목을 감고 몸쪽으로 당기는, 45° 각도의 훅 전용 케이블 풀입니다.",
        "muscles": "이두근, 손목 굴근",
        "instructions": [
          "테이블 높이에서 케이블을 45° 각도로 설정하세요",
          "손목을 감고 자신 쪽으로 당기세요",
          "컵을 유지하며 통제하여 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-10회",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "옆이 아닌 자신 쪽으로 당기세요"
        ]
      }
    }
  },
  {
    "id": "cableWristCurls",
    "slug": "cable-wrist-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Isolated Wrist Curls on Cable Machine",
        "description": "Isolated wrist curl on a cable machine for constant tension through the whole range.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Kneel or sit facing a low cable pulley",
          "Rest forearm on your thigh, palm facing up",
          "Curl the wrist up against the cable, then lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "The cable keeps tension on the wrist through the whole range",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Изолированные сгибания запястий на блоке",
        "description": "Изолированное сгибание кисти на блочном тренажёре для постоянного напряжения по всей амплитуде.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Встаньте на колено или сядьте лицом к нижнему блоку",
          "Положите предплечье на бедро, ладонью вверх",
          "Сгибайте кисть против блока, затем опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Блок сохраняет напряжение на кисти по всей амплитуде",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Isolierte Handgelenk-Curls am Kabelzug",
        "description": "Isolierter Handgelenk-Curl am Kabelzug für konstante Spannung über den gesamten Radius.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Knie oder sitze mit Blick zu einem niedrigen Kabelzug",
          "Lege den Unterarm auf den Oberschenkel, Handfläche nach oben",
          "Curle das Handgelenk gegen den Zug, senke dann kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Der Kabelzug hält die Spannung im Handgelenk über den gesamten Radius",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Flexions du poignet isolées à la poulie",
        "description": "Flexion isolée du poignet à la poulie pour une tension constante sur toute l'amplitude.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Agenouillez-vous ou asseyez-vous face à une poulie basse",
          "Posez l'avant-bras sur la cuisse, paume vers le haut",
          "Enroulez le poignet contre la poulie, puis descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "La poulie maintient la tension sur le poignet sur toute l'amplitude",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Curl de Muñeca Aislado en Polea",
        "description": "Curl de muñeca aislado en máquina de poleas para tensión constante en todo el rango.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Arrodíllate o siéntate frente a una polea baja",
          "Apoya el antebrazo en el muslo, palma hacia arriba",
          "Curva la muñeca contra la polea, luego baja con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "La polea mantiene la tensión en la muñeca en todo el rango",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho Isoladas no Cabo",
        "description": "Flexão de punho isolada na máquina de cabos para tensão constante em toda a amplitude.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Ajoelha-te ou senta-te de frente para um cabo baixo",
          "Apoia o antebraço na coxa, palma para cima",
          "Enrola o punho contra o cabo, depois desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "O cabo mantém a tensão no punho em toda a amplitude",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "ケーブルアイソレーションリストカール",
        "description": "フルレンジで一定の緊張を保つためのケーブルマシンでのアイソレーションリストカールです。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "低いケーブルプーリーに向かって膝立ちか座る",
          "前腕を太ももに乗せ、手のひらを上に向ける",
          "ケーブルに逆らって手首を巻き込み、制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "ケーブルがフルレンジで手首に緊張を保つ",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "케이블 아이솔레이션 리스트 컬",
        "description": "전체 범위에서 일정한 긴장을 위한 케이블 머신 아이솔레이션 리스트 컬입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "낮은 케이블 풀리를 향해 무릎을 꿇거나 앉으세요",
          "손바닥을 위로 하고 전완을 허벅지에 올리세요",
          "케이블에 저항하며 손목을 감고 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "케이블이 전체 범위에서 손목의 긴장을 유지합니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "cableWristCurls45",
    "slug": "cable-wrist-curls-45",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "cable"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Cable Wrist Curls — 45°",
        "description": "Cable wrist curl performed at a 45° angle to mimic mid-match hand position.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Set the cable at a 45° angle",
          "Curl the wrist up against the cable",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Сгибания запястий на блоке — 45°",
        "description": "Сгибание кисти на блоке под углом 45°, имитирующим позицию в середине поединка.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Установите блок под углом 45°",
          "Сгибайте кисть против блока",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls am Kabelzug — 45°",
        "description": "Handgelenk-Curl am Kabelzug im 45°-Winkel, der die Handposition mitten im Match nachahmt.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Stelle den Kabelzug auf einen 45°-Winkel ein",
          "Curle das Handgelenk gegen den Zug",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Flexions du poignet à la poulie — 45°",
        "description": "Flexion du poignet à la poulie à un angle de 45°, imitant la position de main en milieu de match.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Réglez la poulie à un angle de 45°",
          "Enroulez le poignet contre la poulie",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Curl de Muñeca en Polea — 45°",
        "description": "Curl de muñeca en polea en ángulo de 45°, que imita la posición de la mano a mitad de combate.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Ajusta la polea a un ángulo de 45°",
          "Curva la muñeca contra la polea",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho no Cabo — 45°",
        "description": "Flexão de punho no cabo num ângulo de 45°, que imita a posição da mão a meio do combate.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Ajusta o cabo para um ângulo de 45°",
          "Enrola o punho contra o cabo",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "ケーブルリストカール－45°",
        "description": "試合中盤の手の位置を模した45°の角度でのケーブルリストカールです。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "ケーブルを45°の角度にセットする",
          "ケーブルに逆らって手首を巻き込む",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "케이블 리스트 컬 — 45°",
        "description": "시합 중반의 손 위치를 모방한 45° 각도의 케이블 리스트 컬입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "케이블을 45° 각도로 설정하세요",
          "케이블에 저항하며 손목을 감으세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "bandHandleWristCurls",
    "slug": "band-handle-wrist-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "band"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Wrist Curls with Resistance Band Handle",
        "description": "Wrist curl using a handle attachment on a resistance band — a home-friendly alternative to cable curls.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Anchor a resistance band at forearm height",
          "Curl the wrist up against the band",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "12-15 reps",
        "tips": [
          "No cable machine needed — great for home training",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Сгибания запястий с рукоятью на резинке",
        "description": "Сгибание кисти с использованием рукояти на резиновой ленте — домашняя альтернатива блочным сгибаниям.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Закрепите резинку на уровне предплечья",
          "Сгибайте кисть против резинки",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "12-15 повторений",
        "tips": [
          "Не требуется блочный тренажёр — отлично для домашних тренировок",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls mit Bandgriff",
        "description": "Handgelenk-Curl mit Griffaufsatz an einem Widerstandsband – heimtaugliche Alternative zu Kabelzug-Curls.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Befestige ein Widerstandsband auf Unterarmhöhe",
          "Curle das Handgelenk gegen das Band",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "12–15 Wdh.",
        "tips": [
          "Kein Kabelzug nötig – ideal fürs Heimtraining",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Flexions du poignet avec poignée élastique",
        "description": "Flexion du poignet avec une poignée sur un élastique — alternative pour la maison aux flexions à la poulie.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Fixez un élastique à hauteur d'avant-bras",
          "Enroulez le poignet contre l'élastique",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 répétitions",
        "tips": [
          "Aucune poulie nécessaire — parfait pour s'entraîner à la maison",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Curl de Muñeca con Asa en Banda",
        "description": "Curl de muñeca usando un asa en una banda de resistencia — alternativa casera a los curls en polea.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Fija una banda de resistencia a la altura del antebrazo",
          "Curva la muñeca contra la banda",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "12-15 repeticiones",
        "tips": [
          "No necesitas polea — ideal para entrenar en casa",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho com Pega em Banda",
        "description": "Flexão de punho usando uma pega numa banda elástica — alternativa caseira às flexões no cabo.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Fixa uma banda elástica à altura do antebraço",
          "Enrola o punho contra a banda",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "12-15 reps",
        "tips": [
          "Sem necessidade de máquina de cabos — ótimo para treino em casa",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "バンドハンドルリストカール",
        "description": "レジスタンスバンドのハンドルを使ったリストカールで、ケーブルカールの自宅代替です。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "前腕の高さにレジスタンスバンドを固定する",
          "バンドに逆らって手首を巻き込む",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "12〜15レップ",
        "tips": [
          "ケーブルマシン不要－自宅トレーニングに最適",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "밴드 핸들 리스트 컬",
        "description": "저항 밴드의 핸들을 사용하는 리스트 컬로, 케이블 컬의 가정용 대안입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "전완 높이에 저항 밴드를 고정하세요",
          "밴드에 저항하며 손목을 감으세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "12-15회",
        "tips": [
          "케이블 머신이 필요 없습니다 - 홈트레이닝에 좋습니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "wristCurlsWinningPosition",
    "slug": "wrist-curls-winning-position",
    "emoji": "🏆",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "table"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Wrist Curls in Winning Position",
        "description": "Wrist curl performed in the 'winning position' — the cupped, hook-grip posture used to finish a match.",
        "muscles": "Wrist flexors, biceps",
        "instructions": [
          "Set your hand in the cupped hook-grip 'winning' position",
          "Curl the wrist further into that position against resistance",
          "Return with control, resetting the grip each rep"
        ],
        "sets": "3-4 sets",
        "reps": "10-12 reps",
        "tips": [
          "Mimic your actual match hand position as closely as possible",
          "Curl as deep as possible for full range"
        ]
      },
      "ru": {
        "name": "Сгибания запястий в выигрышной позиции",
        "description": "Сгибание кисти в 'выигрышной позиции' — согнутой хватке крюка, используемой для завершения поединка.",
        "muscles": "Сгибатели кисти, бицепс",
        "instructions": [
          "Установите руку в согнутую хватку крюка ('выигрышную' позицию)",
          "Сгибайте кисть дальше в эту позицию против сопротивления",
          "Возвращайтесь с контролем, восстанавливая хват каждое повторение"
        ],
        "sets": "3-4 подхода",
        "reps": "10-12 повторений",
        "tips": [
          "Максимально имитируйте вашу реальную позицию руки в поединке",
          "Сгибайте максимально глубоко для полной амплитуды"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls in Siegposition",
        "description": "Handgelenk-Curl in der 'Siegposition' – der gecuppten Hook-Griffhaltung, die zum Match-Abschluss genutzt wird.",
        "muscles": "Handgelenkbeuger, Bizeps",
        "instructions": [
          "Bringe deine Hand in die gecuppte Hook-Griffhaltung ('Sieg'-Position)",
          "Curle das Handgelenk weiter in diese Position gegen Widerstand",
          "Kehre kontrolliert zurück, stelle den Griff jede Wdh. neu ein"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–12 Wdh.",
        "tips": [
          "Ahme deine tatsächliche Match-Handposition so genau wie möglich nach",
          "Curle so tief wie möglich für den vollen Radius"
        ]
      },
      "fr": {
        "name": "Flexions du poignet en position gagnante",
        "description": "Flexion du poignet dans la « position gagnante » — la prise en coupe du hook utilisée pour terminer un match.",
        "muscles": "Fléchisseurs du poignet, biceps",
        "instructions": [
          "Placez votre main dans la prise en coupe du hook (position « gagnante »)",
          "Enroulez le poignet davantage dans cette position contre résistance",
          "Revenez en contrôlant, en rétablissant la prise à chaque répétition"
        ],
        "sets": "3-4 séries",
        "reps": "10-12 répétitions",
        "tips": [
          "Imitez au mieux votre position de main réelle en match",
          "Enroulez aussi profondément que possible pour une amplitude complète"
        ]
      },
      "es": {
        "name": "Curl de Muñeca en Posición Ganadora",
        "description": "Curl de muñeca en la 'posición ganadora' — el agarre ahuecado de gancho usado para rematar un combate.",
        "muscles": "Flexores de muñeca, bíceps",
        "instructions": [
          "Coloca la mano en el agarre ahuecado de gancho (posición 'ganadora')",
          "Curva la muñeca más hacia esa posición contra resistencia",
          "Vuelve con control, restableciendo el agarre en cada repetición"
        ],
        "sets": "3-4 series",
        "reps": "10-12 repeticiones",
        "tips": [
          "Imita al máximo la posición real de tu mano en el combate",
          "Curva tan profundo como sea posible para un rango completo"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho em Posição Vencedora",
        "description": "Flexão de punho na 'posição vencedora' — a pega em cup do hook usada para finalizar um combate.",
        "muscles": "Flexores do punho, bíceps",
        "instructions": [
          "Coloca a mão na pega em cup do hook (posição 'vencedora')",
          "Enrola o punho ainda mais nessa posição contra resistência",
          "Regressa com controlo, reajustando a pega em cada rep"
        ],
        "sets": "3-4 séries",
        "reps": "10-12 reps",
        "tips": [
          "Imita ao máximo a tua posição de mão real em combate",
          "Enrola o mais fundo possível para amplitude completa"
        ]
      },
      "ja": {
        "name": "ウィニングポジションリストカール",
        "description": "試合をフィニッシュするために使うフックのカップグリップ姿勢「ウィニングポジション」でのリストカールです。",
        "muscles": "手首屈筋群、上腕二頭筋",
        "instructions": [
          "手をフックのカップグリップ（「ウィニング」姿勢）にセットする",
          "抵抗に対してその姿勢へさらに手首を巻き込む",
          "毎レップグリップをリセットしながら制御して戻す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜12レップ",
        "tips": [
          "実際の試合の手の姿勢をできる限り模す",
          "フルレンジのためにできるだけ深く巻き込む"
        ]
      },
      "ko": {
        "name": "위닝 포지션 리스트 컬",
        "description": "시합을 마무리하는 데 사용되는 훅의 컵 그립 자세인 '위닝 포지션'에서 하는 리스트 컬입니다.",
        "muscles": "손목 굴근, 이두근",
        "instructions": [
          "손을 훅의 컵 그립('위닝') 자세로 두세요",
          "저항에 맞서 그 자세로 손목을 더 감으세요",
          "매 반복마다 그립을 재설정하며 통제하여 되돌리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-12회",
        "tips": [
          "실제 시합의 손 자세를 최대한 모방하세요",
          "전체 범위를 위해 가능한 한 깊게 감으세요"
        ]
      }
    }
  },
  {
    "id": "singleBarWristCurls",
    "slug": "single-bar-wrist-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "barbell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Wrist Curls with a Single Bar",
        "description": "Classic wrist curl performed with a single straight bar.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Sit with forearm resting on your thigh or a bench, palm up",
          "Hold the bar with a relaxed grip",
          "Curl the wrist up, then lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 reps",
        "tips": [
          "Use full range of motion for maximum development",
          "Control the negative (lowering) portion slowly"
        ]
      },
      "ru": {
        "name": "Сгибания запястий с одиночным грифом",
        "description": "Классическое сгибание кисти с прямым грифом.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Сядьте, положив предплечье на бедро или скамью, ладонью вверх",
          "Держите гриф расслабленным хватом",
          "Сгибайте кисть вверх, затем опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Используйте полную амплитуду движения для максимального развития",
          "Контролируйте негативную фазу (опускание)"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls mit einzelner Stange",
        "description": "Klassischer Handgelenk-Curl mit einer geraden Stange.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Setze dich mit dem Unterarm auf Oberschenkel oder Bank, Handfläche nach oben",
          "Halte die Stange mit lockerem Griff",
          "Curle das Handgelenk hoch, senke dann kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Nutze den vollen Bewegungsradius für maximale Entwicklung",
          "Kontrolliere die negative Phase (Absenken) langsam"
        ]
      },
      "fr": {
        "name": "Flexions du poignet avec barre simple",
        "description": "Flexion classique du poignet avec une barre droite.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Asseyez-vous, l'avant-bras sur la cuisse ou le banc, paume vers le haut",
          "Tenez la barre avec une prise détendue",
          "Enroulez le poignet vers le haut, puis descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Utilisez une amplitude complète pour un développement maximal",
          "Contrôlez lentement la phase négative (descente)"
        ]
      },
      "es": {
        "name": "Curl de Muñeca con Barra Simple",
        "description": "Curl de muñeca clásico con una barra recta.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Siéntate con el antebrazo apoyado en el muslo o banco, palma hacia arriba",
          "Sujeta la barra con agarre relajado",
          "Curva la muñeca hacia arriba, luego baja con control"
        ],
        "sets": "3-4 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Usa un rango de movimiento completo para máximo desarrollo",
          "Controla la fase negativa (bajada) lentamente"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho com Barra Simples",
        "description": "Flexão de punho clássica com uma barra reta.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Senta-te com o antebraço apoiado na coxa ou banco, palma virada para cima",
          "Segura a barra com pega relaxada",
          "Enrola o punho para cima, depois desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 reps",
        "tips": [
          "Usa a amplitude completa para o máximo desenvolvimento",
          "Controla a fase negativa (descida) lentamente"
        ]
      },
      "ja": {
        "name": "シングルバーリストカール",
        "description": "ストレートバーを使ったクラシックなリストカールです。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "前腕を太ももかベンチに乗せ、手のひらを上に向けて座る",
          "リラックスしたグリップでバーを持つ",
          "手首を巻き上げ、制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15レップ",
        "tips": [
          "最大限の発達のために可動域をフルに使う",
          "ネガティブ（下ろす）動作をゆっくり制御する"
        ]
      },
      "ko": {
        "name": "싱글 바 리스트 컬",
        "description": "곧은 바를 사용하는 클래식 리스트 컬입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "손바닥을 위로 향하게 하고 전완을 허벅지나 벤치에 올린 채 앉으세요",
          "바를 편안한 그립으로 잡으세요",
          "손목을 감아 올리고 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15회",
        "tips": [
          "최대 발달을 위해 전체 가동 범위를 사용하세요",
          "네거티브(내리는) 구간을 천천히 통제하세요"
        ]
      }
    }
  },
  {
    "id": "offsetBarWristCurls",
    "slug": "offset-bar-wrist-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "barbell"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "Offset Wrist Curls — One End of the Bar",
        "description": "Wrist curl gripping only one end of the bar, adding an off-center stabilization challenge.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Grip one end of a barbell only, letting it tilt slightly",
          "Curl the wrist up while resisting the tilt",
          "Lower with control"
        ],
        "sets": "3 sets",
        "reps": "10-12 reps",
        "tips": [
          "Use lighter weight than a standard wrist curl to start",
          "Control the negative (lowering) portion slowly"
        ]
      },
      "ru": {
        "name": "Сгибания запястий со смещённым хватом",
        "description": "Сгибание кисти с хватом только за один конец грифа, добавляющее нагрузку на стабилизацию из-за смещённого центра тяжести.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Возьмитесь только за один конец штанги, позволяя ей слегка наклоняться",
          "Сгибайте кисть вверх, сопротивляясь наклону",
          "Опускайте с контролем"
        ],
        "sets": "3 подхода",
        "reps": "10-12 повторений",
        "tips": [
          "Начните с меньшего веса, чем в обычном сгибании кисти",
          "Контролируйте негативную фазу (опускание)"
        ]
      },
      "de": {
        "name": "Handgelenk-Curls mit versetztem Griff",
        "description": "Handgelenk-Curl mit Griff an nur einem Ende der Stange, zusätzliche Stabilisierungsherausforderung durch den versetzten Schwerpunkt.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Greife nur ein Ende der Langhantel, lass sie leicht kippen",
          "Curle das Handgelenk hoch, während du dem Kippen widerstehst",
          "Senke kontrolliert ab"
        ],
        "sets": "3 Sätze",
        "reps": "10–12 Wdh.",
        "tips": [
          "Starte mit weniger Gewicht als bei einem normalen Handgelenk-Curl",
          "Kontrolliere die negative Phase (Absenken) langsam"
        ]
      },
      "fr": {
        "name": "Flexions du poignet en prise décentrée",
        "description": "Flexion du poignet en saisissant une seule extrémité de la barre, ajoutant un défi de stabilisation dû au centre de gravité décentré.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Saisissez une seule extrémité de la barre, en la laissant basculer légèrement",
          "Enroulez le poignet vers le haut en résistant au basculement",
          "Descendez en contrôlant"
        ],
        "sets": "3 séries",
        "reps": "10-12 répétitions",
        "tips": [
          "Commencez avec moins de poids que pour une flexion du poignet classique",
          "Contrôlez lentement la phase négative (descente)"
        ]
      },
      "es": {
        "name": "Curl de Muñeca con Agarre Descentrado",
        "description": "Curl de muñeca agarrando solo un extremo de la barra, añadiendo un reto de estabilización por el centro de gravedad descentrado.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Agarra solo un extremo de la barra, dejando que se incline ligeramente",
          "Curva la muñeca hacia arriba resistiendo la inclinación",
          "Baja con control"
        ],
        "sets": "3 series",
        "reps": "10-12 repeticiones",
        "tips": [
          "Empieza con menos peso que en un curl de muñeca normal",
          "Controla la fase negativa (bajada) lentamente"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho com Pega Descentrada",
        "description": "Flexão de punho agarrando apenas uma extremidade da barra, adicionando um desafio extra de estabilização devido ao centro de gravidade descentrado.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Agarra apenas uma extremidade de uma barra, deixando-a inclinar-se ligeiramente",
          "Enrola o punho para cima resistindo à inclinação",
          "Desce com controlo"
        ],
        "sets": "3 séries",
        "reps": "10-12 reps",
        "tips": [
          "Começa com menos peso do que numa flexão de punho normal",
          "Controla a fase negativa (descida) lentamente"
        ]
      },
      "ja": {
        "name": "オフセットバーリストカール",
        "description": "バーの片端のみを握るリストカールで、重心のずれによる追加の安定化課題を加えます。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "バーの片端のみを握り、少し傾けさせる",
          "傾きに抵抗しながら手首を巻き上げる",
          "制御して下ろす"
        ],
        "sets": "3セット",
        "reps": "10〜12レップ",
        "tips": [
          "通常のリストカールより軽い重量から始める",
          "ネガティブ（下ろす）動作をゆっくり制御する"
        ]
      },
      "ko": {
        "name": "오프셋 바 리스트 컬",
        "description": "바의 한쪽 끝만 잡는 리스트 컬로, 무게 중심이 치우쳐 안정화에 추가적인 도전이 됩니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "바벨의 한쪽 끝만 잡아 살짝 기울어지게 하세요",
          "기울어짐에 저항하며 손목을 감아 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3세트",
        "reps": "10-12회",
        "tips": [
          "일반 리스트 컬보다 가벼운 무게로 시작하세요",
          "네거티브(내리는) 구간을 천천히 통제하세요"
        ]
      }
    }
  },
  {
    "id": "generalWristStrengthDrill",
    "slug": "general-wrist-strength-drill",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "wristFlexors",
    "translations": {
      "en": {
        "name": "General Wrist Strength Drill",
        "description": "General wrist-strengthening drill for building overall flexor endurance and control.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Hold a light dumbbell with forearm supported",
          "Curl the wrist through a full, controlled range",
          "Lower slowly and repeat"
        ],
        "sets": "3 sets",
        "reps": "12-20 reps",
        "tips": [
          "Use full range of motion for maximum development",
          "Control the negative (lowering) portion slowly"
        ]
      },
      "ru": {
        "name": "Общее упражнение на силу запястья",
        "description": "Общеразвивающее упражнение для запястья, развивающее выносливость и контроль сгибателей.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Держите лёгкую гантель с опорой предплечья",
          "Сгибайте кисть по полной контролируемой амплитуде",
          "Медленно опускайте и повторяйте"
        ],
        "sets": "3 подхода",
        "reps": "12-20 повторений",
        "tips": [
          "Используйте полную амплитуду движения для максимального развития",
          "Контролируйте негативную фазу (опускание)"
        ]
      },
      "de": {
        "name": "Allgemeine Handgelenkkraft-Übung",
        "description": "Allgemeine Handgelenk-Stärkungsübung zum Aufbau von Ausdauer und Kontrolle der Beuger.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Halte eine leichte Kurzhantel mit abgestütztem Unterarm",
          "Curle das Handgelenk über den vollen kontrollierten Radius",
          "Senke langsam ab und wiederhole"
        ],
        "sets": "3 Sätze",
        "reps": "12–20 Wdh.",
        "tips": [
          "Nutze den vollen Bewegungsradius für maximale Entwicklung",
          "Kontrolliere die negative Phase (Absenken) langsam"
        ]
      },
      "fr": {
        "name": "Exercice général de force du poignet",
        "description": "Exercice général de renforcement du poignet pour développer l'endurance et le contrôle des fléchisseurs.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Tenez un haltère léger avec l'avant-bras soutenu",
          "Enroulez le poignet sur une amplitude complète et contrôlée",
          "Descendez lentement et répétez"
        ],
        "sets": "3 séries",
        "reps": "12-20 répétitions",
        "tips": [
          "Utilisez une amplitude complète pour un développement maximal",
          "Contrôlez lentement la phase négative (descente)"
        ]
      },
      "es": {
        "name": "Ejercicio General de Fuerza de Muñeca",
        "description": "Ejercicio general de fortalecimiento de muñeca para desarrollar resistencia y control de los flexores.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Sujeta una mancuerna ligera con el antebrazo apoyado",
          "Curva la muñeca en un rango completo y controlado",
          "Baja lentamente y repite"
        ],
        "sets": "3 series",
        "reps": "12-20 repeticiones",
        "tips": [
          "Usa un rango de movimiento completo para máximo desarrollo",
          "Controla la fase negativa (bajada) lentamente"
        ]
      },
      "pt-BR": {
        "name": "Exercício Geral de Força de Punho",
        "description": "Exercício geral de fortalecimento do punho para desenvolver resistência e controlo dos flexores.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Segura um halter leve com o antebraço apoiado",
          "Enrola o punho numa amplitude completa e controlada",
          "Desce lentamente e repete"
        ],
        "sets": "3 séries",
        "reps": "12-20 reps",
        "tips": [
          "Usa a amplitude completa para o máximo desenvolvimento",
          "Controla a fase negativa (descida) lentamente"
        ]
      },
      "ja": {
        "name": "一般的な手首筋力ドリル",
        "description": "屈筋群の持久力とコントロールを鍛える一般的な手首強化エクササイズです。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "前腕を支えた状態で軽いダンベルを持つ",
          "フルレンジで制御して手首を巻き込む",
          "ゆっくり下ろして繰り返す"
        ],
        "sets": "3セット",
        "reps": "12〜20レップ",
        "tips": [
          "最大限の発達のために可動域をフルに使う",
          "ネガティブ（下ろす）動作をゆっくり制御する"
        ]
      },
      "ko": {
        "name": "일반 손목 근력 드릴",
        "description": "굴근의 지구력과 컨트롤을 기르는 일반적인 손목 강화 운동입니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "전완을 받친 채 가벼운 덤벨을 잡으세요",
          "전체 범위로 통제하며 손목을 감으세요",
          "천천히 내리고 반복하세요"
        ],
        "sets": "3세트",
        "reps": "12-20회",
        "tips": [
          "최대 발달을 위해 전체 가동 범위를 사용하세요",
          "네거티브(내리는) 구간을 천천히 통제하세요"
        ]
      }
    }
  },
  {
    "id": "tableReverseWristCurls",
    "slug": "table-reverse-wrist-curls",
    "emoji": "🔃",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "table"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Reverse Wrist Curls on the Table",
        "description": "Reverse wrist curl performed with the forearm resting on the table, close to a live match position.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Rest forearm on the table, palm facing down",
          "Hold a lighter weight than a regular wrist curl",
          "Raise the wrist up, keeping the forearm still"
        ],
        "sets": "3 sets",
        "reps": "12-15 reps",
        "tips": [
          "Use lighter weight than regular wrist curls",
          "Essential for elbow and wrist health"
        ]
      },
      "ru": {
        "name": "Обратные сгибания запястий на столе",
        "description": "Обратное сгибание кисти с предплечьем, лежащим на столе, близко к реальной позиции в поединке.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Положите предплечье на стол, ладонью вниз",
          "Используйте меньший вес, чем для обычных сгибаний",
          "Поднимите кисть вверх, удерживая предплечье неподвижным"
        ],
        "sets": "3 подхода",
        "reps": "12-15 повторений",
        "tips": [
          "Используйте меньший вес, чем для обычных сгибаний",
          "Необходимо для здоровья локтя и запястья"
        ]
      },
      "de": {
        "name": "Umgekehrte Handgelenk-Curls am Tisch",
        "description": "Umgekehrter Handgelenk-Curl mit dem Unterarm auf dem Tisch, nah an einer echten Match-Position.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Unterarm auf den Tisch, Handfläche nach unten",
          "Halte leichteres Gewicht als bei normalen Handgelenk-Curls",
          "Hebe das Handgelenk an, Unterarm bleibt still"
        ],
        "sets": "3 Sätze",
        "reps": "12–15 Wdh.",
        "tips": [
          "Nutze leichteres Gewicht als bei normalen Handgelenk-Curls",
          "Essenziell für die Gesundheit von Ellbogen und Handgelenk"
        ]
      },
      "fr": {
        "name": "Flexions inversées du poignet à table",
        "description": "Flexion inversée du poignet avec l'avant-bras posé sur la table, proche d'une position de match réelle.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Posez l'avant-bras sur la table, paume vers le bas",
          "Tenez un poids plus léger que pour les flexions classiques",
          "Levez le poignet en gardant l'avant-bras immobile"
        ],
        "sets": "3 séries",
        "reps": "12-15 répétitions",
        "tips": [
          "Utilisez un poids plus léger que pour les flexions classiques",
          "Essentiel pour la santé du coude et du poignet"
        ]
      },
      "es": {
        "name": "Curl Inverso de Muñeca en Mesa",
        "description": "Curl inverso de muñeca con el antebrazo apoyado en la mesa, cerca de una posición real de combate.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Apoya el antebrazo en la mesa, palma hacia abajo",
          "Sujeta un peso más ligero que en los curls de muñeca normales",
          "Eleva la muñeca hacia arriba, manteniendo el antebrazo quieto"
        ],
        "sets": "3 series",
        "reps": "12-15 repeticiones",
        "tips": [
          "Usa un peso más ligero que en los curls de muñeca normales",
          "Esencial para la salud del codo y la muñeca"
        ]
      },
      "pt-BR": {
        "name": "Flexões de Punho Invertidas na Mesa",
        "description": "Flexão de punho invertida com o antebraço apoiado na mesa, próxima de uma posição real de combate.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Apoia o antebraço na mesa, palma virada para baixo",
          "Segura um peso mais leve do que nas flexões de punho normais",
          "Eleva o punho, mantendo o antebraço imóvel"
        ],
        "sets": "3 séries",
        "reps": "12-15 reps",
        "tips": [
          "Usa um peso mais leve do que nas flexões de punho normais",
          "Essencial para a saúde do cotovelo e do punho"
        ]
      },
      "ja": {
        "name": "テーブルリバースリストカール",
        "description": "前腕をテーブルに乗せて行うリバースリストカールで、実際の試合姿勢に近いエクササイズです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "前腕をテーブルに乗せ、手のひらを下に向ける",
          "通常のリストカールより軽い重量を持つ",
          "前腕を固定したまま手首を持ち上げる"
        ],
        "sets": "3セット",
        "reps": "12〜15レップ",
        "tips": [
          "通常のリストカールより軽い重量を使う",
          "肘と手首の健康に不可欠"
        ]
      },
      "ko": {
        "name": "테이블 리버스 리스트 컬",
        "description": "전완을 테이블에 올린 채 하는 리버스 리스트 컬로, 실제 시합 자세에 가깝습니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손바닥을 아래로 향하게 하고 전완을 테이블에 올리세요",
          "일반 리스트 컬보다 가벼운 무게를 잡으세요",
          "전완을 고정한 채 손목을 위로 들어 올리세요"
        ],
        "sets": "3세트",
        "reps": "12-15회",
        "tips": [
          "일반 리스트 컬보다 가벼운 무게를 사용하세요",
          "팔꿈치와 손목 건강에 필수적입니다"
        ]
      }
    }
  },
  {
    "id": "staticReverseWristCurlHold",
    "slug": "static-reverse-wrist-curl-hold",
    "emoji": "🔃",
    "category": "static",
    "equipmentKeys": [
      "dumbbell"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Static Reverse Wrist Curl Hold",
        "description": "Isometric hold at the top of a reverse wrist curl to build wrist-extensor lockout strength.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Rest forearm on a bench, palm facing down",
          "Raise the wrist to the top position",
          "Hold with the wrist fully extended"
        ],
        "sets": "3 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "Use lighter weight than regular wrist curls",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание обратного сгибания запястья",
        "description": "Изометрическое удержание в верхней точке обратного сгибания кисти для развития силы разгибателей в конечной точке.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Положите предплечье на скамью, ладонью вниз",
          "Поднимите кисть в верхнюю точку",
          "Удерживайте с полностью разогнутой кистью"
        ],
        "sets": "3 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "Используйте меньший вес, чем для обычных сгибаний",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Statisches Halten des umgekehrten Handgelenk-Curls",
        "description": "Isometrisches Halten am oberen Punkt eines umgekehrten Handgelenk-Curls zum Aufbau von Streckkraft in der Endposition.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Unterarm auf eine Bank, Handfläche nach unten",
          "Hebe das Handgelenk in die obere Position",
          "Halte mit voll gestrecktem Handgelenk"
        ],
        "sets": "3 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "Nutze leichteres Gewicht als bei normalen Handgelenk-Curls",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien statique de flexion inversée du poignet",
        "description": "Maintien isométrique au point haut d'une flexion inversée du poignet pour développer la force des extenseurs en position finale.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Posez l'avant-bras sur un banc, paume vers le bas",
          "Levez le poignet jusqu'à la position haute",
          "Maintenez avec le poignet complètement en extension"
        ],
        "sets": "3 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "Utilisez un poids plus léger que pour les flexions classiques",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén Estático de Curl Inverso de Muñeca",
        "description": "Sostén isométrico en el punto más alto de un curl inverso de muñeca para desarrollar fuerza de extensión en la posición final.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Apoya el antebrazo en un banco, palma hacia abajo",
          "Eleva la muñeca hasta la posición más alta",
          "Sostén con la muñeca totalmente extendida"
        ],
        "sets": "3 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "Usa un peso más ligero que en los curls de muñeca normales",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria Estática de Flexão de Punho Invertida",
        "description": "Isometria no topo de uma flexão de punho invertida para desenvolver força de extensão na posição final.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Apoia o antebraço num banco, palma virada para baixo",
          "Eleva o punho até à posição mais alta",
          "Mantém com o punho totalmente estendido"
        ],
        "sets": "3 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "Usa um peso mais leve do que nas flexões de punho normais",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "スタティックリバースリストカールホールド",
        "description": "フィニッシュ姿勢での伸筋力を鍛えるため、リバースリストカールの頂点でのアイソメトリックホールドです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "前腕をベンチに乗せ、手のひらを下に向ける",
          "手首を最高点まで持ち上げる",
          "手首を完全に伸展させたまま保持する"
        ],
        "sets": "3セット",
        "reps": "10〜15秒保持",
        "tips": [
          "通常のリストカールより軽い重量を使う",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "스태틱 리버스 리스트 컬 홀드",
        "description": "마무리 자세에서 신근 근력을 기르기 위한 리버스 리스트 컬 상단에서의 등척성 홀드입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손바닥을 아래로 향하게 하고 전완을 벤치에 올리세요",
          "손목을 최고점까지 들어 올리세요",
          "손목을 완전히 신전한 채 유지하세요"
        ],
        "sets": "3세트",
        "reps": "10-15초 유지",
        "tips": [
          "일반 리스트 컬보다 가벼운 무게를 사용하세요",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "beltOverFingersRiserPulls",
    "slug": "belt-over-fingers-riser-pulls",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "riser",
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Pulls — Belt Over Fingers",
        "description": "Riser pull with a belt looped over the fingers, training the rising portion of the top roll with a finger-specific grip.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Loop a belt or strap over your fingers and attach to the riser",
          "Raise your knuckles and thumb upward (radial deviation)",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Movement is toward the thumb (radial) side",
          "Key strength component for top roll"
        ]
      },
      "ru": {
        "name": "Тяга райзера — лямка через пальцы",
        "description": "Тяга райзера с лямкой, продетой через пальцы, тренирующая подъёмную часть верхнего с хватом за пальцы.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Проденьте лямку через пальцы и прикрепите к райзеру",
          "Поднимайте костяшки и большой палец вверх (радиальное отклонение)",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Движение в сторону большого пальца",
          "Ключевой компонент силы для верхнего"
        ]
      },
      "de": {
        "name": "Riser-Züge — Strap über den Fingern",
        "description": "Riser-Zug mit einem über die Finger gelegten Strap, trainiert den Rising-Anteil des Top Rolls mit fingerspezifischem Griff.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege einen Strap über die Finger und befestige ihn am Riser",
          "Hebe Knöchel und Daumen nach oben (Radialabduktion)",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Die Bewegung geht zur Daumenseite (radial)",
          "Wichtige Kraftkomponente für den Top Roll"
        ]
      },
      "fr": {
        "name": "Tirages au riser — sangle sur les doigts",
        "description": "Tirage au riser avec une sangle passée sur les doigts, entraîne la composante rising du top roll avec une prise spécifique aux doigts.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Passez une sangle sur les doigts et fixez-la au riser",
          "Levez les articulations et le pouce vers le haut (déviation radiale)",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Le mouvement se fait vers le côté du pouce (radial)",
          "Composante de force clé pour le top roll"
        ]
      },
      "es": {
        "name": "Tirones de Riser — Correa Sobre los Dedos",
        "description": "Tirón de riser con una correa pasada por los dedos, entrena la parte de elevación del top roll con agarre específico de dedos.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Pasa una correa por los dedos y fíjala al riser",
          "Eleva los nudillos y el pulgar hacia arriba (desviación radial)",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "El movimiento es hacia el lado del pulgar (radial)",
          "Componente clave de fuerza para el top roll"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Riser — Correia Sobre os Dedos",
        "description": "Puxada de riser com uma correia passada sobre os dedos, treina a componente de elevação do top roll com pega específica de dedos.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Passa uma correia sobre os dedos e fixa-a ao riser",
          "Eleva os nós dos dedos e o polegar para cima (desvio radial)",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "O movimento é para o lado do polegar (radial)",
          "Componente-chave de força para o top roll"
        ]
      },
      "ja": {
        "name": "ライザープル－指越しベルト",
        "description": "指越しにベルトを通して行うライザープルで、指特有のグリップでトップロールのライジング要素を鍛えます。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "指越しにベルトかストラップを通しライザーに取り付ける",
          "拳と親指を上に持ち上げる（橈屈）",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "動作は親指（橈側）方向へ",
          "トップロールの重要な筋力要素"
        ]
      },
      "ko": {
        "name": "라이저 풀 — 손가락 위 벨트",
        "description": "손가락 위로 벨트를 걸어 하는 라이저 풀로, 손가락 특화 그립으로 탑롤의 라이징 요소를 훈련합니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손가락 위로 벨트나 스트랩을 걸고 라이저에 연결하세요",
          "관절과 엄지를 위로 들어 올리세요(요측 편위)",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "동작은 엄지(요측) 방향입니다",
          "탑롤의 핵심 근력 요소입니다"
        ]
      }
    }
  },
  {
    "id": "riserPulls110",
    "slug": "riser-pulls-110",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "riser"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Pulls — 110°+",
        "description": "Riser pull at a wide 110°+ angle for top roll strength closer to a finishing position.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Attach the riser at a wide 110°+ angle",
          "Raise your knuckles and thumb upward",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "The wider angle trains rising closer to a finishing position",
          "Movement is toward the thumb (radial) side"
        ]
      },
      "ru": {
        "name": "Тяга райзера — 110°+",
        "description": "Тяга райзера под широким углом 110°+ для силы верхнего ближе к завершающей позиции.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Закрепите райзер под широким углом 110°+",
          "Поднимайте костяшки и большой палец вверх",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Более широкий угол тренирует подъём ближе к завершающей позиции",
          "Движение в сторону большого пальца"
        ]
      },
      "de": {
        "name": "Riser-Züge — 110°+",
        "description": "Riser-Zug im weiten Winkel von 110°+ für Top-Roll-Kraft näher an der Abschlussposition.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Befestige den Riser im weiten Winkel von 110°+",
          "Hebe Knöchel und Daumen nach oben",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Der weitere Winkel trainiert Rising näher an der Abschlussposition",
          "Die Bewegung geht zur Daumenseite (radial)"
        ]
      },
      "fr": {
        "name": "Tirages au riser — 110°+",
        "description": "Tirage au riser à un angle large de 110°+ pour la force de top roll plus proche de la position de finition.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Fixez le riser à un angle large de 110°+",
          "Levez les articulations et le pouce vers le haut",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "L'angle plus large entraîne le rising plus près de la position de finition",
          "Le mouvement se fait vers le côté du pouce (radial)"
        ]
      },
      "es": {
        "name": "Tirones de Riser — 110°+",
        "description": "Tirón de riser en ángulo amplio de 110°+ para fuerza de top roll cerca de la posición de remate.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Fija el riser en ángulo amplio de 110°+",
          "Eleva los nudillos y el pulgar hacia arriba",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "El ángulo más amplio entrena la elevación cerca de la posición de remate",
          "El movimiento es hacia el lado del pulgar (radial)"
        ]
      },
      "pt-BR": {
        "name": "Puxadas de Riser — 110°+",
        "description": "Puxada de riser num ângulo amplo de 110°+ para força de top roll mais perto da posição de finalização.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Fixa o riser num ângulo amplo de 110°+",
          "Eleva os nós dos dedos e o polegar para cima",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "O ângulo mais amplo treina a elevação mais perto da posição de finalização",
          "O movimento é para o lado do polegar (radial)"
        ]
      },
      "ja": {
        "name": "ライザープル－110°+",
        "description": "フィニッシュ姿勢に近いトップロール力のための110°+の広い角度でのライザープルです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "ライザーを110°+の広い角度に固定する",
          "拳と親指を上に持ち上げる",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "広い角度はフィニッシュ姿勢に近いライジングを鍛える",
          "動作は親指（橈側）方向へ"
        ]
      },
      "ko": {
        "name": "라이저 풀 — 110°+",
        "description": "마무리 자세에 가까운 탑롤 근력을 위한 110°+의 넓은 각도의 라이저 풀입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "라이저를 110°+의 넓은 각도로 고정하세요",
          "관절과 엄지를 위로 들어 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "더 넓은 각도는 마무리 자세에 가까운 라이징을 훈련합니다",
          "동작은 엄지(요측) 방향입니다"
        ]
      }
    }
  },
  {
    "id": "riserDynamicPulls",
    "slug": "riser-dynamic-pulls",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "riser"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Dynamic Pulls",
        "description": "Dynamic riser pull for building rising strength through the top-roll range of motion.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Attach the riser to a cable or band",
          "Raise your knuckles and thumb upward against resistance",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Movement is toward the thumb (radial) side",
          "Key strength component for top roll"
        ]
      },
      "ru": {
        "name": "Динамическая тяга райзера",
        "description": "Динамическая тяга райзера для развития силы подъёма по всей амплитуде верхнего.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Закрепите райзер на блоке или резинке",
          "Поднимайте костяшки и большой палец вверх против сопротивления",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Движение в сторону большого пальца",
          "Ключевой компонент силы для верхнего"
        ]
      },
      "de": {
        "name": "Dynamische Riser-Züge",
        "description": "Dynamischer Riser-Zug zum Aufbau von Rising-Kraft über die gesamte Top-Roll-Bewegung.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Befestige den Riser an Kabelzug oder Band",
          "Hebe Knöchel und Daumen gegen den Widerstand nach oben",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Die Bewegung geht zur Daumenseite (radial)",
          "Wichtige Kraftkomponente für den Top Roll"
        ]
      },
      "fr": {
        "name": "Tirages dynamiques au riser",
        "description": "Tirage dynamique au riser pour développer la force de rising sur toute l'amplitude du top roll.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Fixez le riser à une poulie ou un élastique",
          "Levez les articulations et le pouce contre la résistance",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Le mouvement se fait vers le côté du pouce (radial)",
          "Composante de force clé pour le top roll"
        ]
      },
      "es": {
        "name": "Tirones Dinámicos de Riser",
        "description": "Tirón dinámico de riser para desarrollar fuerza de elevación en todo el rango del top roll.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Fija el riser a una polea o banda",
          "Eleva los nudillos y el pulgar contra la resistencia",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "El movimiento es hacia el lado del pulgar (radial)",
          "Componente clave de fuerza para el top roll"
        ]
      },
      "pt-BR": {
        "name": "Puxadas Dinâmicas de Riser",
        "description": "Puxada dinâmica de riser para desenvolver força de elevação em toda a amplitude do top roll.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Fixa o riser a um cabo ou banda",
          "Eleva os nós dos dedos e o polegar contra a resistência",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "O movimento é para o lado do polegar (radial)",
          "Componente-chave de força para o top roll"
        ]
      },
      "ja": {
        "name": "ダイナミックライザープル",
        "description": "トップロールの可動域全体でライジング力を鍛えるダイナミックなライザープルです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "ライザーをケーブルかバンドに固定する",
          "抵抗に逆らって拳と親指を持ち上げる",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "動作は親指（橈側）方向へ",
          "トップロールの重要な筋力要素"
        ]
      },
      "ko": {
        "name": "다이나믹 라이저 풀",
        "description": "탑롤 전체 범위에서 라이징 힘을 기르는 다이나믹 라이저 풀입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "라이저를 케이블이나 밴드에 고정하세요",
          "저항에 맞서 관절과 엄지를 들어 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "동작은 엄지(요측) 방향입니다",
          "탑롤의 핵심 근력 요소입니다"
        ]
      }
    }
  },
  {
    "id": "tableRiserLift",
    "slug": "table-riser-lift",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "riser",
      "table"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Lift on the Table with Weight",
        "description": "Riser lift with the forearm resting on the table and a weight attached, close to a live top-roll position.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Rest your elbow on the table with the riser attached to a weight",
          "Raise your knuckles and thumb upward",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Keep the elbow anchored on the table throughout",
          "Movement is toward the thumb (radial) side"
        ]
      },
      "ru": {
        "name": "Подъём райзера на столе с весом",
        "description": "Подъём райзера с предплечьем, лежащим на столе, и весом, близко к реальной позиции верхнего.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Положите локоть на стол, райзер прикреплён к весу",
          "Поднимайте костяшки и большой палец вверх",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Держите локоть зафиксированным на столе на протяжении",
          "Движение в сторону большого пальца"
        ]
      },
      "de": {
        "name": "Riser-Hebung am Tisch mit Gewicht",
        "description": "Riser-Hebung mit dem Unterarm auf dem Tisch und angehängtem Gewicht, nah an einer echten Top-Roll-Position.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Stütze den Ellbogen auf dem Tisch, der Riser ist an einem Gewicht befestigt",
          "Hebe Knöchel und Daumen nach oben",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Halte den Ellbogen durchgehend auf dem Tisch fixiert",
          "Die Bewegung geht zur Daumenseite (radial)"
        ]
      },
      "fr": {
        "name": "Levée au riser à table avec poids",
        "description": "Levée au riser avec l'avant-bras posé sur la table et un poids attaché, proche d'une position réelle de top roll.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Posez le coude sur la table, le riser fixé à un poids",
          "Levez les articulations et le pouce vers le haut",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Gardez le coude ancré sur la table tout du long",
          "Le mouvement se fait vers le côté du pouce (radial)"
        ]
      },
      "es": {
        "name": "Elevación de Riser en Mesa con Peso",
        "description": "Elevación de riser con el antebrazo apoyado en la mesa y un peso fijado, cerca de una posición real de top roll.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Apoya el codo en la mesa, el riser fijado a un peso",
          "Eleva los nudillos y el pulgar hacia arriba",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "Mantén el codo anclado en la mesa en todo momento",
          "El movimiento es hacia el lado del pulgar (radial)"
        ]
      },
      "pt-BR": {
        "name": "Elevação de Riser na Mesa com Peso",
        "description": "Elevação de riser com o antebraço apoiado na mesa e um peso fixado, próxima de uma posição real de top roll.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Apoia o cotovelo na mesa, o riser fixado a um peso",
          "Eleva os nós dos dedos e o polegar para cima",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "Mantém o cotovelo ancorado na mesa do início ao fim",
          "O movimento é para o lado do polegar (radial)"
        ]
      },
      "ja": {
        "name": "テーブルライザーリフト（ウェイト付き）",
        "description": "前腕をテーブルに乗せウェイトを取り付けたライザーリフトで、実際のトップロール姿勢に近いエクササイズです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "肘をテーブルに乗せ、ライザーをウェイトに取り付ける",
          "拳と親指を上に持ち上げる",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "常に肘をテーブルに固定しておく",
          "動作は親指（橈側）方向へ"
        ]
      },
      "ko": {
        "name": "테이블 라이저 리프트 (웨이트 포함)",
        "description": "전완을 테이블에 올리고 웨이트를 연결한 라이저 리프트로, 실제 탑롤 자세에 가깝습니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "팔꿈치를 테이블에 대고 라이저를 웨이트에 연결하세요",
          "관절과 엄지를 위로 들어 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "내내 팔꿈치를 테이블에 고정하세요",
          "동작은 엄지(요측) 방향입니다"
        ]
      }
    }
  },
  {
    "id": "riserOverFingers45",
    "slug": "riser-over-fingers-45",
    "emoji": "⬆️",
    "category": "static",
    "equipmentKeys": [
      "riser",
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Hold Over Fingers — 45°",
        "description": "Isometric riser hold with the strap over the fingers at a 45° angle, training the rising position used mid-toproll.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Loop the riser strap over your fingers at a 45° angle",
          "Raise your knuckles and thumb upward",
          "Hold the position under tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание райзера через пальцы — 45°",
        "description": "Изометрическое удержание райзера с лямкой через пальцы под углом 45°, тренирующее позицию подъёма в середине верхнего.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Проденьте лямку райзера через пальцы под углом 45°",
          "Поднимайте костяшки и большой палец вверх",
          "Удерживайте позицию под напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Riser-Halten über den Fingern — 45°",
        "description": "Isometrisches Riser-Halten mit dem Strap über den Fingern im 45°-Winkel, trainiert die Rising-Position mitten im Top Roll.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Riser-Strap im 45°-Winkel über die Finger",
          "Hebe Knöchel und Daumen nach oben",
          "Halte die Position unter Spannung"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien au riser sur les doigts — 45°",
        "description": "Maintien isométrique au riser avec la sangle sur les doigts à un angle de 45°, entraîne la position de rising en milieu de top roll.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Passez la sangle du riser sur les doigts à un angle de 45°",
          "Levez les articulations et le pouce vers le haut",
          "Maintenez la position sous tension"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Riser Sobre los Dedos — 45°",
        "description": "Sostén isométrico de riser con la correa sobre los dedos en ángulo de 45°, entrena la posición de elevación a mitad del top roll.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Pasa la correa del riser por los dedos en ángulo de 45°",
          "Eleva los nudillos y el pulgar hacia arriba",
          "Sostén la posición bajo tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Riser Sobre os Dedos — 45°",
        "description": "Isometria de riser com a correia sobre os dedos num ângulo de 45°, treina a posição de elevação a meio do top roll.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Passa a correia do riser sobre os dedos num ângulo de 45°",
          "Eleva os nós dos dedos e o polegar para cima",
          "Mantém a posição sob tensão"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "指越しライザーホールド－45°",
        "description": "指越しにストラップを通して45°の角度で行うアイソメトリックライザーホールドで、トップロール中盤のライジング姿勢を鍛えます。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "45°の角度で指越しにライザーストラップを通す",
          "拳と親指を上に持ち上げる",
          "緊張下で姿勢を保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15秒保持",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "손가락 위 라이저 홀드 — 45°",
        "description": "손가락 위로 스트랩을 걸어 45° 각도로 하는 등척성 라이저 홀드로, 탑롤 중반의 라이징 자세를 훈련합니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "45° 각도로 손가락 위에 라이저 스트랩을 거세요",
          "관절과 엄지를 위로 들어 올리세요",
          "긴장 상태로 자세를 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15초 유지",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "riserPullOverFingers45",
    "slug": "riser-pull-over-fingers-45",
    "emoji": "⬆️",
    "category": "dynamic",
    "equipmentKeys": [
      "riser",
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Riser Pull Over Fingers — 45°",
        "description": "Dynamic riser pull with the strap over the fingers at a 45° angle.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Loop the riser strap over your fingers at a 45° angle",
          "Raise your knuckles and thumb upward against resistance",
          "Lower with control"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "45° mimics a common mid-match hand position",
          "Movement is toward the thumb (radial) side"
        ]
      },
      "ru": {
        "name": "Тяга райзера через пальцы — 45°",
        "description": "Динамическая тяга райзера с лямкой через пальцы под углом 45°.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Проденьте лямку райзера через пальцы под углом 45°",
          "Поднимайте костяшки и большой палец вверх против сопротивления",
          "Опускайте с контролем"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "45° имитирует распространённую позицию руки в середине поединка",
          "Движение в сторону большого пальца"
        ]
      },
      "de": {
        "name": "Riser-Zug über den Fingern — 45°",
        "description": "Dynamischer Riser-Zug mit dem Strap über den Fingern im 45°-Winkel.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Riser-Strap im 45°-Winkel über die Finger",
          "Hebe Knöchel und Daumen gegen den Widerstand nach oben",
          "Senke kontrolliert ab"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "45° ahmt eine häufige Handposition mitten im Match nach",
          "Die Bewegung geht zur Daumenseite (radial)"
        ]
      },
      "fr": {
        "name": "Tirage au riser sur les doigts — 45°",
        "description": "Tirage dynamique au riser avec la sangle sur les doigts à un angle de 45°.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Passez la sangle du riser sur les doigts à un angle de 45°",
          "Levez les articulations et le pouce contre la résistance",
          "Descendez en contrôlant"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "45° imite une position de main courante en milieu de match",
          "Le mouvement se fait vers le côté du pouce (radial)"
        ]
      },
      "es": {
        "name": "Tirón de Riser Sobre los Dedos — 45°",
        "description": "Tirón dinámico de riser con la correa sobre los dedos en ángulo de 45°.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Pasa la correa del riser por los dedos en ángulo de 45°",
          "Eleva los nudillos y el pulgar contra la resistencia",
          "Baja con control"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "45° imita una posición común de la mano a mitad de combate",
          "El movimiento es hacia el lado del pulgar (radial)"
        ]
      },
      "pt-BR": {
        "name": "Puxada de Riser Sobre os Dedos — 45°",
        "description": "Puxada dinâmica de riser com a correia sobre os dedos num ângulo de 45°.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Passa a correia do riser sobre os dedos num ângulo de 45°",
          "Eleva os nós dos dedos e o polegar contra a resistência",
          "Desce com controlo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "45° imita uma posição comum de mão a meio do combate",
          "O movimento é para o lado do polegar (radial)"
        ]
      },
      "ja": {
        "name": "指越しライザープル－45°",
        "description": "指越しにストラップを通して45°の角度で行うダイナミックなライザープルです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "45°の角度で指越しにライザーストラップを通す",
          "抵抗に逆らって拳と親指を持ち上げる",
          "制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "45°は試合中盤によくある手の位置を模す",
          "動作は親指（橈側）方向へ"
        ]
      },
      "ko": {
        "name": "손가락 위 라이저 풀 — 45°",
        "description": "손가락 위로 스트랩을 걸어 45° 각도로 하는 다이나믹 라이저 풀입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "45° 각도로 손가락 위에 라이저 스트랩을 거세요",
          "저항에 맞서 관절과 엄지를 들어 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "45°는 시합 중반의 일반적인 손 위치를 모방합니다",
          "동작은 엄지(요측) 방향입니다"
        ]
      }
    }
  },
  {
    "id": "staticRiserHold",
    "slug": "static-riser-hold",
    "emoji": "⬆️",
    "category": "static",
    "equipmentKeys": [
      "riser"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Static Riser Hold",
        "description": "Isometric riser hold for raw top-roll finishing strength.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Attach the riser to a cable or band",
          "Raise your knuckles and thumb to the top position",
          "Hold with the wrist fully raised"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Movement is toward the thumb (radial) side",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Статическое удержание райзера",
        "description": "Изометрическое удержание райзера для чистой завершающей силы верхнего.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Закрепите райзер на блоке или резинке",
          "Поднимите костяшки и большой палец в верхнюю позицию",
          "Удерживайте с полностью поднятой кистью"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Движение в сторону большого пальца",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Statisches Riser-Halten",
        "description": "Isometrisches Riser-Halten für reine Top-Roll-Abschlusskraft.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Befestige den Riser an Kabelzug oder Band",
          "Hebe Knöchel und Daumen in die obere Position",
          "Halte mit voll gehobenem Handgelenk"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Die Bewegung geht zur Daumenseite (radial)",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien statique au riser",
        "description": "Maintien isométrique au riser pour une pure force de finition du top roll.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Fixez le riser à une poulie ou un élastique",
          "Levez les articulations et le pouce jusqu'à la position haute",
          "Maintenez avec le poignet complètement levé"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Le mouvement se fait vers le côté du pouce (radial)",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén Estático de Riser",
        "description": "Sostén isométrico de riser para pura fuerza de remate del top roll.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Fija el riser a una polea o banda",
          "Eleva los nudillos y el pulgar hasta la posición más alta",
          "Sostén con la muñeca totalmente elevada"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "El movimiento es hacia el lado del pulgar (radial)",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria Estática de Riser",
        "description": "Isometria de riser para força pura de finalização do top roll.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Fixa o riser a um cabo ou banda",
          "Eleva os nós dos dedos e o polegar até à posição mais alta",
          "Mantém com o punho totalmente elevado"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "O movimento é para o lado do polegar (radial)",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "スタティックライザーホールド",
        "description": "純粋なトップロールのフィニッシュ力のためのアイソメトリックライザーホールドです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "ライザーをケーブルかバンドに固定する",
          "拳と親指を最高位置まで持ち上げる",
          "手首を完全に持ち上げたまま保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "動作は親指（橈側）方向へ",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "스태틱 라이저 홀드",
        "description": "순수한 탑롤 마무리 근력을 위한 등척성 라이저 홀드입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "라이저를 케이블이나 밴드에 고정하세요",
          "관절과 엄지를 최고 위치까지 들어 올리세요",
          "손목을 완전히 든 채 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "동작은 엄지(요측) 방향입니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticRiserOverFingers110",
    "slug": "static-riser-over-fingers-110",
    "emoji": "⬆️",
    "category": "static",
    "equipmentKeys": [
      "riser",
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Static Riser Hold Over Fingers — 110°+",
        "description": "Isometric riser hold with the strap over the fingers at a wide 110°+ angle.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Loop the riser strap over your fingers at a wide 110°+ angle",
          "Raise your knuckles and thumb upward",
          "Hold the position under tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "The wider angle trains rising closer to a finishing position",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание райзера через пальцы — 110°+",
        "description": "Изометрическое удержание райзера с лямкой через пальцы под широким углом 110°+.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Проденьте лямку райзера через пальцы под широким углом 110°+",
          "Поднимайте костяшки и большой палец вверх",
          "Удерживайте позицию под напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "Более широкий угол тренирует подъём ближе к завершающей позиции",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Riser-Halten über den Fingern — 110°+",
        "description": "Isometrisches Riser-Halten mit dem Strap über den Fingern im weiten Winkel von 110°+.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Riser-Strap im weiten Winkel von 110°+ über die Finger",
          "Hebe Knöchel und Daumen nach oben",
          "Halte die Position unter Spannung"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "Der weitere Winkel trainiert Rising näher an der Abschlussposition",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien au riser sur les doigts — 110°+",
        "description": "Maintien isométrique au riser avec la sangle sur les doigts à un angle large de 110°+.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Passez la sangle du riser sur les doigts à un angle large de 110°+",
          "Levez les articulations et le pouce vers le haut",
          "Maintenez la position sous tension"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "L'angle plus large entraîne le rising plus près de la position de finition",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Riser Sobre los Dedos — 110°+",
        "description": "Sostén isométrico de riser con la correa sobre los dedos en ángulo amplio de 110°+.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Pasa la correa del riser por los dedos en ángulo amplio de 110°+",
          "Eleva los nudillos y el pulgar hacia arriba",
          "Sostén la posición bajo tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "El ángulo más amplio entrena la elevación cerca de la posición de remate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Riser Sobre os Dedos — 110°+",
        "description": "Isometria de riser com a correia sobre os dedos num ângulo amplo de 110°+.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Passa a correia do riser sobre os dedos num ângulo amplo de 110°+",
          "Eleva os nós dos dedos e o polegar para cima",
          "Mantém a posição sob tensão"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "O ângulo mais amplo treina a elevação mais perto da posição de finalização",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "指越しライザーホールド－110°+",
        "description": "指越しにストラップを通して110°+の広い角度で行うアイソメトリックライザーホールドです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "110°+の広い角度で指越しにライザーストラップを通す",
          "拳と親指を上に持ち上げる",
          "緊張下で姿勢を保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15秒保持",
        "tips": [
          "広い角度はフィニッシュ姿勢に近いライジングを鍛える",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "손가락 위 라이저 홀드 — 110°+",
        "description": "손가락 위로 스트랩을 걸어 110°+의 넓은 각도로 하는 등척성 라이저 홀드입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "110°+의 넓은 각도로 손가락 위에 라이저 스트랩을 거세요",
          "관절과 엄지를 위로 들어 올리세요",
          "긴장 상태로 자세를 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15초 유지",
        "tips": [
          "더 넓은 각도는 마무리 자세에 가까운 라이징을 훈련합니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "staticRiserOverFingers",
    "slug": "static-riser-over-fingers",
    "emoji": "⬆️",
    "category": "static",
    "equipmentKeys": [
      "riser",
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Static Riser Hold Over Fingers",
        "description": "Isometric riser hold with the strap looped over the fingers.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Loop the riser strap over your fingers",
          "Raise your knuckles and thumb upward",
          "Hold the position under tension"
        ],
        "sets": "3-4 sets",
        "reps": "10-15 seconds hold",
        "tips": [
          "Movement is toward the thumb (radial) side",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание райзера через пальцы",
        "description": "Изометрическое удержание райзера с лямкой, продетой через пальцы.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Проденьте лямку райзера через пальцы",
          "Поднимайте костяшки и большой палец вверх",
          "Удерживайте позицию под напряжением"
        ],
        "sets": "3-4 подхода",
        "reps": "10-15 секунд удержания",
        "tips": [
          "Движение в сторону большого пальца",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Riser-Halten über den Fingern",
        "description": "Isometrisches Riser-Halten mit dem über die Finger gelegten Strap.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Lege den Riser-Strap über die Finger",
          "Hebe Knöchel und Daumen nach oben",
          "Halte die Position unter Spannung"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–15 Sekunden halten",
        "tips": [
          "Die Bewegung geht zur Daumenseite (radial)",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien au riser sur les doigts",
        "description": "Maintien isométrique au riser avec la sangle passée sur les doigts.",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Passez la sangle du riser sur les doigts",
          "Levez les articulations et le pouce vers le haut",
          "Maintenez la position sous tension"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-15 secondes",
        "tips": [
          "Le mouvement se fait vers le côté du pouce (radial)",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén de Riser Sobre los Dedos",
        "description": "Sostén isométrico de riser con la correa pasada por los dedos.",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Pasa la correa del riser por los dedos",
          "Eleva los nudillos y el pulgar hacia arriba",
          "Sostén la posición bajo tensión"
        ],
        "sets": "3-4 series",
        "reps": "10-15 segundos de sostén",
        "tips": [
          "El movimiento es hacia el lado del pulgar (radial)",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Riser Sobre os Dedos",
        "description": "Isometria de riser com a correia passada sobre os dedos.",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Passa a correia do riser sobre os dedos",
          "Eleva os nós dos dedos e o polegar para cima",
          "Mantém a posição sob tensão"
        ],
        "sets": "3-4 séries",
        "reps": "10-15 segundos de isometria",
        "tips": [
          "O movimento é para o lado do polegar (radial)",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "指越しライザーホールド",
        "description": "指越しにストラップを通して行うアイソメトリックライザーホールドです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "指越しにライザーストラップを通す",
          "拳と親指を上に持ち上げる",
          "緊張下で姿勢を保持する"
        ],
        "sets": "3〜4セット",
        "reps": "10〜15秒保持",
        "tips": [
          "動作は親指（橈側）方向へ",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "손가락 위 라이저 홀드",
        "description": "손가락 위로 스트랩을 걸어 하는 등척성 라이저 홀드입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손가락 위로 라이저 스트랩을 거세요",
          "관절과 엄지를 위로 들어 올리세요",
          "긴장 상태로 자세를 유지하세요"
        ],
        "sets": "3-4세트",
        "reps": "10-15초 유지",
        "tips": [
          "동작은 엄지(요측) 방향입니다",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "larrattsLiftOverFingers",
    "slug": "larratts-lift-over-fingers",
    "emoji": "🖐️",
    "category": "dynamic",
    "equipmentKeys": [
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "Larratt's Lift (Finger Curl) — Over Fingers",
        "description": "Finger-curl lift using a weighted strap looped over just the fingers, building the exact finger strength used to cup an opponent's hand.",
        "muscles": "Finger flexors, forearm",
        "instructions": [
          "Loop the strap over your fingers only, not the full hand, with the weight hanging below",
          "Curl your fingers to lift the weight, isolating the finger flexors",
          "Lower with control, keeping the strap positioned over the fingers"
        ],
        "sets": "3 sets",
        "reps": "10-15 reps",
        "tips": [
          "Keep the strap over the fingers only — don't let it slip into a full-hand grip",
          "Builds the exact finger strength needed to cup an opponent's hand"
        ]
      },
      "ru": {
        "name": "Подъём Ларратта (сгибание пальцев) — через пальцы",
        "description": "Подъём сгибанием пальцев с использованием утяжелённого ремня, надетого только на пальцы — развивает именно ту силу пальцев, которая нужна для захвата чашкой руки соперника.",
        "muscles": "Сгибатели пальцев, предплечье",
        "instructions": [
          "Наденьте ремень только на пальцы, не на всю кисть, груз должен свободно висеть снизу",
          "Сгибайте пальцы, поднимая груз и изолируя сгибатели пальцев",
          "Опускайте с контролем, удерживая ремень на пальцах"
        ],
        "sets": "3 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Держите ремень только на пальцах — не позволяйте ему соскользнуть на всю кисть",
          "Развивает именно ту силу пальцев, которая нужна для захвата руки соперника"
        ]
      },
      "de": {
        "name": "Larratts Lift (Finger-Curl) — über die Finger",
        "description": "Finger-Curl-Hebung mit einem Gewichtsgurt, der nur über die Finger geschlungen wird — baut genau die Fingerkraft auf, die für den Cup-Griff der gegnerischen Hand nötig ist.",
        "muscles": "Fingerbeuger, Unterarm",
        "instructions": [
          "Schlinge den Gurt nur über die Finger, nicht über die ganze Hand, das Gewicht hängt frei darunter",
          "Curle die Finger, um das Gewicht zu heben, und isoliere dabei die Fingerbeuger",
          "Senke kontrolliert ab, der Gurt bleibt dabei über den Fingern"
        ],
        "sets": "3 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Halte den Gurt nur über den Fingern — lass ihn nicht zu einem Griff mit der ganzen Hand rutschen",
          "Baut genau die Fingerkraft auf, die für den Griff der gegnerischen Hand nötig ist"
        ]
      },
      "fr": {
        "name": "Levée de Larratt (flexion des doigts) — sur les doigts",
        "description": "Levée en flexion des doigts avec une sangle lestée passée uniquement sur les doigts — développe exactement la force des doigts nécessaire pour saisir la main de l'adversaire en coupe.",
        "muscles": "Fléchisseurs des doigts, avant-bras",
        "instructions": [
          "Passez la sangle uniquement sur les doigts, pas sur toute la main, le poids pendant librement en dessous",
          "Enroulez les doigts pour soulever le poids, en isolant les fléchisseurs des doigts",
          "Descendez en contrôlant, en gardant la sangle positionnée sur les doigts"
        ],
        "sets": "3 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Gardez la sangle uniquement sur les doigts — ne la laissez pas glisser vers une prise pleine main",
          "Développe exactement la force des doigts nécessaire pour saisir la main de l'adversaire"
        ]
      },
      "es": {
        "name": "Elevación de Larratt (Curl de Dedos) — Sobre los Dedos",
        "description": "Elevación con curl de dedos usando una correa con peso enlazada solo sobre los dedos — desarrolla exactamente la fuerza de dedos necesaria para ahuecar la mano del oponente.",
        "muscles": "Flexores de dedos, antebrazo",
        "instructions": [
          "Enlaza la correa solo sobre los dedos, no sobre toda la mano, dejando el peso colgando libremente debajo",
          "Curva los dedos para levantar el peso, aislando los flexores de dedos",
          "Baja con control, manteniendo la correa sobre los dedos"
        ],
        "sets": "3 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Mantén la correa solo sobre los dedos — no dejes que se deslice hacia un agarre de mano completa",
          "Desarrolla exactamente la fuerza de dedos necesaria para agarrar la mano del oponente"
        ]
      },
      "pt-BR": {
        "name": "Elevação de Larratt (Flexão de Dedos) — Sobre os Dedos",
        "description": "Elevação com flexão de dedos usando uma cinta com peso passada apenas sobre os dedos — desenvolve exatamente a força de dedos necessária para fazer cup na mão do adversário.",
        "muscles": "Flexores dos dedos, antebraço",
        "instructions": [
          "Passe a cinta apenas sobre os dedos, não sobre toda a mão, deixando o peso pendurado livremente abaixo",
          "Enrola os dedos para levantar o peso, isolando os flexores dos dedos",
          "Desce com controlo, mantendo a cinta posicionada sobre os dedos"
        ],
        "sets": "3 séries",
        "reps": "10-15 reps",
        "tips": [
          "Mantém a cinta apenas sobre os dedos — não deixes que escorregue para uma pega com a mão toda",
          "Desenvolve exatamente a força de dedos necessária para agarrar a mão do adversário"
        ]
      },
      "ja": {
        "name": "ラレットリフト（フィンガーカール）－指越し",
        "description": "指の上だけにベルトをかけたウェイト付きストラップを使うフィンガーカールリフトで、相手の手をカップするために必要な正確な指の力を鍛えます。",
        "muscles": "指屈筋群、前腕",
        "instructions": [
          "手全体ではなく指の上だけにストラップをかけ、ウェイトを下に自由に垂らす",
          "指を曲げてウェイトを持ち上げ、指屈筋群を分離して使う",
          "ストラップを指の上に保ったまま、制御して下ろす"
        ],
        "sets": "3セット",
        "reps": "10〜15レップ",
        "tips": [
          "ストラップを指の上だけに保ち、手全体を使うグリップに滑り込ませない",
          "相手の手を握るのに必要な正確な指の力を鍛える"
        ]
      },
      "ko": {
        "name": "라렛 리프트 (핑거 컬) — 손가락 위",
        "description": "무게가 달린 스트랩을 손가락 위에만 걸어서 하는 핑거 컬 리프트로, 상대의 손을 컵으로 잡는 데 필요한 정확한 손가락 힘을 기릅니다.",
        "muscles": "손가락 굴근, 전완",
        "instructions": [
          "손 전체가 아닌 손가락 위에만 스트랩을 걸고, 무게가 아래로 자유롭게 매달리게 하세요",
          "손가락을 감아 무게를 들어 올리며 손가락 굴근을 고립시키세요",
          "스트랩을 손가락 위에 유지한 채 통제하여 내리세요"
        ],
        "sets": "3세트",
        "reps": "10-15회",
        "tips": [
          "스트랩을 손가락 위에만 유지하세요 — 손 전체를 감싸는 그립으로 미끄러지지 않게 하세요",
          "상대의 손을 잡는 데 필요한 정확한 손가락 힘을 기릅니다"
        ]
      }
    }
  },
  {
    "id": "larrattsLift",
    "slug": "larratts-lift",
    "emoji": "🖐️",
    "category": "dynamic",
    "equipmentKeys": [
      "strap"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "Larratt's Lift (Finger Curl)",
        "description": "Finger-curl lift using a weighted strap looped through the hand, building raw finger and grip strength for a strong cup.",
        "muscles": "Finger flexors, forearm",
        "instructions": [
          "Loop the strap through your hand with the weight hanging below",
          "Curl your fingers to lift the weight, keeping the wrist stable",
          "Lower with control back to the starting position"
        ],
        "sets": "3 sets",
        "reps": "10-15 reps",
        "tips": [
          "Keep the wrist locked and let the fingers do the lifting",
          "Builds crucial grip strength for hand control"
        ]
      },
      "ru": {
        "name": "Подъём Ларратта (сгибание пальцев)",
        "description": "Подъём сгибанием пальцев с использованием утяжелённого ремня, надетого на кисть — развивает базовую силу пальцев и хвата для мощной чашки.",
        "muscles": "Сгибатели пальцев, предплечье",
        "instructions": [
          "Наденьте ремень на кисть, груз должен свободно висеть снизу",
          "Сгибайте пальцы, поднимая груз, удерживая запястье неподвижным",
          "Опускайте с контролем в исходное положение"
        ],
        "sets": "3 подхода",
        "reps": "10-15 повторений",
        "tips": [
          "Держите запястье зафиксированным — подъём выполняют только пальцы",
          "Развивает критическую силу хвата для контроля руки"
        ]
      },
      "de": {
        "name": "Larratts Lift (Finger-Curl)",
        "description": "Finger-Curl-Hebung mit einem Gewichtsgurt, der durch die Hand geschlungen wird — baut grundlegende Finger- und Griffkraft für einen starken Cup auf.",
        "muscles": "Fingerbeuger, Unterarm",
        "instructions": [
          "Schlinge den Gurt durch die Hand, das Gewicht hängt frei darunter",
          "Curle die Finger, um das Gewicht zu heben, das Handgelenk bleibt dabei stabil",
          "Senke kontrolliert zurück in die Ausgangsposition ab"
        ],
        "sets": "3 Sätze",
        "reps": "10–15 Wdh.",
        "tips": [
          "Halte das Handgelenk fixiert — die Finger übernehmen die Hebearbeit",
          "Baut entscheidende Griffkraft für die Handkontrolle auf"
        ]
      },
      "fr": {
        "name": "Levée de Larratt (flexion des doigts)",
        "description": "Levée en flexion des doigts avec une sangle lestée passée à travers la main — développe la force de base des doigts et de préhension pour une coupe puissante.",
        "muscles": "Fléchisseurs des doigts, avant-bras",
        "instructions": [
          "Passez la sangle à travers la main, le poids pendant librement en dessous",
          "Enroulez les doigts pour soulever le poids, en gardant le poignet stable",
          "Descendez en contrôlant jusqu'à la position de départ"
        ],
        "sets": "3 séries",
        "reps": "10-15 répétitions",
        "tips": [
          "Gardez le poignet verrouillé — ce sont les doigts qui effectuent le mouvement",
          "Développe une force de préhension cruciale pour le contrôle de la main"
        ]
      },
      "es": {
        "name": "Elevación de Larratt (Curl de Dedos)",
        "description": "Elevación con curl de dedos usando una correa con peso enlazada a través de la mano — desarrolla la fuerza básica de dedos y agarre para un ahuecado potente.",
        "muscles": "Flexores de dedos, antebrazo",
        "instructions": [
          "Enlaza la correa a través de la mano, dejando el peso colgando libremente debajo",
          "Curva los dedos para levantar el peso, manteniendo la muñeca estable",
          "Baja con control hasta la posición inicial"
        ],
        "sets": "3 series",
        "reps": "10-15 repeticiones",
        "tips": [
          "Mantén la muñeca fija — deja que los dedos hagan el trabajo de elevación",
          "Desarrolla la fuerza de agarre crucial para el control de la mano"
        ]
      },
      "pt-BR": {
        "name": "Elevação de Larratt (Flexão de Dedos)",
        "description": "Elevação com flexão de dedos usando uma cinta com peso passada através da mão — desenvolve a força básica dos dedos e da pega para um cup poderoso.",
        "muscles": "Flexores dos dedos, antebraço",
        "instructions": [
          "Passe a cinta através da mão, deixando o peso pendurado livremente abaixo",
          "Enrola os dedos para levantar o peso, mantendo o pulso estável",
          "Desce com controlo até à posição inicial"
        ],
        "sets": "3 séries",
        "reps": "10-15 reps",
        "tips": [
          "Mantém o pulso travado — são os dedos que fazem o trabalho de elevação",
          "Desenvolve força de pega crucial para o controlo de mão"
        ]
      },
      "ja": {
        "name": "ラレットリフト（フィンガーカール）",
        "description": "手を通してウェイト付きストラップをかけるフィンガーカールリフトで、強力なカップのための基本的な指とグリップの力を鍛えます。",
        "muscles": "指屈筋群、前腕",
        "instructions": [
          "ストラップを手に通し、ウェイトを下に自由に垂らす",
          "手首を安定させたまま、指を曲げてウェイトを持ち上げる",
          "制御しながら開始位置まで下ろす"
        ],
        "sets": "3セット",
        "reps": "10〜15レップ",
        "tips": [
          "手首をロックしたまま保ち、指だけで持ち上げる",
          "ハンドコントロールに重要なグリップ力を鍛える"
        ]
      },
      "ko": {
        "name": "라렛 리프트 (핑거 컬)",
        "description": "무게가 달린 스트랩을 손에 걸어서 하는 핑거 컬 리프트로, 강력한 컵을 위한 기본 손가락과 그립 힘을 기릅니다.",
        "muscles": "손가락 굴근, 전완",
        "instructions": [
          "스트랩을 손에 걸고, 무게가 아래로 자유롭게 매달리게 하세요",
          "손목을 안정적으로 유지하며 손가락을 감아 무게를 들어 올리세요",
          "통제하며 시작 자세로 내리세요"
        ],
        "sets": "3세트",
        "reps": "10-15회",
        "tips": [
          "손목을 고정하고 손가락만으로 들어 올리세요",
          "손 컨트롤에 중요한 그립 힘을 키웁니다"
        ]
      }
    }
  },
  {
    "id": "twoByTwoHandleFingerHold",
    "slug": "two-by-two-handle-finger-hold",
    "emoji": "🤏",
    "category": "static",
    "equipmentKeys": [
      "gripper"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "2x2 Handle Finger Containment Hold",
        "description": "Isometric finger-containment hold on a thick 2x2 handle, building the crush strength needed to trap an opponent's fingers.",
        "muscles": "Finger flexors, forearm",
        "instructions": [
          "Grip the 2x2 handle with fingers wrapped fully around it",
          "Squeeze and hold, keeping fingers locked in place",
          "Release slowly"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Try different hand positions to hit the grip from multiple angles",
          "Builds crush grip strength for containing an opponent's fingers"
        ]
      },
      "ru": {
        "name": "Удержание рукояти 2x2 пальцами",
        "description": "Изометрическое удержание пальцами толстой рукояти 2x2, развивающее сдавливающую силу для удержания пальцев соперника.",
        "muscles": "Сгибатели пальцев, предплечье",
        "instructions": [
          "Обхватите рукоять 2x2 пальцами полностью вокруг неё",
          "Сожмите и удерживайте, держа пальцы зафиксированными",
          "Медленно отпустите"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Попробуйте разные положения руки, чтобы проработать хват под разными углами",
          "Развивает сдавливающую силу хвата для удержания пальцев соперника"
        ]
      },
      "de": {
        "name": "2x2-Griff-Fingerhalten",
        "description": "Isometrisches Fingerhalten an einem dicken 2x2-Griff, baut die Crush-Kraft auf, um die Finger des Gegners festzuhalten.",
        "muscles": "Fingerbeuger, Unterarm",
        "instructions": [
          "Umfasse den 2x2-Griff vollständig mit den Fingern",
          "Drücke zusammen und halte, Finger bleiben fixiert",
          "Löse langsam"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Probiere verschiedene Handpositionen, um den Griff aus mehreren Winkeln zu trainieren",
          "Baut Crush-Griffkraft zum Festhalten der gegnerischen Finger auf"
        ]
      },
      "fr": {
        "name": "Maintien des doigts sur poignée 2x2",
        "description": "Maintien isométrique des doigts sur une épaisse poignée 2x2, développe la force d'écrasement pour retenir les doigts de l'adversaire.",
        "muscles": "Fléchisseurs des doigts, avant-bras",
        "instructions": [
          "Enroulez complètement les doigts autour de la poignée 2x2",
          "Serrez et maintenez, en gardant les doigts fixes",
          "Relâchez lentement"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Essayez différentes positions de main pour travailler la prise sous plusieurs angles",
          "Développe la force d'écrasement pour retenir les doigts de l'adversaire"
        ]
      },
      "es": {
        "name": "Sostén de Dedos en Asa 2x2",
        "description": "Sostén isométrico de dedos en un asa gruesa 2x2, desarrolla la fuerza de aplastamiento para contener los dedos del oponente.",
        "muscles": "Flexores de dedos, antebrazo",
        "instructions": [
          "Rodea completamente el asa 2x2 con los dedos",
          "Aprieta y sostén, manteniendo los dedos fijos",
          "Suelta lentamente"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "Prueba diferentes posiciones de mano para trabajar el agarre desde varios ángulos",
          "Desarrolla la fuerza de agarre por aplastamiento para contener los dedos del oponente"
        ]
      },
      "pt-BR": {
        "name": "Isometria de Dedos na Pega 2x2",
        "description": "Isometria de dedos numa pega grossa 2x2, desenvolve a força de esmagamento para conter os dedos do adversário.",
        "muscles": "Flexores dos dedos, antebraço",
        "instructions": [
          "Envolve completamente a pega 2x2 com os dedos",
          "Aperta e mantém, com os dedos fixos",
          "Solta lentamente"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Experimenta diferentes posições de mão para trabalhar a pega em vários ângulos",
          "Desenvolve força de pega de esmagamento para conter os dedos do adversário"
        ]
      },
      "ja": {
        "name": "2x2ハンドルフィンガーホールド",
        "description": "太い2x2ハンドルでのアイソメトリックフィンガーホールドで、相手の指を抑えるためのクラッシュ力を鍛えます。",
        "muscles": "指屈筋群、前腕",
        "instructions": [
          "2x2ハンドルを指で完全に包み込む",
          "握って保持し、指を固定したままにする",
          "ゆっくり離す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "さまざまな角度でグリップを鍛えるため異なる手の位置を試す",
          "相手の指を抑えるためのクラッシュグリップ力を鍛える"
        ]
      },
      "ko": {
        "name": "2x2 핸들 핑거 홀드",
        "description": "두꺼운 2x2 핸들을 사용하는 등척성 핑거 홀드로, 상대의 손가락을 제압하는 데 필요한 크러시 힘을 기릅니다.",
        "muscles": "손가락 굴근, 전완",
        "instructions": [
          "2x2 핸들을 손가락으로 완전히 감싸세요",
          "손가락을 고정한 채 쥐고 유지하세요",
          "천천히 풀어 주세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "여러 각도에서 그립을 훈련하기 위해 다양한 손 위치를 시도하세요",
          "상대의 손가락을 제압하는 크러시 그립 힘을 기릅니다"
        ]
      }
    }
  },
  {
    "id": "multispinnerWristFingerDrill",
    "slug": "multispinner-wrist-finger-drill",
    "emoji": "🌀",
    "category": "dynamic",
    "equipmentKeys": [
      "multispinner",
      "cable"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "Multispinner Wrist & Finger Drill",
        "description": "Rolling wrist-and-finger drill on a multispinner handle, training cupping power through a full range of wrist flexion angles.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Attach the multispinner to a cable or band",
          "Curl the wrist so the handle rolls out of your hand",
          "Reset your grip and repeat"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Let the handle actively roll out of your hand during the flexion",
          "Builds crucial grip strength for hand control"
        ]
      },
      "ru": {
        "name": "Упражнение на мультиспиннере для кисти и пальцев",
        "description": "Раскручивающее упражнение для кисти и пальцев на рукояти мультиспиннера, развивающее силу чашки по всей амплитуде сгибания кисти.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Закрепите мультиспиннер на блоке или резинке",
          "Сгибайте кисть так, чтобы рукоять раскручивалась из руки",
          "Восстановите хват и повторите"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Позвольте рукояти активно раскрутиться из руки во время сгибания",
          "Развивает критическую силу хвата для контроля руки"
        ]
      },
      "de": {
        "name": "Multispinner-Übung für Handgelenk und Finger",
        "description": "Abroll-Übung für Handgelenk und Finger am Multispinner-Griff, trainiert Cupping-Kraft über den vollen Bereich der Handgelenkbeugung.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Befestige den Multispinner an Kabelzug oder Band",
          "Curle das Handgelenk, sodass sich der Griff aus der Hand abrollt",
          "Stelle den Griff neu ein und wiederhole"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Lass den Griff sich während der Beugung aktiv aus der Hand abrollen",
          "Baut entscheidende Griffkraft für die Handkontrolle auf"
        ]
      },
      "fr": {
        "name": "Exercice poignet et doigts au multispinner",
        "description": "Exercice de déroulement pour poignet et doigts sur une poignée multispinner, entraîne la force de cupping sur toute l'amplitude de flexion du poignet.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Fixez le multispinner à une poulie ou un élastique",
          "Enroulez le poignet de sorte que la poignée se déroule de la main",
          "Rétablissez la prise et répétez"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Laissez la poignée se dérouler activement de la main pendant la flexion",
          "Développe une force de préhension cruciale pour le contrôle de la main"
        ]
      },
      "es": {
        "name": "Ejercicio de Muñeca y Dedos en Multispinner",
        "description": "Ejercicio de desenrollado para muñeca y dedos en un asa multispinner, entrena la fuerza de ahuecado en todo el rango de flexión de muñeca.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Fija el multispinner a una polea o banda",
          "Curva la muñeca de modo que el asa se desenrolle de la mano",
          "Restablece el agarre y repite"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "Deja que el asa se desenrolle activamente de la mano durante la flexión",
          "Desarrolla la fuerza de agarre crucial para el control de la mano"
        ]
      },
      "pt-BR": {
        "name": "Exercício de Punho e Dedos no Multispinner",
        "description": "Exercício de desenrolar para punho e dedos numa pega multispinner, treina a força de cupping em toda a amplitude de flexão do punho.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Fixa o multispinner a um cabo ou banda",
          "Enrola o punho de forma que a pega se desenrole da mão",
          "Reajusta a pega e repete"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "Deixa a pega desenrolar-se ativamente da mão durante a flexão",
          "Desenvolve força de pega crucial para o controlo de mão"
        ]
      },
      "ja": {
        "name": "マルチスピナー手首・指ドリル",
        "description": "マルチスピナーハンドルでの手首と指の展開エクササイズで、手首屈曲の全可動域でカッピング力を鍛えます。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "マルチスピナーをケーブルかバンドに取り付ける",
          "ハンドルが手から巻き解けるように手首を巻き込む",
          "グリップをリセットして繰り返す"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "屈曲中にハンドルが手から積極的に巻き解けるようにする",
          "ハンドコントロールに重要なグリップ力を鍛える"
        ]
      },
      "ko": {
        "name": "멀티스피너 손목 및 손가락 드릴",
        "description": "멀티스피너 핸들로 하는 손목과 손가락 풀림 운동으로, 손목 굴곡의 전체 범위에서 커핑 힘을 훈련합니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "멀티스피너를 케이블이나 밴드에 연결하세요",
          "핸들이 손에서 풀려나가도록 손목을 감으세요",
          "그립을 재설정하고 반복하세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "굴곡 중에 핸들이 손에서 적극적으로 풀려나가게 하세요",
          "손 컨트롤에 중요한 그립 힘을 키웁니다"
        ]
      }
    }
  },
  {
    "id": "kettlebellStaticHold",
    "slug": "kettlebell-static-hold",
    "emoji": "🏋️",
    "category": "static",
    "equipmentKeys": [
      "kettlebell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "cupping"
    ],
    "armGroup": "grip",
    "translations": {
      "en": {
        "name": "Kettlebell Static Hold",
        "description": "Isometric hold gripping a kettlebell in the hook/cupping position, building raw static grip and wrist-flexor strength.",
        "muscles": "Wrist flexors, forearm",
        "instructions": [
          "Grip the kettlebell handle in your cupping/hook position",
          "Hold with the wrist curled and fingers locked",
          "Release slowly"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Mimic your actual match hand position as closely as possible",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Статическое удержание гири",
        "description": "Изометрическое удержание гири в позиции крюка/чашки, развивающее чистую статическую силу хвата и сгибателей кисти.",
        "muscles": "Сгибатели кисти, предплечье",
        "instructions": [
          "Возьмитесь за рукоять гири в позиции чашки/крюка",
          "Удерживайте с согнутой кистью и заблокированными пальцами",
          "Медленно отпустите"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Максимально имитируйте вашу реальную позицию руки в поединке",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Statisches Kettlebell-Halten",
        "description": "Isometrisches Halten einer Kettlebell in Hook-/Cupping-Position, baut reine statische Griff- und Beugerkraft auf.",
        "muscles": "Handgelenkbeuger, Unterarm",
        "instructions": [
          "Greife den Kettlebell-Griff in deiner Cupping-/Hook-Position",
          "Halte mit gebeugtem Handgelenk und fixierten Fingern",
          "Löse langsam"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Ahme deine tatsächliche Match-Handposition so genau wie möglich nach",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien statique au kettlebell",
        "description": "Maintien isométrique d'un kettlebell en position de hook/cupping, développe la force statique pure de préhension et des fléchisseurs du poignet.",
        "muscles": "Fléchisseurs du poignet, avant-bras",
        "instructions": [
          "Saisissez la poignée du kettlebell dans votre position de cupping/hook",
          "Maintenez avec le poignet enroulé et les doigts verrouillés",
          "Relâchez lentement"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Imitez au mieux votre position de main réelle en match",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén Estático con Pesa Rusa",
        "description": "Sostén isométrico de una pesa rusa en posición de gancho/ahuecado, desarrolla pura fuerza estática de agarre y flexores de muñeca.",
        "muscles": "Flexores de muñeca, antebrazo",
        "instructions": [
          "Agarra el asa de la pesa rusa en tu posición de ahuecado/gancho",
          "Sostén con la muñeca curvada y los dedos fijos",
          "Suelta lentamente"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "Imita al máximo la posición real de tu mano en el combate",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria Estática com Kettlebell",
        "description": "Isometria segurando uma kettlebell na posição de hook/cupping, desenvolve força estática pura de pega e dos flexores do punho.",
        "muscles": "Flexores do punho, antebraço",
        "instructions": [
          "Agarra a pega da kettlebell na tua posição de cupping/hook",
          "Mantém com o punho enrolado e os dedos bloqueados",
          "Solta lentamente"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Imita ao máximo a tua posição de mão real em combate",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "ケトルベルスタティックホールド",
        "description": "フック/カッピング姿勢でケトルベルを保持するアイソメトリックホールドで、純粋なスタティックグリップ力と手首屈筋力を鍛えます。",
        "muscles": "手首屈筋群、前腕",
        "instructions": [
          "カッピング/フック姿勢でケトルベルのハンドルを握る",
          "手首を巻き込み指をロックしたまま保持する",
          "ゆっくり離す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "実際の試合の手の姿勢をできる限り模す",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "케틀벨 스태틱 홀드",
        "description": "훅/커핑 자세로 케틀벨을 잡는 등척성 홀드로, 순수한 스태틱 그립력과 손목 굴근력을 기릅니다.",
        "muscles": "손목 굴근, 전완",
        "instructions": [
          "커핑/훅 자세로 케틀벨 손잡이를 잡으세요",
          "손목을 감고 손가락을 고정한 채 유지하세요",
          "천천히 풀어 주세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "실제 시합의 손 자세를 최대한 모방하세요",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "kettlebellReverseGripHold",
    "slug": "kettlebell-reverse-grip-hold",
    "emoji": "🏋️",
    "category": "static",
    "equipmentKeys": [
      "kettlebell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "wristExtensors",
    "translations": {
      "en": {
        "name": "Kettlebell Reverse Grip Hold",
        "description": "Isometric hold gripping a kettlebell in a reverse (extended-wrist) grip.",
        "muscles": "Wrist extensors, forearm",
        "instructions": [
          "Grip the kettlebell handle with your wrist extended",
          "Hold the position without letting the wrist drop",
          "Release slowly"
        ],
        "sets": "3-4 sets",
        "reps": "10-20 seconds hold",
        "tips": [
          "Use lighter weight than a regular grip hold to start",
          "Breathe steadily — don't hold your breath through the hold"
        ]
      },
      "ru": {
        "name": "Удержание гири обратным хватом",
        "description": "Изометрическое удержание гири обратным (разогнутым) хватом.",
        "muscles": "Разгибатели кисти, предплечье",
        "instructions": [
          "Возьмитесь за рукоять гири с разогнутой кистью",
          "Удерживайте позицию, не позволяя кисти опуститься",
          "Медленно отпустите"
        ],
        "sets": "3-4 подхода",
        "reps": "10-20 секунд удержания",
        "tips": [
          "Начните с меньшего веса, чем в обычном удержании хвата",
          "Дышите ровно — не задерживайте дыхание во время удержания"
        ]
      },
      "de": {
        "name": "Kettlebell-Halten mit umgekehrtem Griff",
        "description": "Isometrisches Halten einer Kettlebell mit umgekehrtem (gestrecktem) Griff.",
        "muscles": "Handgelenkstrecker, Unterarm",
        "instructions": [
          "Greife den Kettlebell-Griff mit gestrecktem Handgelenk",
          "Halte die Position, ohne dass das Handgelenk absinkt",
          "Löse langsam"
        ],
        "sets": "3–4 Sätze",
        "reps": "10–20 Sekunden halten",
        "tips": [
          "Starte mit weniger Gewicht als bei einem normalen Griffhalten",
          "Atme gleichmäßig – halte während des Haltens nicht die Luft an"
        ]
      },
      "fr": {
        "name": "Maintien au kettlebell en prise inversée",
        "description": "Maintien isométrique d'un kettlebell avec une prise inversée (poignet en extension).",
        "muscles": "Extenseurs du poignet, avant-bras",
        "instructions": [
          "Saisissez la poignée du kettlebell avec le poignet en extension",
          "Maintenez la position sans laisser le poignet retomber",
          "Relâchez lentement"
        ],
        "sets": "3-4 séries",
        "reps": "Maintien de 10-20 secondes",
        "tips": [
          "Commencez avec moins de poids que pour un maintien de prise classique",
          "Respirez régulièrement — ne bloquez pas votre respiration pendant le maintien"
        ]
      },
      "es": {
        "name": "Sostén con Pesa Rusa en Agarre Inverso",
        "description": "Sostén isométrico de una pesa rusa con agarre inverso (muñeca extendida).",
        "muscles": "Extensores de muñeca, antebrazo",
        "instructions": [
          "Agarra el asa de la pesa rusa con la muñeca extendida",
          "Sostén la posición sin dejar que la muñeca caiga",
          "Suelta lentamente"
        ],
        "sets": "3-4 series",
        "reps": "10-20 segundos de sostén",
        "tips": [
          "Empieza con menos peso que en un sostén de agarre normal",
          "Respira de forma constante — no aguantes la respiración durante el sostén"
        ]
      },
      "pt-BR": {
        "name": "Isometria com Kettlebell em Pega Invertida",
        "description": "Isometria segurando uma kettlebell com pega invertida (punho em extensão).",
        "muscles": "Extensores do punho, antebraço",
        "instructions": [
          "Agarra a pega da kettlebell com o punho em extensão",
          "Mantém a posição sem deixar o punho cair",
          "Solta lentamente"
        ],
        "sets": "3-4 séries",
        "reps": "10-20 segundos de isometria",
        "tips": [
          "Começa com menos peso do que numa isometria de pega normal",
          "Respira de forma constante — não prendas a respiração durante a isometria"
        ]
      },
      "ja": {
        "name": "ケトルベルリバースグリップホールド",
        "description": "リバース（手首伸展）グリップでケトルベルを保持するアイソメトリックホールドです。",
        "muscles": "手首伸筋群、前腕",
        "instructions": [
          "手首を伸展させてケトルベルのハンドルを握る",
          "手首を落とさずに姿勢を保持する",
          "ゆっくり離す"
        ],
        "sets": "3〜4セット",
        "reps": "10〜20秒保持",
        "tips": [
          "通常のグリップホールドより軽い重量から始める",
          "呼吸を一定に保つ－保持中に息を止めない"
        ]
      },
      "ko": {
        "name": "케틀벨 리버스 그립 홀드",
        "description": "리버스(손목 신전) 그립으로 케틀벨을 잡는 등척성 홀드입니다.",
        "muscles": "손목 신근, 전완",
        "instructions": [
          "손목을 신전한 채 케틀벨 손잡이를 잡으세요",
          "손목이 내려가지 않게 자세를 유지하세요",
          "천천히 풀어 주세요"
        ],
        "sets": "3-4세트",
        "reps": "10-20초 유지",
        "tips": [
          "일반 그립 홀드보다 가벼운 무게로 시작하세요",
          "호흡을 일정하게 하세요 - 홀드 중 숨을 참지 마세요"
        ]
      }
    }
  },
  {
    "id": "bicepCurls",
    "slug": "bicep-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "dumbbell",
      "barbell"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Bicep Curls",
        "description": "Classic bicep curl to build the raw pulling and hook strength that biceps contribute to armwrestling.",
        "muscles": "Biceps, forearm",
        "instructions": [
          "Stand holding dumbbells or a barbell at your sides",
          "Curl the weight up, keeping elbows tucked",
          "Lower with control, no swinging"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "No swinging — strict form only",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Сгибания на бицепс",
        "description": "Классическое сгибание на бицепс для развития базовой тяговой силы и силы крюка.",
        "muscles": "Бицепс, предплечье",
        "instructions": [
          "Встаньте, держа гантели или штангу по бокам",
          "Поднимите вес, держа локти прижатыми",
          "Опускайте с контролем, без раскачивания"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Без раскачивания — только строгая техника",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Bizeps-Curls",
        "description": "Klassischer Bizeps-Curl zum Aufbau grundlegender Zug- und Hook-Kraft.",
        "muscles": "Bizeps, Unterarm",
        "instructions": [
          "Stehe mit Kurzhanteln oder Langhantel an den Seiten",
          "Curle das Gewicht hoch, Ellbogen bleiben eng am Körper",
          "Senke kontrolliert ab, kein Schwung"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Kein Schwung – nur strenge Ausführung",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Curls biceps",
        "description": "Curl biceps classique pour développer la force de traction et de hook de base.",
        "muscles": "Biceps, avant-bras",
        "instructions": [
          "Debout, haltères ou barre le long du corps",
          "Enroulez le poids vers le haut, coudes serrés",
          "Descendez en contrôlant, sans balancement"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Pas de balancement - forme stricte uniquement",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Curl de Bíceps",
        "description": "Curl de bíceps clásico para desarrollar fuerza básica de tracción y de gancho.",
        "muscles": "Bíceps, antebrazo",
        "instructions": [
          "Ponte de pie sujetando mancuernas o barra a los lados",
          "Eleva el peso manteniendo los codos pegados",
          "Baja con control, sin balanceo"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "Sin balanceo — solo forma estricta",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Rosca Bíceps",
        "description": "Rosca de bíceps clássica para desenvolver força bruta de puxada e de hook.",
        "muscles": "Bíceps, antebraço",
        "instructions": [
          "Fica de pé segurando halteres ou barra ao lado do corpo",
          "Enrola o peso mantendo os cotovelos junto ao corpo",
          "Desce com controlo, sem balançar"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "Sem balanços — apenas forma rigorosa",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "バイセップカール",
        "description": "基本的な引く力とフック力を鍛えるクラシックなバイセップカールです。",
        "muscles": "上腕二頭筋、前腕",
        "instructions": [
          "ダンベルかバーベルを体の横に持って立つ",
          "肘を締めたまま重量を巻き上げる",
          "反動を使わず制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "反動を使わず、厳格なフォームのみ",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "바이셉 컬",
        "description": "기본적인 당기기와 훅 힘을 기르는 클래식 바이셉 컬입니다.",
        "muscles": "이두근, 전완",
        "instructions": [
          "덤벨이나 바벨을 옆에 들고 서세요",
          "팔꿈치를 붙인 채 무게를 감아 올리세요",
          "반동 없이 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "반동 금지 - 엄격한 자세만 사용하세요",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "ezBarHammerCurls",
    "slug": "ez-bar-hammer-curls",
    "emoji": "🔨",
    "category": "dynamic",
    "equipmentKeys": [
      "ezBar"
    ],
    "difficulty": "beginner",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook",
      "toproll"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "EZ-Bar Hammer Curls",
        "description": "Hammer curl variation using an EZ-bar for a more wrist-friendly neutral-ish grip.",
        "muscles": "Brachioradialis, biceps, forearm",
        "instructions": [
          "Hold the EZ-bar on its angled grips, palms facing each other",
          "Curl the weight up keeping the grip neutral throughout",
          "Lower with control, no swinging"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "Keep the neutral grip throughout the movement",
          "No swinging - strict form only"
        ]
      },
      "ru": {
        "name": "Молотковые сгибания с EZ-грифом",
        "description": "Вариант молоткового сгибания с EZ-грифом для более щадящего для запястий почти нейтрального хвата.",
        "muscles": "Плечелучевая мышца, бицепс, предплечье",
        "instructions": [
          "Держите EZ-гриф за изогнутые ручки, ладони обращены друг к другу",
          "Поднимайте вес, сохраняя нейтральный хват на протяжении",
          "Опускайте с контролем, без раскачивания"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Сохраняйте нейтральный хват на протяжении всего движения",
          "Без раскачивания - только строгая техника"
        ]
      },
      "de": {
        "name": "Hammer-Curls mit SZ-Stange",
        "description": "Hammer-Curl-Variante mit SZ-Stange für einen handgelenkschonenderen, fast neutralen Griff.",
        "muscles": "Brachioradialis, Bizeps, Unterarm",
        "instructions": [
          "Halte die SZ-Stange an den abgewinkelten Griffen, Handflächen zueinander",
          "Curle das Gewicht hoch, Griff bleibt durchgehend neutral",
          "Senke kontrolliert ab, kein Schwung"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Halte den neutralen Griff über die gesamte Bewegung",
          "Kein Schwung – nur strenge Ausführung"
        ]
      },
      "fr": {
        "name": "Curls marteau à la barre EZ",
        "description": "Variante de curl marteau avec une barre EZ pour une prise presque neutre plus douce pour les poignets.",
        "muscles": "Brachioradial, biceps, avant-bras",
        "instructions": [
          "Tenez la barre EZ sur ses prises angulées, paumes face à face",
          "Enroulez le poids vers le haut en gardant la prise neutre tout du long",
          "Descendez en contrôlant, sans balancement"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Gardez la prise neutre tout au long du mouvement",
          "Pas de balancement - forme stricte uniquement"
        ]
      },
      "es": {
        "name": "Curl Martillo con Barra EZ",
        "description": "Variante de curl martillo con barra EZ para un agarre casi neutro más suave con las muñecas.",
        "muscles": "Braquiorradial, bíceps, antebrazo",
        "instructions": [
          "Sujeta la barra EZ por los agarres angulados, palmas enfrentadas",
          "Eleva el peso manteniendo el agarre neutro en todo momento",
          "Baja con control, sin balanceo"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "Mantén el agarre neutro durante todo el movimiento",
          "Sin balanceo, solo forma estricta"
        ]
      },
      "pt-BR": {
        "name": "Rosca Martelo com Barra EZ",
        "description": "Variante da rosca martelo com barra EZ para uma pega quase neutra mais suave para os punhos.",
        "muscles": "Braquiorradial, bíceps, antebraço",
        "instructions": [
          "Segura a barra EZ nas pegas angulares, palmas viradas uma para a outra",
          "Enrola o peso mantendo a pega neutra do início ao fim",
          "Desce com controlo, sem balançar"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "Mantém a pega neutra durante todo o movimento",
          "Sem balanços - apenas forma rigorosa"
        ]
      },
      "ja": {
        "name": "EZバーハンマーカール",
        "description": "手首に優しいほぼニュートラルなグリップのためのEZバーを使ったハンマーカールのバリエーションです。",
        "muscles": "腕橈骨筋、上腕二頭筋、前腕",
        "instructions": [
          "EZバーの角度付きグリップを手のひらが向かい合うように持つ",
          "常にニュートラルグリップを保ったまま重量を巻き上げる",
          "反動を使わず制御して下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "動作全体を通してニュートラルグリップを保つ",
          "反動を使わず、厳格なフォームのみ"
        ]
      },
      "ko": {
        "name": "EZ바 해머 컬",
        "description": "손목에 더 편안한 거의 중립적인 그립을 위해 EZ바를 사용하는 해머 컬 변형입니다.",
        "muscles": "상완요골근, 이두근, 전완",
        "instructions": [
          "EZ바의 각진 그립을 손바닥이 마주보게 잡으세요",
          "내내 중립 그립을 유지하며 무게를 감아 올리세요",
          "반동 없이 통제하여 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "동작 내내 중립 그립을 유지하세요",
          "반동 금지 - 엄격한 자세만 사용하세요"
        ]
      }
    }
  },
  {
    "id": "reverseGripBicepCurls",
    "slug": "reverse-grip-bicep-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "barbell"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "toproll"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Reverse-Grip Bicep Curls",
        "description": "Bicep curl with a reverse (pronated) grip, building brachioradialis and top-roll pulling strength.",
        "muscles": "Brachioradialis, biceps, forearm",
        "instructions": [
          "Hold a barbell with palms facing down (pronated)",
          "Curl the weight up keeping wrists straight",
          "Lower with control"
        ],
        "sets": "3 sets",
        "reps": "10-12 reps",
        "tips": [
          "Use lighter weight than a standard bicep curl to start",
          "No swinging - strict form only"
        ]
      },
      "ru": {
        "name": "Сгибания на бицепс обратным хватом",
        "description": "Сгибание на бицепс обратным (пронированным) хватом, развивающее плечелучевую мышцу и тяговую силу для верхнего.",
        "muscles": "Плечелучевая мышца, бицепс, предплечье",
        "instructions": [
          "Держите штангу ладонями вниз (пронация)",
          "Поднимайте вес, удерживая кисти прямыми",
          "Опускайте с контролем"
        ],
        "sets": "3 подхода",
        "reps": "10-12 повторений",
        "tips": [
          "Начните с меньшего веса, чем в обычном сгибании на бицепс",
          "Без раскачивания - только строгая техника"
        ]
      },
      "de": {
        "name": "Bizeps-Curls mit umgekehrtem Griff",
        "description": "Bizeps-Curl mit umgekehrtem (proniertem) Griff, baut Brachioradialis und Zugkraft für den Top Roll auf.",
        "muscles": "Brachioradialis, Bizeps, Unterarm",
        "instructions": [
          "Halte die Langhantel mit Handflächen nach unten (proniert)",
          "Curle das Gewicht hoch, Handgelenke bleiben gerade",
          "Senke kontrolliert ab"
        ],
        "sets": "3 Sätze",
        "reps": "10–12 Wdh.",
        "tips": [
          "Starte mit weniger Gewicht als bei einem normalen Bizeps-Curl",
          "Kein Schwung – nur strenge Ausführung"
        ]
      },
      "fr": {
        "name": "Curls biceps en prise inversée",
        "description": "Curl biceps avec une prise inversée (pronée), développe le brachioradial et la force de traction pour le top roll.",
        "muscles": "Brachioradial, biceps, avant-bras",
        "instructions": [
          "Tenez une barre avec les paumes vers le bas (pronation)",
          "Enroulez le poids vers le haut en gardant les poignets droits",
          "Descendez en contrôlant"
        ],
        "sets": "3 séries",
        "reps": "10-12 répétitions",
        "tips": [
          "Commencez avec moins de poids que pour un curl biceps classique",
          "Pas de balancement - forme stricte uniquement"
        ]
      },
      "es": {
        "name": "Curl de Bíceps con Agarre Inverso",
        "description": "Curl de bíceps con agarre inverso (pronado), desarrolla el braquiorradial y la fuerza de tracción para el top roll.",
        "muscles": "Braquiorradial, bíceps, antebrazo",
        "instructions": [
          "Sujeta una barra con las palmas hacia abajo (pronado)",
          "Eleva el peso manteniendo las muñecas rectas",
          "Baja con control"
        ],
        "sets": "3 series",
        "reps": "10-12 repeticiones",
        "tips": [
          "Empieza con menos peso que en un curl de bíceps normal",
          "Sin balanceo, solo forma estricta"
        ]
      },
      "pt-BR": {
        "name": "Rosca Bíceps com Pega Invertida",
        "description": "Rosca de bíceps com pega invertida (pronada), desenvolve o braquiorradial e a força de puxada para o top roll.",
        "muscles": "Braquiorradial, bíceps, antebraço",
        "instructions": [
          "Segura uma barra com as palmas para baixo (pronada)",
          "Enrola o peso mantendo os punhos retos",
          "Desce com controlo"
        ],
        "sets": "3 séries",
        "reps": "10-12 reps",
        "tips": [
          "Começa com menos peso do que numa rosca de bíceps normal",
          "Sem balanços - apenas forma rigorosa"
        ]
      },
      "ja": {
        "name": "リバースグリップバイセップカール",
        "description": "リバース（プロネート）グリップでのバイセップカールで、腕橈骨筋とトップロールの引く力を鍛えます。",
        "muscles": "腕橈骨筋、上腕二頭筋、前腕",
        "instructions": [
          "手のひらを下にしてバーベルを持つ（プロネート）",
          "手首をまっすぐに保ったまま重量を巻き上げる",
          "制御して下ろす"
        ],
        "sets": "3セット",
        "reps": "10〜12レップ",
        "tips": [
          "通常のバイセップカールより軽い重量から始める",
          "反動を使わず、厳格なフォームのみ"
        ]
      },
      "ko": {
        "name": "리버스 그립 바이셉 컬",
        "description": "리버스(회내) 그립으로 하는 바이셉 컬로, 상완요골근과 탑롤을 위한 당기기 힘을 기릅니다.",
        "muscles": "상완요골근, 이두근, 전완",
        "instructions": [
          "손바닥을 아래로 하고 바벨을 잡으세요(회내)",
          "손목을 곧게 유지하며 무게를 감아 올리세요",
          "통제하여 내리세요"
        ],
        "sets": "3세트",
        "reps": "10-12회",
        "tips": [
          "일반 바이셉 컬보다 가벼운 무게로 시작하세요",
          "반동 금지 - 엄격한 자세만 사용하세요"
        ]
      }
    }
  },
  {
    "id": "scottBenchBicepCurls",
    "slug": "scott-bench-bicep-curls",
    "emoji": "💪",
    "category": "dynamic",
    "equipmentKeys": [
      "barbell"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "hook"
    ],
    "armGroup": "brachialis",
    "translations": {
      "en": {
        "name": "Scott Bench Bicep Curls for Armwrestling",
        "description": "Bicep curl on a preacher (Scott) bench, isolating the biceps with the elbow fully supported — a favorite accessory lift for hook strength.",
        "muscles": "Biceps, forearm",
        "instructions": [
          "Rest your upper arm on the preacher bench pad",
          "Curl the weight up through a full range",
          "Lower slowly, resisting the stretch at the bottom"
        ],
        "sets": "3-4 sets",
        "reps": "8-12 reps",
        "tips": [
          "The bench keeps the elbow anchored for strict isolation",
          "Use full range of motion for maximum development"
        ]
      },
      "ru": {
        "name": "Сгибания на бицепс на скамье Скотта для армрестлинга",
        "description": "Сгибание на бицепс на скамье проповедника (Скотта), изолирующее бицепс с полной опорой локтя — любимое дополнительное упражнение для силы крюка.",
        "muscles": "Бицепс, предплечье",
        "instructions": [
          "Положите плечо на подушку скамьи Скотта",
          "Поднимайте вес по полной амплитуде",
          "Медленно опускайте, сопротивляясь растяжению внизу"
        ],
        "sets": "3-4 подхода",
        "reps": "8-12 повторений",
        "tips": [
          "Скамья фиксирует локоть для строгой изоляции",
          "Используйте полную амплитуду движения для максимального развития"
        ]
      },
      "de": {
        "name": "Bizeps-Curls an der Scott-Bank für Armdrücken",
        "description": "Bizeps-Curl an der Scott-Bank (Preacher), isoliert den Bizeps mit voll abgestütztem Ellbogen — beliebte Zusatzübung für Hook-Kraft.",
        "muscles": "Bizeps, Unterarm",
        "instructions": [
          "Lege den Oberarm auf das Polster der Scott-Bank",
          "Curle das Gewicht über den vollen Radius hoch",
          "Senke langsam ab, widerstehe der Dehnung unten"
        ],
        "sets": "3–4 Sätze",
        "reps": "8–12 Wdh.",
        "tips": [
          "Die Bank fixiert den Ellbogen für strenge Isolation",
          "Nutze den vollen Bewegungsradius für maximale Entwicklung"
        ]
      },
      "fr": {
        "name": "Curls biceps au banc Larry Scott pour bras de fer",
        "description": "Curl biceps au banc pupitre (Larry Scott), isole le biceps avec le coude entièrement soutenu — exercice accessoire favori pour la force du hook.",
        "muscles": "Biceps, avant-bras",
        "instructions": [
          "Posez le bras sur le coussin du banc Larry Scott",
          "Enroulez le poids sur une amplitude complète",
          "Descendez lentement, en résistant à l'étirement en bas"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 répétitions",
        "tips": [
          "Le banc fixe le coude pour un isolement strict",
          "Utilisez une amplitude complète pour un développement maximal"
        ]
      },
      "es": {
        "name": "Curl de Bíceps en Banco Scott para Pulso",
        "description": "Curl de bíceps en banco predicador (Scott), aísla el bíceps con el codo totalmente apoyado — ejercicio accesorio favorito para la fuerza de gancho.",
        "muscles": "Bíceps, antebrazo",
        "instructions": [
          "Apoya el brazo superior en el cojín del banco Scott",
          "Eleva el peso en un rango completo",
          "Baja lentamente, resistiendo el estiramiento abajo"
        ],
        "sets": "3-4 series",
        "reps": "8-12 repeticiones",
        "tips": [
          "El banco fija el codo para un aislamiento estricto",
          "Usa un rango de movimiento completo para máximo desarrollo"
        ]
      },
      "pt-BR": {
        "name": "Rosca Bíceps no Banco Scott para Queda de Braço",
        "description": "Rosca de bíceps no banco pregador (Scott), isola o bíceps com o cotovelo totalmente apoiado — exercício acessório favorito para força de hook.",
        "muscles": "Bíceps, antebraço",
        "instructions": [
          "Apoia o braço no banco Scott",
          "Enrola o peso em amplitude completa",
          "Desce lentamente, resistindo ao alongamento em baixo"
        ],
        "sets": "3-4 séries",
        "reps": "8-12 reps",
        "tips": [
          "O banco fixa o cotovelo para isolamento rigoroso",
          "Usa a amplitude completa para o máximo desenvolvimento"
        ]
      },
      "ja": {
        "name": "アームレスリング用スコットベンチバイセップカール",
        "description": "肘を完全に支えて上腕二頭筋をアイソレートするプリーチャー（スコット）ベンチでのバイセップカールで、フック力のための人気の補助種目です。",
        "muscles": "上腕二頭筋、前腕",
        "instructions": [
          "上腕をスコットベンチのパッドに乗せる",
          "フルレンジで重量を巻き上げる",
          "下部でのストレッチに抵抗しながらゆっくり下ろす"
        ],
        "sets": "3〜4セット",
        "reps": "8〜12レップ",
        "tips": [
          "ベンチが肘を固定し厳格なアイソレーションを可能にする",
          "最大限の発達のために可動域をフルに使う"
        ]
      },
      "ko": {
        "name": "팔씨름용 스콧 벤치 바이셉 컬",
        "description": "팔꿈치를 완전히 지지한 채 이두근을 고립시키는 프리처(스콧) 벤치 바이셉 컬로, 훅 힘을 위한 인기 있는 보조 운동입니다.",
        "muscles": "이두근, 전완",
        "instructions": [
          "위팔을 스콧 벤치 패드에 올리세요",
          "전체 범위로 무게를 감아 올리세요",
          "하단에서 스트레치에 저항하며 천천히 내리세요"
        ],
        "sets": "3-4세트",
        "reps": "8-12회",
        "tips": [
          "벤치가 팔꿈치를 고정하여 엄격한 고립을 가능하게 합니다",
          "최대 발달을 위해 전체 가동 범위를 사용하세요"
        ]
      }
    }
  },
  {
    "id": "wristEngagedPullUps",
    "slug": "wrist-engaged-pull-ups",
    "emoji": "🧗",
    "category": "dynamic",
    "equipmentKeys": [
      "pullupBar"
    ],
    "difficulty": "intermediate",
    "hasVideo": true,
    "relatedTechniqueIds": [
      "sidepressure"
    ],
    "armGroup": "pressure",
    "translations": {
      "en": {
        "name": "Pull-Ups with Wrist Engagement",
        "description": "Pull-up variation with active wrist and grip engagement, building the pulling strength side pressure and back pressure both depend on.",
        "muscles": "Wrist flexors, lats, biceps",
        "instructions": [
          "Grip the pull-up bar and actively curl your wrists into it",
          "Pull up while maintaining that wrist engagement",
          "Lower with control, keeping the grip active throughout"
        ],
        "sets": "3 sets",
        "reps": "6-10 reps",
        "tips": [
          "Try different grip widths to target different pulling angles",
          "Control the negative (lowering) portion slowly"
        ]
      },
      "ru": {
        "name": "Подтягивания с активной кистью",
        "description": "Вариант подтягиваний с активным вовлечением кисти и хвата, развивающий тяговую силу, от которой зависят и боковое, и давление на спину.",
        "muscles": "Сгибатели кисти, широчайшие, бицепс",
        "instructions": [
          "Возьмитесь за турник и активно сгибайте кисти в хват",
          "Подтягивайтесь, сохраняя это вовлечение кисти",
          "Опускайтесь с контролем, сохраняя активный хват на протяжении"
        ],
        "sets": "3 подхода",
        "reps": "6-10 повторений",
        "tips": [
          "Попробуйте разную ширину хвата для разных углов тяги",
          "Контролируйте негативную фазу (опускание)"
        ]
      },
      "de": {
        "name": "Klimmzüge mit aktivem Handgelenk",
        "description": "Klimmzug-Variante mit aktivem Einsatz von Handgelenk und Griff, baut die Zugkraft auf, von der sowohl Seiten- als auch Zugdruck abhängen.",
        "muscles": "Handgelenkbeuger, Latissimus, Bizeps",
        "instructions": [
          "Greife die Klimmzugstange und curle die Handgelenke aktiv in den Griff",
          "Ziehe dich hoch, während du dieses Handgelenk-Engagement beibehältst",
          "Senke dich kontrolliert ab, halte den aktiven Griff durchgehend bei"
        ],
        "sets": "3 Sätze",
        "reps": "6–10 Wdh.",
        "tips": [
          "Probiere verschiedene Griffbreiten für unterschiedliche Zugwinkel",
          "Kontrolliere die negative Phase (Absenken) langsam"
        ]
      },
      "fr": {
        "name": "Tractions avec poignet actif",
        "description": "Variante de tractions avec engagement actif du poignet et de la prise, développe la force de traction dont dépendent à la fois la pression latérale et arrière.",
        "muscles": "Fléchisseurs du poignet, dorsaux, biceps",
        "instructions": [
          "Saisissez la barre de traction et enroulez activement les poignets dans la prise",
          "Tirez-vous vers le haut en maintenant cet engagement du poignet",
          "Descendez en contrôlant, en gardant la prise active tout du long"
        ],
        "sets": "3 séries",
        "reps": "6-10 répétitions",
        "tips": [
          "Essayez différentes largeurs de prise pour varier les angles de traction",
          "Contrôlez lentement la phase négative (descente)"
        ]
      },
      "es": {
        "name": "Dominadas con Muñeca Activa",
        "description": "Variante de dominadas con implicación activa de muñeca y agarre, desarrolla la fuerza de tracción de la que dependen tanto la presión lateral como hacia atrás.",
        "muscles": "Flexores de muñeca, dorsales, bíceps",
        "instructions": [
          "Agarra la barra de dominadas y curva activamente las muñecas hacia el agarre",
          "Tira hacia arriba manteniendo esa implicación de muñeca",
          "Baja con control, manteniendo el agarre activo en todo momento"
        ],
        "sets": "3 series",
        "reps": "6-10 repeticiones",
        "tips": [
          "Prueba distintos anchos de agarre para diferentes ángulos de tracción",
          "Controla la fase negativa (bajada) lentamente"
        ]
      },
      "pt-BR": {
        "name": "Elevações com Punho Ativo",
        "description": "Variante de elevações com envolvimento ativo do punho e da pega, desenvolve a força de puxada da qual dependem tanto a pressão lateral como para trás.",
        "muscles": "Flexores do punho, dorsais, bíceps",
        "instructions": [
          "Agarra a barra de elevação e enrola ativamente os punhos na pega",
          "Puxa-te para cima mantendo esse envolvimento do punho",
          "Desce com controlo, mantendo a pega ativa do início ao fim"
        ],
        "sets": "3 séries",
        "reps": "6-10 reps",
        "tips": [
          "Experimenta diferentes larguras de pega para ângulos de puxada distintos",
          "Controla a fase negativa (descida) lentamente"
        ]
      },
      "ja": {
        "name": "手首エンゲージ懸垂",
        "description": "手首とグリップを積極的に関与させる懸垂のバリエーションで、サイドプレッシャーとバックプレッシャー両方が依存する引く力を鍛えます。",
        "muscles": "手首屈筋群、広背筋、上腕二頭筋",
        "instructions": [
          "懸垂バーを握り、積極的に手首をグリップへ巻き込む",
          "その手首の関与を維持しながら体を引き上げる",
          "常に積極的なグリップを保ちながら制御して下ろす"
        ],
        "sets": "3セット",
        "reps": "6〜10レップ",
        "tips": [
          "異なる引く角度のためにさまざまなグリップ幅を試す",
          "ネガティブ（下ろす）動作をゆっくり制御する"
        ]
      },
      "ko": {
        "name": "손목 사용 풀업",
        "description": "손목과 그립을 적극적으로 사용하는 풀업 변형으로, 사이드 프레셔와 백 프레셔 모두에 필요한 당기기 힘을 기릅니다.",
        "muscles": "손목 굴근, 광배근, 이두근",
        "instructions": [
          "풀업 바를 잡고 적극적으로 손목을 그립으로 감으세요",
          "그 손목 사용을 유지하며 몸을 끌어올리세요",
          "내내 적극적인 그립을 유지하며 통제하여 내리세요"
        ],
        "sets": "3세트",
        "reps": "6-10회",
        "tips": [
          "다양한 당기기 각도를 위해 여러 그립 너비를 시도하세요",
          "네거티브(내리는) 구간을 천천히 통제하세요"
        ]
      }
    }
  }
];

export const TECHNIQUES_MIN: TechniqueMin[] = [
  {
    "id": "hook",
    "keyExerciseIds": [
      "wristCurls",
      "hammerCurls",
      "supinationCurls",
      "twoByTwoHandleFingerHold"
    ],
    "names": {
      "en": "Hook",
      "ru": "Крюк",
      "de": "Hook",
      "fr": "Hook",
      "es": "Gancho",
      "pt-BR": "Hook",
      "ja": "フック",
      "ko": "훅"
    }
  },
  {
    "id": "toproll",
    "keyExerciseIds": [
      "pronationCurls",
      "rising",
      "wristEngagedPullUps",
      "staticRiserHold"
    ],
    "names": {
      "en": "Top Roll",
      "ru": "Верхний (Топролл)",
      "de": "Top Roll",
      "fr": "Top Roll",
      "es": "Top Roll",
      "pt-BR": "Top Roll",
      "ja": "トップロール",
      "ko": "탑롤"
    }
  },
  {
    "id": "sidepressure",
    "keyExerciseIds": [
      "sidePressureDrills",
      "staticBarWristCurlsSidePressure",
      "wristEngagedPullUps"
    ],
    "names": {
      "en": "Side Pressure",
      "ru": "Боковое давление",
      "de": "Seitendruck",
      "fr": "Pression latérale",
      "es": "Presión Lateral",
      "pt-BR": "Pressão Lateral",
      "ja": "サイドプレッシャー",
      "ko": "사이드 프레셔"
    }
  },
  {
    "id": "pronation",
    "keyExerciseIds": [
      "pronationCurls",
      "pronationHold",
      "chainHandlePronationLifts"
    ],
    "names": {
      "en": "Pronation",
      "ru": "Пронация",
      "de": "Pronation",
      "fr": "Pronation",
      "es": "Pronación",
      "pt-BR": "Pronação",
      "ja": "回内",
      "ko": "회내"
    }
  },
  {
    "id": "cupping",
    "keyExerciseIds": [
      "wristCurls",
      "wristCurlsWinningPosition",
      "twoByTwoHandleFingerHold",
      "larrattsLiftOverFingers"
    ],
    "names": {
      "en": "Cupping",
      "ru": "Чашка",
      "de": "Cupping",
      "fr": "Cupping",
      "es": "Ahuecado",
      "pt-BR": "Cupping",
      "ja": "カッピング",
      "ko": "커핑"
    }
  }
];
